// Supported UI locales. Adding a new locale requires creating a new
// dictionary in `./locales/` and registering it in `./index.tsx`.
export type Locale = 'en' | 'id' | 'de' | 'zh-CN' | 'zh-TW' | 'pt-BR' | 'es-ES' | 'ru' | 'fa' | 'ar' | 'ja' | 'ko' | 'pl' | 'hu' | 'fr' | 'uk' | 'tr' | 'th' | 'it';

export const LOCALES: Locale[] = ['pt-BR'];

export const LOCALE_LABEL: Record<Locale, string> = {
  'en': 'English',
  'id': 'Bahasa Indonesia',
  'de': 'Deutsch',
  'zh-CN': '简体中文',
  'zh-TW': '繁體中文',
  'pt-BR': 'Português (Brasil)',
  'es-ES': 'Español (España)',
  'ru': 'Русский',
  'fa': 'فارسی',
  'ar': 'العربية',
  'ja': '日本語',
  'ko': '한국어',
  'pl': 'Polski',
  'hu': 'Magyar',
  'fr': 'Français',
  'uk': 'Українська',
  'tr': 'Türkçe',
  'th': 'ภาษาไทย',
  'it': 'Italiano'
};

// Translation dictionary shape — flat keys, dot-namespaced. We keep it
// flat (not deeply nested) so missing-key TS errors point straight at the
// offending string instead of a generic object mismatch.
export interface Dict {
  'chat.imageEngine': string;
  'chat.imageEngineChoose': string;
  'chat.imageEngineEmpty': string;
  'chat.imageEngineSaveFailed': string;
  'chat.imageEngineHelp': string;
  'chat.imageEngineAspectUnsupported': string;
  'billing.wallet': string;
  /** The product name of the allowance pool; kept untranslated in every locale. */
  'billing.codingPlanDesignPlan': string;
  /** Period of a quota window, derived from its `durationSeconds`. */
  'billing.codingPlanPeriodHours': string;
  'billing.codingPlanPeriodDays': string;
  /** The share of a window STILL AVAILABLE, the number the bar fills to. */
  'billing.codingPlanRemainingPercent': string;
  // Workspace invite acceptance (C lane)
  'invite.header.eyebrow': string;
  'invite.loading': string;
  'invite.landing.title': string;
  'invite.landing.subtitle': string;
  'invite.landing.roleLabel': string;
  'invite.landing.invitedEmail': string;
  'invite.landing.expires': string;
  'invite.role.admin': string;
  'invite.role.member': string;
  'invite.role.admin.desc': string;
  'invite.role.member.desc': string;
  'invite.accept.cta': string;
  'invite.accountMismatch.title': string;
  'invite.accountMismatch.body': string;
  'invite.accountMismatch.continue': string;
  'invite.accountMismatch.switch': string;
  'invite.accepting.title': string;
  'invite.accepting.body': string;
  'invite.success.title': string;
  'invite.success.body': string;
  'invite.success.roleReceipt': string;
  'invite.success.enter': string;
  'invite.open.cta': string;
  'invite.open.opening': string;
  'invite.open.retry': string;
  'invite.notInstalled.title': string;
  'invite.notInstalled.body': string;
  'invite.notInstalled.download': string;
  'invite.error.title': string;
  'invite.error.invite_expired': string;
  'invite.error.invite_consumed': string;
  'invite.error.workspace_seat_limit_reached': string;
  'invite.error.workspace_subscription_locked': string;
  'invite.error.workspace_not_found': string;
  'invite.error.workspace_forbidden': string;
  'invite.error.invite_unavailable': string;
  'invite.error.generic': string;
  'invite.error.retry': string;
  'workspaceInvite.dialogAria': string;
  'workspaceInvite.title': string;
  'workspaceInvite.freePlanBody': string;
  'workspaceInvite.teamPlanBody': string;
  'workspaceInvite.seatsExhaustedBody': string;
  'workspaceInvite.seatsExhaustedAction': string;
  'workspaceInvite.emailLabel': string;
  'workspaceInvite.roleLabel': string;
  'workspaceInvite.defaultRoleLabel': string;
  'workspaceInvite.emailPlaceholder': string;
  'workspaceInvite.removeRow': string;
  'workspaceInvite.addMember': string;
  'workspaceInvite.visibilityQuestion': string;
  'workspaceInvite.visibilityAnswer': string;
  'workspaceInvite.sent': string;
  'workspaceInvite.sending': string;
  'workspaceInvite.confirm': string;
  'workspaceInvite.submitFailed': string;
  'workspaceInvite.errorAlreadyMember': string;
  'workspaceInvite.errorPendingInvite': string;
  'workspaceInvite.errorNoSession': string;
  'workspaceInvite.errorNoWorkspace': string;
  'workspaceInvite.errorUnreachable': string;
  // Common
  'common.cancel': string;
  'chat.selectFromLibrary': string;
  'chat.importFigma': string;
  'chat.plus.group.files': string;
  'chat.plus.group.code': string;
  'chat.plus.group.designs': string;
  'chat.plus.group.other': string;
  'chat.plus.attachFiles': string;
  'chat.plus.referenceProject': string;
  'chat.plus.linkLocalCode': string;
  'chat.plus.uploadFig': string;
  'chat.plus.learnHow': string;
  'chat.plus.designSystem': string;
  'chat.plus.skills': string;
  'chat.plus.noSkills': string;
  'chat.plus.connectors': string;
  'chat.plus.plugins': string;
  'chat.plus.mcp': string;
  'chat.referenceProject.title': string;
  'chat.referenceProject.search': string;
  'chat.referenceProject.empty': string;
  'chat.referenceProject.emptyAll': string;
  'chat.referenceProject.loadFailed': string;
  'chat.referenceProject.confirm': string;
  'chat.contextPrompt.referenceProject': string;
  'chat.contextPrompt.localCode': string;
  'chat.figmaHelp.title': string;
  'chat.figmaHelp.intro': string;
  'chat.figmaHelp.step1': string;
  'chat.figmaHelp.step2': string;
  'chat.figmaHelp.step3': string;
  'chat.figmaHelp.note': string;
  'designFiles.library.label': string;
  'designFiles.library.title': string;
  'libraryPicker.title': string;
  'libraryPicker.searchPlaceholder': string;
  'libraryPicker.empty': string;
  'libraryPicker.allKinds': string;
  'libraryPicker.add': string;
  'libraryPicker.loading': string;
  'common.save': string;
  'common.close': string;
  'common.clear': string;
  'common.delete': string;
  'common.rename': string;
  'common.edit': string;
  'common.preview': string;
  'common.share': string;
  'common.search': string;
  'common.searchEllipsis': string;
  'common.loading': string;
  'common.all': string;
  'common.none': string;
  'common.default': string;
  'common.installed': string;
  'common.notInstalled': string;
  'common.active': string;
  'common.inactive': string;
  'common.offline': string;
  'common.selected': string;
  'common.create': string;
  'common.openPreview': string;
  'common.exitFullscreen': string;
  'common.fullscreen': string;
  'common.openInNewTab': string;
  'common.exportPdf': string;
  'common.exportZip': string;
  'common.exportHtml': string;
  'common.exportImage': string;
  'common.exportImageFailed': string;
  'common.justNow': string;
  'common.minutesAgo': string;
  'common.hoursAgo': string;
  'common.daysAgo': string;
  'common.weeksAgo': string;
  'common.now': string;
  'common.minutesShort': string;
  'common.hoursShort': string;
  'common.daysShort': string;
  'common.untitled': string;
  'designBrowser.savePageBrief': string;
  'designBrowser.viewport.desktop': string;
  'designBrowser.viewport.desktopTitle': string;
  'designBrowser.viewport.tablet': string;
  'designBrowser.viewport.tabletTitle': string;
  'designBrowser.viewport.mobile': string;
  'designBrowser.viewport.mobileTitle': string;
  'designBrowser.menu': string;
  'designBrowser.copyScreenshot': string;
  'designBrowser.hardReload': string;
  'designBrowser.copyUrl': string;
  'designBrowser.openExternal': string;
  'designBrowser.downloadPage': string;
  'designBrowser.downloadPageBusy': string;
  'designBrowser.clearHistory': string;
  'designBrowser.clearCookies': string;
  'designBrowser.clearAllData': string;
  'designBrowser.status.noUrlToCopy': string;
  'designBrowser.status.urlCopied': string;
  'designBrowser.status.openHttpFirst': string;
  'designBrowser.status.openBeforeScreenshot': string;
  'designBrowser.status.screenshotSaved': string;
  'designBrowser.addImageToChatButton': string;
  'designBrowser.status.imageAddedToChat': string;
  'designBrowser.status.screenshotFailed': string;
  'designBrowser.status.openBeforeBrief': string;
  'designBrowser.status.briefSaveFailed': string;
  'designBrowser.status.openBeforeDownload': string;
  'designBrowser.status.pageSnapshotStarted': string;
  'designBrowser.status.pageSnapshotUnsupported': string;
  'designBrowser.status.pageSnapshotFailed': string;
  'designBrowser.status.pageSnapshotSaved': string;
  'designBrowser.status.viewDesignFiles': string;
  'designBrowser.status.downloadAssistHint': string;
  'designBrowser.status.desktopDataUnavailable': string;
  'designBrowser.status.browserDataCleared': string;
  'designBrowser.status.browserDataClearFailed': string;
  'designBrowser.status.historyCleared': string;

  // Plugin registry / details
  'plugins.availableDetails.provenance': string;
  'plugins.availableDetails.provenanceLine': string;
  'plugins.availableDetails.provenanceLineWithIntegrity': string;
  'plugins.availableDetails.install': string;
  'plugins.availableDetails.version': string;
  'plugins.availableDetails.pluginVersion': string;
  'plugins.availableDetails.copyInstallCommand': string;
  'plugins.availableDetails.copied': string;
  'plugins.availableDetails.deprecatedPrefix': string;
  'plugins.availableDetails.deprecatedFallback': string;
  'plugins.availableDetails.yanked': string;
  'plugins.availableDetails.yankedWithReason': string;
  'plugins.availableDetails.versionDeprecatedSuffix': string;
  'plugins.availableDetails.versionYankedSuffix': string;
  'plugins.availableDetails.ref': string;
  'plugins.availableDetails.integrity': string;
  'plugins.availableDetails.permissions': string;
  'plugins.availableDetails.capabilitySummary': string;
  'plugins.actions.copyInstallCommand': string;
  'plugins.actions.copyPluginId': string;
  'plugins.actions.copyReadmeBadge': string;
  'plugins.actions.openSourceGithub': string;
  'plugins.actions.openSource': string;
  'plugins.actions.openHomepage': string;
  'plugins.actions.openMarketplace': string;

  // App / brand
  'app.brand': string;
  'app.brandPill': string;
  'app.brandSubtitle': string;
  'app.welcomeLoading': string;

  // Settings dialog
  'settings.welcomeKicker': string;
  'settings.welcomeTitle': string;
  'settings.welcomeSubtitle': string;
  'settings.onboardingCreateTitle': string;
  'settings.onboardingCreateBody': string;
  'settings.onboardingMemoryTitle': string;
  'settings.onboardingMemoryBody': string;
  // Onboarding step 2 — memory callout (answers seed the profile)
  'settings.onboardingMemoryCalloutTitle': string;
  'settings.onboardingMemoryCalloutBody': string;
  'settings.onboardingMemoryBenefitIntent': string;
  'settings.onboardingMemoryBenefitFewerQuestions': string;
  'settings.onboardingMemoryBenefitPersonalized': string;
  'settings.onboardingSystemsTitle': string;
  'settings.onboardingSystemsBody': string;
  'settings.onboardingExecutionTitle': string;
  'settings.onboardingExecutionBody': string;
  'settings.onboardingAmrCloudBenefitOfficial': string;
  'settings.onboardingAmrCloudBenefitReady': string;
  'settings.onboardingAmrCloudBenefitModels': string;
  'settings.onboardingAmrCloudBenefitPricing': string;
  'settings.onboardingAmrCloudUpcomingLabel': string;
  'settings.onboardingAmrCloudUpcomingImageVideo': string;
  'settings.onboardingAmrCloudUpcomingSkills': string;
  'settings.onboardingAmrCloudUpcomingRouting': string;
  'settings.onboardingAmrModelSourceLabel': string;
  'settings.onboardingAmrCloudAuthorizeAction': string;
  'settings.onboardingAmrCloudAuthorizedAction': string;
  'settings.onboardingStepConnect': string;
  'settings.onboardingStepDesignSystem': string;
  'settings.onboardingStepProfile': string;
  'settings.onboardingStepNewsletter': string;
  'settings.onboardingNewsletterTitle': string;
  'settings.onboardingNewsletterBody': string;
  'settings.onboardingConnectTitle': string;
  'settings.onboardingConnectBody': string;
  'settings.onboardingCloudTitle': string;
  'settings.onboardingCloudBody': string;
  'settings.onboardingCloudSignIn': string;
  'settings.onboardingFreeCredits': string;
  'settings.onboardingFreeCreditsHint': string;
  'settings.onboardingOwnAi': string;
  'settings.onboardingLocalAi': string;
  'settings.onboardingApiKey': string;
  'settings.onboardingCloudContinue': string;
  'settings.onboardingCloudAlternative': string;
  'settings.onboardingCloudOr': string;
  'settings.onboardingCloudRights': string;
  'settings.onboardingGateTooltipNoRuntime': string;
  'settings.onboardingGateTooltipAmr': string;
  'settings.onboardingGateTooltipLocal': string;
  'settings.onboardingGateTooltipByok': string;
  'settings.onboardingRecommended': string;
  'settings.onboardingAmrCloudOfficialBadge': string;
  'settings.onboardingLocalTitle': string;
  'settings.onboardingLocalBody': string;
  'settings.onboardingLocalAction': string;
  'settings.onboardingCliScanHint': string;
  'settings.onboardingByokTitle': string;
  'settings.onboardingByokBody': string;
  'settings.onboardingByokAction': string;
  'settings.onboardingDesignTitle': string;
  'settings.onboardingDesignBody': string;
  'settings.onboardingDesignIntroGenerateTitle': string;
  'settings.onboardingDesignIntroGenerateBody': string;
  'settings.onboardingDesignIntroReuseTitle': string;
  'settings.onboardingDesignIntroReuseBody': string;
  'settings.onboardingDesignIntroSkipTitle': string;
  'settings.onboardingDesignIntroSkipBody': string;
  'settings.onboardingGithubTitle': string;
  'settings.onboardingGithubBody': string;
  'settings.onboardingUploadTitle': string;
  'settings.onboardingUploadBody': string;
  'settings.onboardingPromptTitle': string;
  'settings.onboardingPromptBody': string;
  'settings.onboardingProfileTitle': string;
  'settings.onboardingProfileBody': string;
  'settings.onboardingRoleLabel': string;
  'settings.onboardingOrgSizeLabel': string;
  'settings.onboardingUseCaseLabel': string;
  'settings.onboardingSourceLabel': string;
  'settings.onboardingSourceX': string;
  'settings.onboardingSourceGithub': string;
  'settings.onboardingSourceYoutube': string;
  'settings.onboardingSourceTiktok': string;
  'settings.onboardingSourceReddit': string;
  'settings.onboardingSourceLinkedin': string;
  'settings.onboardingSourceMetaSocial': string;
  'settings.onboardingSourceSearch': string;
  'settings.onboardingSourceAiTool': string;
  'settings.onboardingSourceFriend': string;
  'settings.onboardingSourceCommunity': string;
  'settings.onboardingSourceEmail': string;
  'settings.onboardingSourceBlog': string;
  'settings.onboardingSourceOther': string;
  'settings.onboardingSourceOtherPlaceholder': string;
  'settings.onboardingSelectPlaceholder': string;
  'settings.onboardingSelectMultiplePlaceholder': string;
  'settings.onboardingOrgSolo': string;
  'settings.onboardingOrgTeam': string;
  'settings.onboardingOrgStartup': string;
  'settings.onboardingOrgGrowth': string;
  'settings.onboardingOrgMidMarket': string;
  'settings.onboardingOrgEnterprise': string;
  'settings.onboardingRolePm': string;
  'settings.onboardingRoleDesigner': string;
  'settings.onboardingRoleEngineer': string;
  'settings.onboardingRoleMarketing': string;
  'settings.onboardingRoleAgency': string;
  'settings.onboardingRoleGrowth': string;
  'settings.onboardingRoleOps': string;
  'settings.onboardingRoleFounder': string;
  'settings.onboardingRoleStudent': string;
  'settings.onboardingRoleOther': string;
  'settings.onboardingUseProduct': string;
  'settings.onboardingUseDesignSystem': string;
  'settings.onboardingUsePrototype': string;
  'settings.onboardingUseLanding': string;
  'settings.onboardingUseMarketing': string;
  'settings.onboardingUseAds': string;
  'settings.onboardingUseDashboard': string;
  'settings.onboardingUseDeck': string;
  'settings.onboardingUseEngineering': string;
  'settings.onboardingUseAgency': string;
  'settings.onboardingBack': string;
  'settings.onboardingContinue': string;
  'settings.onboardingFinish': string;
  'settings.kicker': string;
  'settings.title': string;
  'settings.subtitle': string;
  'settings.general': string;
  'settings.generalHint': string;
  'settings.modeAria': string;
  'settings.protocolAria': string;
  'settings.modeDaemon': string;
  'settings.modeDaemonHelp': string;
  'settings.modeDaemonOffline': string;
  'settings.modeDaemonOfflineMeta': string;
  'settings.modeDaemonInstalledMeta': string;
  'settings.modeApi': string;
  'settings.cloudCalloutTitle': string;
  'settings.cloudCalloutBody': string;
  'settings.cloudCalloutButton': string;
  'settings.modeApiMeta': string;
  'settings.byokNoFileToolsNotice': string;
  'settings.byokDraftNotice': string;
  'settings.codeAgent': string;
  'settings.codeAgentHint': string;
  'settings.rescan': string;
  'settings.rescanTitle': string;
  'settings.rescanRunning': string;
  'settings.rescanSuccess': string;
  'settings.rescanFailed': string;
  'settings.test': string;
  'settings.testTitle': string;
  'settings.testRunning': string;
  'settings.byokReadyToTest': string;
  'settings.testCancel': string;
  'settings.testRetry': string;
  'settings.required': string;
  'settings.testMissingFields': string;
  'settings.testSuccessApi': string;
  'settings.testSuccessCli': string;
  'settings.testAuthFailed': string;
  'settings.testForbidden': string;
  'settings.testNotFoundModel': string;
  'settings.testInvalidModelId': string;
  'settings.testInvalidBaseUrl': string;
  'settings.testRateLimited': string;
  'settings.testUpstream': string;
  'settings.testTimeout': string;
  'settings.testAgentMissing': string;
  'settings.testAgentSpawn': string;
  'settings.testUnknown': string;
  'settings.agentInstall.install': string;
  'settings.agentInstall.docs': string;
  'settings.agentInstall.pathHint': string;
  'settings.agentInstall.stepOpenLinks': string;
  'settings.agentInstall.stepAuth': string;
  'settings.agentInstall.stepRescan': string;
  'settings.agentInstall.stepSelect': string;
  'settings.noAgentsDetected': string;
  'settings.agentInstalledGroup': string;
  'settings.agentInstallGroup': string;
  'settings.agentAuthRequired': string;
  'settings.agentAuthUnknown': string;
  'settings.advanced': string;
  'settings.amrLogin': string;
  'settings.amrLogout': string;
  'settings.amrLoggingIn': string;
  'settings.amrLoggingOut': string;
  'settings.amrLoggedInAs': string;
  'settings.amrLoggedInWithPlan': string;
  'settings.amrLoggedInPill': string;
  'settings.amrNotLoggedIn': string;
  'settings.amrCloud': string;
  'settings.amrAuthorize': string;
  'settings.amrBenefitOfficial': string;
  'settings.amrBenefitLowerPrice': string;
  'settings.amrBenefitManyModels': string;
  'settings.amrPromoBonus': string;
  'settings.amrSignInToContinue': string;
  'settings.amrSignIn': string;
  'settings.amrSignedIn': string;
  'settings.amrWalletBalance': string;
  'settings.amrWalletUnavailable': string;
  'settings.amrWalletUpdatedAt': string;
  'settings.amrWalletCached': string;
  'settings.amrWalletRefresh': string;
  'settings.amrWalletRefreshTitle': string;
  'settings.amrNotSignedIn': string;
  'settings.amrSigningIn': string;
  'settings.amrActivationHint': string;
  'settings.amrActivationBrowserFailed': string;
  'settings.amrActivationOpen': string;
  'settings.amrCancelSignIn': string;
  'settings.amrAccountStatus': string;
  'settings.amrConsole': string;
  'settings.amrBalance': string;
  'settings.amrPlan': string;
  'settings.amrUpgrade': string;
  // Settings > Workspace region (E-frontend, D4.3): the role-gated shell for
  // other-lane workspace destinations. Shown only for a team workspace.
  'settings.workspace': string;
  'settings.workspaceHint': string;
  'settings.workspaceLede': string;
  'settings.workspaceBack': string;
  'settings.workspaceMembers': string;
  'settings.workspaceMembersHint': string;
  'settings.workspaceBilling': string;
  'settings.workspaceBillingHint': string;
  'settings.workspaceAutoRecharge': string;
  'settings.workspaceAutoRechargeHint': string;
  'settings.workspaceTeamSpace': string;
  'settings.workspaceTeamSpaceHint': string;
  // Full-page Settings (`/settings`): sidebar nav head above the section list
  // (back-to-home link, decorative search field, personal group label).
  'settings.pageBackToHome': string;
  'settings.pageNavGroupPersonal': string;
  'settings.amrModelUpgradeHint': string;
  'settings.amrLoginErrorCompact': string;
  'settings.apiSection': string;
  'settings.quickFillProvider': string;
  'settings.providerPreset': string;
  'settings.protocolGroupProtocols': string;
  'settings.protocolGroupGateways': string;
  'settings.customProvider': string;
  'settings.apiKey': string;
  'settings.apiKeyGetLink': string;
  'settings.showKey': string;
  'settings.hideKey': string;
  'settings.show': string;
  'settings.hide': string;
  'settings.model': string;
  'settings.apiKeyInvalid': string;
  'settings.apiKeyCleaned': string;
  'settings.modelsLoadedFromAccount': string;
  'settings.modelsLoadedCount': string;
  'settings.modelSourceAccount': string;
  'settings.modelSourceSuggested': string;
  'modelCapability.standard': string;
  'modelCapability.advanced': string;
  'modelCapability.bestQuality': string;
  'modelCapability.standardDescription': string;
  'modelCapability.advancedDescription': string;
  'modelCapability.bestQualityDescription': string;
  'modelCost.upToHalf': string;
  'modelCost.halfToOne': string;
  'modelCost.oneToFour': string;
  'modelCost.overFour': string;
  'settings.fetchModels': string;
  'settings.fetchModelsTitle': string;
  'settings.fetchModelsRunning': string;
  'settings.fetchModelsMissingFields': string;
  'settings.fetchModelsInvalidBaseUrl': string;
  'settings.fetchModelsUnsupportedAzure': string;
  'settings.fetchModelsUnsupportedOllama': string;
  'settings.fetchModelsSuccess': string;
  'settings.fetchModelsEmpty': string;
  'settings.fetchModelsUnsupported': string;
  'settings.fetchModelsFailed': string;
  'settings.suggestedModelsHint': string;
  'settings.maxTokens': string;
  'settings.maxTokensHint': string;
  'settings.baseUrl': string;
  'settings.baseUrlInvalid': string;
  'settings.baseUrlCustomize': string;
  'settings.baseUrlDefaultHint': string;
  'settings.azureBaseUrlPlaceholder': string;
  'settings.azureBaseUrlHint': string;
  'settings.azureDeploymentModel': string;
  'settings.azureCustomDeploymentName': string;
  'settings.azureDeploymentModelHint': string;
  'settings.azureModelFetchHint': string;
  'settings.apiVersion': string;
  'settings.byokImageModel': string;
  'settings.byokVideoModel': string;
  'settings.byokVideoI2vHint': string;
  'settings.byokSpeechModel': string;
  'settings.byokSpeechVoice': string;
  'settings.byokModelDefaultOption': string;
  'settings.apiHint': string;
  'settings.skipForNow': string;
  'settings.getStarted': string;
  'settings.envConfigure': string;
  'settings.localCli': string;
  'settings.dshSetupTitle': string;
  'settings.dshSetupDescription': string;
  'settings.dshSetupNote': string;
  'settings.dshSetupConfirm': string;
  'settings.dshSetupInstalling': string;
  'settings.dshSetupRequired': string;
  'settings.anthropicApi': string;
  'settings.noAgentSelected': string;
  'settings.language': string;
  'settings.languageHint': string;
  'settings.agentModelHead': string;
  'settings.modelPicker': string;
  'settings.modelSourceLive': string;
  'settings.modelUsesCliDefault': string;
  'settings.modelSourceFallback': string;
  'settings.reasoningPicker': string;
  'settings.serviceTierPicker': string;
  'settings.modelPickerHint': string;
  'settings.modelPickerLiveHint': string;
  'settings.modelPickerLiveCatalogOnlyHint': string;
  'settings.modelPickerFallbackHint': string;
  'settings.cliEnvTitle': string;
  'settings.cliEnvHint': string;
  'settings.cliEnvClaudeConfigDir': string;
  'settings.cliEnvClaudeBaseUrl': string;
  'settings.cliEnvClaudeApiKey': string;
  'settings.cliEnvCodexHome': string;
  'settings.cliEnvCodexBin': string;
  'settings.cliEnvCodexBaseUrl': string;
  'settings.cliEnvCodexApiKey': string;
  'settings.modelCustom': string;
  'settings.modelCustomLabel': string;
  'settings.modelCustomPlaceholder': string;
  'settings.mediaProviders': string;
  'settings.mediaProvidersHint': string;
  'settings.mcpServerTitle': string;
  'settings.mcpServerHint': string;
  'settings.externalMcpTitle': string;
  'settings.externalMcpHint': string;
  'settings.mediaProviderApiKey': string;
  'settings.mediaProviderBaseUrl': string;
  'settings.mediaProviderConfigured': string;
  'settings.mediaProviderUnset': string;
  'settings.mediaProviderClear': string;
  'settings.mediaProviderClearConfirm': string;
  'settings.mediaProviderPlaceholder': string;
  'settings.mediaProviderBaseUrlPlaceholder': string;
  'settings.mediaProviderReload': string;
  'settings.mediaProviderReloadError': string;
  'settings.mediaProviderReloadSuccess': string;
  'settings.mediaProviderLoadError': string;
  'settings.mediaProviderComingSoonHint': string;
  // #5517 media-providers redesign: provider pill selector + single detail
  // card (labeled Model field, docs callout, save-bar note, no-key panel).
  'settings.mediaProviderModelProviders': string;
  'settings.mediaProviderModel': string;
  'settings.mediaProviderModelPlaceholder': string;
  'settings.mediaProviderDocsTitle': string;
  'settings.mediaProviderDocsHint': string;
  'settings.mediaProviderSaveHint': string;
  'settings.mediaProviderNoKeyRequired': string;
  'settings.mediaProviderNoKeyHint': string;
  'settings.privacy': string;
  'settings.privacyHint': string;
  'settings.privacyConsentKicker': string;
  'settings.privacyConsentLead': string;
  'settings.privacyConsentFooter': string;
  'settings.privacyConsentShare': string;
  'settings.privacyConsentDecline': string;
  'settings.privacyConsentAccept': string;
  'settings.privacyConsentBannerFooter': string;
  'settings.privacyConsentPolicyLink': string;
  'settings.privacyMetrics': string;
  'settings.privacyMetricsHint': string;
  'settings.privacyContent': string;
  'settings.privacyContentHint': string;
  'settings.privacyInstallationId': string;
  'settings.privacyOptedOut': string;
  'settings.privacyDataDeletion': string;
  'settings.privacyDataDeletionHint': string;
  'settings.about': string;
  'settings.aboutHint': string;
  'settings.appVersion': string;
  'settings.appChannel': string;
  'settings.appRuntime': string;
  'settings.appPlatform': string;
  'settings.appArchitecture': string;
  'settings.runtimePackaged': string;
  'settings.runtimeDevelopment': string;
  'settings.versionUnavailable': string;
  'settings.allowSilentUpdates': string;
  'settings.allowSilentUpdatesDesc': string;
  'settings.installLatest': string;
  'settings.alreadyLatest': string;
  'settings.updateCheck': string;
  'settings.updateNow': string;
  'settings.updateRecheck': string;
  'settings.updateRetry': string;
  'settings.updateViewReleases': string;
  'settings.updateActionFailed': string;
  'settings.updateQuitFailed': string;
  'settings.updateStatusNotChecked': string;
  'settings.updateStatusDevelopment': string;
  'settings.updateStatusUnsupported': string;
  'settings.updateStatusChecking': string;
  'settings.updateStatusUpToDate': string;
  'settings.updateStatusAvailable': string;
  'settings.updateStatusAvailableUnknown': string;
  'settings.updateStatusDownloading': string;
  'settings.updateStatusDownloadingPercent': string;
  'settings.updateStatusReady': string;
  'settings.updateStatusReadyUnknown': string;
  'settings.updateStatusInstalling': string;
  'settings.updateStatusFailed': string;
  'settings.skills': string;
  'settings.skillsHint': string;
  'settings.skillsNew': string;
  'settings.skillsEmpty': string;
  'settings.skillsEdit': string;
  'settings.skillsDelete': string;
  'settings.skillsDeleteConfirm': string;
  'settings.skillsName': string;
  'settings.skillsTriggers': string;
  'settings.skillsDescription': string;
  'settings.skillsBody': string;
  'settings.skillsCreate': string;
  'settings.skillsSave': string;
  'settings.skillsSaving': string;
  'settings.skillsOverrideCreate': string;
  'settings.skillsOverrideSave': string;
  'settings.skillsBuiltInOverrideWarning': string;
  'settings.skillsFiles': string;
  'settings.skillsNoFiles': string;
  'settings.skillsNameRequired': string;
  'settings.skillsBodyRequired': string;
  'settings.designSystems': string;
  'settings.designSystemsHint': string;
  'settings.designSystemsInstalled': string;
  'settings.designSystemRenameFailed': string;
  'settings.designSystemsAdd': string;
  'settings.designSystemsHiddenCount': string;
  'settings.designSystemsShowAll': string;
  'settings.designSystemsShowHidden': string;
  'settings.designSystemsSource': string;
  'settings.designSystemsSourceLocal': string;
  'settings.designSystemsSourceGithub': string;
  'settings.designSystemsSourceShadcn': string;
  'settings.designSystemsShadcnReference': string;
  'settings.designSystemsImportShadcn': string;
  'settings.designSystemsStructure': string;
  'settings.designSystemsModeHybrid': string;
  'settings.designSystemsModeNormalized': string;
  'settings.designSystemsModeVerbatim': string;
  'settings.designSystemsCraft': string;
  'settings.designSystemsCraftColor': string;
  'settings.designSystemsCraftAccessibility': string;
  'settings.designSystemsGithubUrl': string;
  'settings.designSystemsProjectPath': string;
  'settings.designSystemsImportGithub': string;
  'settings.designSystemsImportProject': string;
  'settings.designSystemsImportedStatus': string;
  'settings.designSystemsViewImported': string;
  'settings.designSystemsCategory': string;
  'settings.designSystemsAllCategories': string;
  'settings.designSystemsShowInHomeGallery': string;
  'settings.projectLocations': string;
  'settings.projectLocationsHint': string;
  'settings.projectLocationsDescription': string;
  'settings.projectLocationsSaveError': string;
  'settings.projectLocationsSaved': string;
  'settings.projectLocationsScanError': string;
  'settings.projectLocationsScanComplete': string;
  'settings.projectLocationsNoFolderSelected': string;
  'settings.projectLocationsDuplicate': string;
  'settings.projectLocationsWorkBaseMeta': string;
  'settings.projectLocationsAddFolder': string;
  'settings.projectLocationsDefaultBadge': string;
  'settings.projectLocationsMakeDefault': string;
  'settings.projectLocationsDefaultSaved': string;
  'settings.librarySkills': string;
  'settings.libraryDesignSystems': string;
  'settings.librarySearch': string;
  'settings.libraryAll': string;
  'settings.libraryPreview': string;
  'settings.libraryPreviewClose': string;
  'settings.libraryLoading': string;
  'settings.libraryNoResults': string;
  'settings.libraryEnabled': string;
  'settings.libraryDisabled': string;
  'settings.libraryToggleLabel': string;
  'settings.libraryInstall': string;
  'settings.libraryInstallGithub': string;
  'settings.libraryInstallLocal': string;
  'settings.libraryInstallUrl': string;
  'settings.libraryInstallPath': string;
  'settings.libraryInstallButton': string;
  'settings.libraryUninstall': string;
  'settings.libraryBuiltIn': string;
  'settings.libraryInstalled': string;
  'settings.connectorsNavHint': string;
  'settings.connectorsHint': string;
  'settings.connectorsComposioApiKey': string;
  'settings.connectorsSavedTitle': string;
  'settings.connectorsSavedWithTail': string;
  'settings.connectorsSaved': string;
  'settings.connectorsGetApiKey': string;
  'settings.connectorsReplaceKeyPlaceholder': string;
  'settings.connectorsApiKeyPlaceholder': string;
  'settings.connectorsClear': string;
  'settings.connectorsClearConfirmTitle': string;
  'settings.connectorsClearConfirmBody': string;
  'settings.connectorsClearConfirmContinue': string;
  'settings.connectorsClearFinalTitle': string;
  'settings.connectorsClearFinalBody': string;
  'settings.connectorsClearFinalConfirm': string;
  'settings.connectorsClearArming': string;
  'settings.connectorsClearCancel': string;
  'settings.connectorsSaveKey': string;
  'settings.connectorsSaveKeyTitle': string;
  'settings.connectorsKeySaving': string;
  'settings.connectorsKeySaved': string;
  'settings.connectorsKeyError': string;
  'settings.connectorsHelpSaved': string;
  'settings.connectorsHelpUnsaved': string;
  'settings.connectorsHelpEmpty': string;
  'settings.connectorsLoadingSavedKey': string;
  'settings.autosaveSaving': string;
  'settings.autosaveSaved': string;
  'settings.autosaveError': string;
  'settings.orbit.eyebrow': string;
  'settings.orbit.title': string;
  'settings.orbit.navHint': string;
  'settings.orbit.lede': string;
  'settings.orbit.statusOnTitle': string;
  'settings.orbit.statusOffTitle': string;
  'settings.orbit.statusActive': string;
  'settings.orbit.statusOff': string;
  'settings.orbit.runTitle': string;
  'settings.orbit.running': string;
  'settings.orbit.runOpen': string;
  'settings.orbit.dailySummaryTitle': string;
  'settings.orbit.dailySummarySub': string;
  'settings.orbit.on': string;
  'settings.orbit.off': string;
  'settings.orbit.runTimeTitle': string;
  'settings.orbit.runTimeSub': string;
  'settings.orbit.runTimeAria': string;
  'settings.orbit.nextRun': string;
  'settings.orbit.nextRunScheduledAfterSave': string;
  'settings.orbit.schedule': string;
  'settings.orbit.pausedManualOnly': string;
  'settings.orbit.templateTitle': string;
  'settings.orbit.templateMissing': string;
  'settings.orbit.templateMissingOption': string;
  'settings.orbit.templateMissingInstall': string;
  'settings.orbit.templateMissingPickAnother': string;
  'settings.orbit.templateResetTitle': string;
  'settings.orbit.templateReset': string;
  'settings.orbit.templateHelp': string;
  'settings.orbit.templateAria': string;
  'settings.orbit.templatesLoading': string;
  'settings.orbit.templatesOptgroup': string;
  'settings.orbit.lastRun': string;
  'settings.orbit.triggerManual': string;
  'settings.orbit.triggerScheduled': string;
  'settings.orbit.meterAria': string;
  'settings.orbit.countChecked': string;
  'settings.orbit.countSucceeded': string;
  'settings.orbit.countSkipped': string;
  'settings.orbit.countFailed': string;
  'settings.orbit.runError': string;
  'settings.orbit.artifactKickerLive': string;
  'settings.orbit.artifactKickerLegacy': string;
  'settings.orbit.artifactTitle': string;
  'settings.orbit.artifactMetaLive': string;
  'settings.orbit.artifactMetaLegacy': string;
  'settings.orbit.copyMarkdownTitle': string;
  'settings.orbit.copied': string;
  'settings.orbit.copy': string;
  'settings.orbit.openArtifact': string;
  'settings.orbit.sourceMarkdown': string;
  'settings.orbit.gateAriaLabel': string;
  'settings.orbit.gateEyebrow': string;
  'settings.orbit.gateTitle': string;
  'settings.orbit.gateBody': string;
  'settings.orbit.gateBodyNoKey': string;
  'settings.orbit.gateAction': string;
  'settings.orbit.gateActionNoKey': string;
  'settings.orbit.gateLoading': string;
  'settings.orbit.controlsLockedBadge': string;
  'settings.orbit.controlsLockedHint': string;

  // Memory (auto-extracted personalization saved as on-disk markdown)
  'settings.memory': string;
  'settings.memoryHint': string;
  'settings.customInstructionsTitle': string;
  'settings.customInstructionsHint': string;
  'settings.customInstructionsPlaceholder': string;
  'settings.memoryDescription': string;
  'settings.memoryEnabled': string;
  'settings.memoryDisabled': string;
  'settings.memoryEnableLabel': string;
  'settings.memoryDisabledBanner': string;
  'settings.memoryNew': string;
  'settings.memoryEdit': string;
  'settings.memoryDelete': string;
  'settings.memoryPreview': string;
  'settings.memoryEmpty': string;
  'settings.memoryEmptyHintZh': string;
  'settings.memoryEmptyHintEn': string;
  'settings.memoryName': string;
  'settings.memoryDesc': string;
  'settings.memoryBody': string;
  'settings.memoryBodyHint': string;
  'settings.memoryStartersLabel': string;
  'settings.memoryStarterUserName': string;
  'settings.memoryStarterUserDesc': string;
  'settings.memoryStarterUserBody': string;
  'settings.memoryStarterFeedbackName': string;
  'settings.memoryStarterFeedbackDesc': string;
  'settings.memoryStarterFeedbackBody': string;
  'settings.memoryStarterProjectName': string;
  'settings.memoryStarterProjectDesc': string;
  'settings.memoryStarterProjectBody': string;
  'settings.memorySaveHint': string;
  'settings.memoryIndexSaveHint': string;
  'settings.memoryIndexUnsaved': string;
  'settings.memoryFlashCreated': string;
  'settings.memoryFlashSaved': string;
  'settings.memoryFlashDeleted': string;
  'settings.memoryFlashIndexSaved': string;
  'settings.memoryFlashPathCopied': string;
  'settings.memoryNameLabel': string;
  'settings.memoryTypeLabel': string;
  'settings.memoryDescLabel': string;
  'settings.memoryBodyLabel': string;
  'settings.memoryTypeUser': string;
  'settings.memoryTypeFeedback': string;
  'settings.memoryTypeProject': string;
  'settings.memoryTypeReference': string;
  'settings.memoryTypeProfile': string;
  'settings.memoryTypeRule': string;
  // Memory → top-level IA tabs (Memories vs. How it works)
  'settings.memoryTabMemories': string;
  'settings.memoryTabMemoriesCaption': string;
  'settings.memoryTabHow': string;
  'settings.memoryTabHowCaption': string;
  'settings.memoryAddDisclosure': string;
  'settings.memoryAddDisclosureHint': string;
  // Memory → two-loop: structured profile editor
  'settings.memoryProfileTab': string;
  'settings.memoryProfileTabCaption': string;
  'settings.memoryProfileTitle': string;
  'settings.memoryProfileHint': string;
  'settings.memoryProfileName': string;
  'settings.memoryProfileDescription': string;
  'settings.memoryProfileSave': string;
  'settings.memoryProfileSaving': string;
  'settings.memoryProfileSaved': string;
  'settings.memoryProfileRole': string;
  'settings.memoryProfileRolePlaceholder': string;
  'settings.memoryProfileCompany': string;
  'settings.memoryProfileCompanyPlaceholder': string;
  'settings.memoryProfileDomain': string;
  'settings.memoryProfileDomainPlaceholder': string;
  'settings.memoryProfileAudience': string;
  'settings.memoryProfileAudiencePlaceholder': string;
  'settings.memoryProfileAesthetic': string;
  'settings.memoryProfileAestheticPlaceholder': string;
  'settings.memoryProfileDeliverables': string;
  'settings.memoryProfileDeliverablesPlaceholder': string;
  'settings.memoryProfileLocale': string;
  'settings.memoryProfileLocalePlaceholder': string;
  'settings.memoryProfileGoals': string;
  'settings.memoryProfileGoalsPlaceholder': string;
  // Memory → two-loop: pluggable hooks panel
  'settings.memoryHooksTitle': string;
  'settings.memoryHooksHint': string;
  'settings.memoryHooksProfileLabel': string;
  'settings.memoryHooksProfileDesc': string;
  'settings.memoryHooksRewriteLabel': string;
  'settings.memoryHooksRewriteDesc': string;
  'settings.memoryHooksVerifyLabel': string;
  'settings.memoryHooksVerifyDesc': string;
  'settings.memoryHooksExtractionLabel': string;
  'settings.memoryHooksExtractionDesc': string;
  'settings.memoryIndex': string;
  'settings.memoryIndexSave': string;
  'settings.memoryIndexReset': string;
  'settings.memoryToastChanged': string;
  'settings.memoryToastClickHint': string;
  'settings.memoryAll': string;
  // Memory → LLM extraction observability
  'settings.memoryExtractions': string;
  'settings.memoryExtractionsHint': string;
  'settings.memoryExtractionsEmpty': string;
  'settings.memoryExtractionsRefresh': string;
  'settings.memoryExtractionsRefreshing': string;
  'settings.memoryExtractionPhaseRunning': string;
  'settings.memoryExtractionPhaseSuccess': string;
  'settings.memoryExtractionPhaseSkipped': string;
  'settings.memoryExtractionPhaseFailed': string;
  'settings.memoryExtractionSkipNoProvider': string;
  'settings.memoryExtractionSkipUnsupportedProvider': string;
  'settings.memoryExtractionSkipDisabled': string;
  'settings.memoryExtractionSkipEmpty': string;
  'settings.memoryExtractionSkipNoMatch': string;
  'settings.memoryExtractionKindHeuristic': string;
  'settings.memoryExtractionKindLlm': string;
  'settings.memoryExtractionProviderEnv': string;
  'settings.memoryExtractionProviderMediaConfig': string;
  'settings.memoryExtractionProviderOverride': string;
  'settings.memoryExtractionProposed': string;
  'settings.memoryExtractionWritten': string;
  'settings.memoryExtractionDuration': string;
  'settings.memoryExtractionDelete': string;
  'settings.memoryExtractionsClear': string;
  'settings.memoryExtractionsClearTitle': string;
  'settings.memoryExtractionsClearConfirm': string;
  'settings.memoryNoProviderBannerTitle': string;
  'settings.memoryNoProviderBannerBody': string;
  'settings.memoryUnsupportedProviderBannerBody': string;
  // Memory model picker — rendered inline next to the chat model picker
  // so picking "the fast model that mines facts each turn" lives in the
  // same row as the chat agent + model. Reuses the surrounding chat
  // protocol/CLI context (key, baseUrl, apiVersion); the user only
  // chooses the model id.
  'settings.memoryModelInlineLabel': string;
  'settings.memoryModelInlineSameAsChat': string;
  'settings.memoryModelInlineSameAsChatWithModel': string;
  'settings.memoryModelInlineSameAsChatWithProvider': string;
  'settings.memoryModelInlineHintCli': string;
  'settings.memoryModelInlineHintCliConstrained': string;
  'settings.memoryModelInlineHintByok': string;
  'settings.memoryModelInlineHintByokNeutral': string;
  'settings.memoryModelInlineFlashSaved': string;
  'settings.memoryModelInlineFlashCleared': string;

  // MCP server settings
  'settings.mcpTitle': string;
  'settings.mcpHint': string;
  'settings.mcpDaemonError': string;
  'settings.mcpBuildDaemon': string;
  'settings.mcpNodeMissing': string;
  'settings.mcpBuildHint': string;
  'settings.mcpMethodCli': string;
  'settings.mcpInstructionCli': string;
  'settings.mcpMethodToml': string;
  'settings.mcpInstructionCodex': string;
  'settings.mcpMethodOneClick': string;
  'settings.mcpInstructionCursor': string;
  'settings.mcpDeeplinkInstallCursor': string;
  'settings.mcpMethodJson': string;
  'settings.mcpInstructionKiro': string;
  'settings.mcpInstructionCopilot': string;
  'settings.mcpInstructionAntigravity': string;
  'settings.mcpInstructionZed': string;
  'settings.mcpInstructionWindsurf': string;
  'settings.mcpCopyAria': string;
  'settings.mcpResolvingFailed': string;
  'settings.mcpLoadingPaths': string;
  'settings.mcpCopied': string;
  'settings.mcpCopy': string;
  'settings.mcpCursorApproval': string;
  'settings.mcpCodexOneClickInstall': string;
  'settings.mcpCodexOneClickUninstall': string;
  'settings.mcpCodexOneClickUnavailable': string;
  'settings.mcpCodexInstallSuccess': string;
  'settings.mcpCodexUninstallSuccess': string;
  'settings.mcpCodexBusy': string;
  'settings.mcpCodexInstallError': string;
  'settings.mcpRestartNote': string;
  'settings.mcpRestartDetail': string;
  'settings.mcpCapabilitiesTitle': string;
  'settings.mcpCapabilityRead': string;
  'settings.mcpCapabilityPull': string;
  'settings.mcpCapabilityDefault': string;
  'settings.mcpRunningNote': string;

  // Notifications (settings + system notifications)
  // `systemPrefs*` heads the System preferences block on Settings → General,
  // which groups the completion sound and desktop notification rows.
  'settings.systemPrefsTitle': string;
  'settings.systemPrefsHint': string;
  'settings.notifications': string;
  'settings.notificationsHint': string;
  'settings.notifyCompletionSound': string;
  'settings.notifyCompletionSoundHint': string;
  'settings.notifySuccessSound': string;
  'settings.notifyFailureSound': string;
  'settings.notifyDesktop': string;
  'settings.notifyDesktopHint': string;
  'settings.notifyDesktopBlocked': string;
  'settings.notifyDesktopUnsupported': string;
  'settings.notifyTest': string;
  'settings.notifyTestSent': string;
  'settings.notifyTestFailed': string;
  'settings.notifySoundDing': string;
  'settings.notifySoundChime': string;
  'settings.notifySoundTwoToneUp': string;
  'settings.notifySoundPluck': string;
  'settings.notifySoundBuzz': string;
  'settings.notifySoundTwoToneDown': string;
  'settings.notifySoundThud': string;
  'notify.successTitle': string;
  'notify.failureTitle': string;
  'notify.successBody': string;
  'notify.failureBody': string;

  // Desktop updater
  'updater.available': string;
  'updater.availableBody': string;
  'updater.activeRunsTitle': string;
  'updater.activeRunsBody': string;
  'updater.activeRunsUnknownBody': string;
  'updater.checking': string;
  'updater.download': string;
  'updater.downloading': string;
  'updater.downloadingPercent': string;
  'updater.dialogAvailableGeneric': string;
  'updater.dialogAvailableVersion': string;
  'updater.dialogCheckFailed': string;
  'updater.dialogReadyGeneric': string;
  'updater.dialogReadyVersion': string;
  'updater.dialogUnsupported': string;
  'updater.done': string;
  'updater.failed': string;
  'updater.installerOpenBody': string;
  'updater.installerOpened': string;
  'updater.later': string;
  'updater.manualDownload': string;
  'updater.allowSilentUpdates': string;
  'updater.openFailedFallback': string;
  'updater.installRestart': string;
  'updater.installingRestart': string;
  'updater.openInstaller': string;
  'updater.opening': string;
  'updater.payloadReadyGeneric': string;
  'updater.payloadReadyVersion': string;
  'updater.quitButton': string;
  'updater.quitFailedBody': string;
  'updater.quitFailedTitle': string;
  'updater.quitting': string;
  'updater.ready': string;
  'updater.readyGeneric': string;
  'updater.readyVersion': string;
  // Forced installer reinstall (feed control.launcher.version.min gate).
  // Copy priority: operator jump link (reinstall.url) > these defaults.
  'updater.reinstallLearnMore': string;
  'updater.reinstallReadyGeneric': string;
  'updater.reinstallReadyVersion': string;
  'updater.restartAnyway': string;
  'updater.upToDate': string;
  'updater.viewVersionFeatures': string;

  // Post-update "what's new" dialog on the home surface. Cover art, release
  // headline and highlight bullets come from the hosted highlights document;
  // these keys cover the dialog chrome only. `updatedTitle` takes the RUNNING
  // app version — never a literal. `cta` opens the release notes, so it names
  // that destination rather than teasing "see what's new".
  'whatsNew.updatedTitle': string;
  'whatsNew.cta': string;

  // Newsletter email field on the onboarding About-you step.
  'newsletter.label': string;
  'newsletter.placeholder': string;

  // Entry view / tabs
  'entry.tabDesigns': string;
  'entry.tabTemplates': string;
  'entry.tabDesignSystems': string;
  'entry.tabConnectors': string;
  'entry.tabImageTemplates': string;
  'entry.tabVideoTemplates': string;
  'entry.openSettingsTitle': string;
  'entry.openSettingsAria': string;
  'entry.discordLabel': string;
  'entry.discordAria': string;
  'entry.discordAriaWithOnline': string;
  'entry.discordOnlineLabel': string;
  'entry.xAria': string;
  'entry.mailAria': string;
  /** Label beside the mail glyph in the rail's account dock social row. */
  'entry.accountSettings': string;
  'chat.collapsePane': string;
  'collabPresence.ariaOne': string;
  'collabPresence.aria': string;
  'collabPresence.ariaWithSelfOne': string;
  'collabPresence.ariaWithSelf': string;
  'collabPresence.moreOnline': string;
  'collabPresence.dialogTitle': string;
  'collabPresence.onlineCount': string;
  'collabPresence.selfBadge': string;
  'collabPresence.roleOwner': string;
  'collabPresence.roleAdmin': string;
  'collabPresence.roleMember': string;
  'collabPresence.viewingFileSelf': string;
  'collabPresence.viewingFileOther': string;
  'collabPresence.viewingProjectSelf': string;
  'collabPresence.viewingProjectOther': string;
  'entry.followXLabel': string;
  'entry.followThreadsLabel': string;
  'entry.youtubeLabel': string;
  'entry.followInstagramLabel': string;
  'entry.followLinkedinLabel': string;
  'entry.followXiaohongshuLabel': string;
  'entry.resizeAria': string;
  'entry.loadingWorkspace': string;
  'entry.useEverywhereTitle': string;
  'entry.useEverywhereAria': string;
  'entry.workspaceTeamsLabel': string;
  'workspaceSwitcher.invite': string;
  'workspaceSwitcher.createTeam': string;
  'workspaceSwitcher.draftsTooltip': string;
  'workspaceSwitcher.allProjectsTooltip': string;
  'entry.primaryNavAria': string;
  'entry.billingTierTeam': string;
  'entry.billingTierFree': string;
  'entry.billingTierPro': string;
  'entry.billingTierPlus': string;
  'entry.billingTierMax': string;
  'entry.billingTierGo': string;
  'entry.billingFamilyCreator': string;
  'entry.creditsAria': string;
  'entry.creditsAriaWithBalance': string;
  'entry.creditsGrantTip': string;
  'entry.creditsUpgrade': string;
  'entry.creditsManage': string;
  'entry.creditsOpening': string;
  'entry.creditsRemaining': string;
  'entry.credits': string;
  'entry.creditsUsage': string;
  'entry.creditsMemberNoticeTitle': string;
  'entry.creditsMemberNoticeBody': string;
  'entry.creditsMemberNoticeAction': string;
  'entry.accountToggleTheme': string;
  'entry.accountSwitchLanguage': string;
  'entry.accountLanguageMeta': string;
  'entry.accountGithubHelp': string;
  'entry.accountFeatureRequest': string;
  /** Account menu row that opens the workspace's billing console. */
  'entry.accountBilling': string;
  'entry.accountAddAccount': string;
  'entry.accountSignOut': string;
  // Sign-out confirmation dialog (recvqgMWpJZqhL) — shared by every logout
  // entry point (nav-rail account menu + AMR account pill) so a stray click
  // can never sign the user out without an explicit confirm.
  'signOut.confirmTitle': string;
  'signOut.confirmMessage': string;
  'signOut.confirmAction': string;
  'entry.navRecents': string;
  'entry.navDashboard': string;
  'entry.blankDraftsTitle': string;
  'entry.blankDraftsDescription': string;
  'entry.blankAllProjectsTitle': string;
  'entry.blankAllProjectsDescription': string;
  'entry.blankCreate': string;
  'entry.workspaceTeamsTitle': string;
  'entry.workspaceTeamsAria': string;
  // Left nav rail (icon-only) — surface labels also serve as tooltips
  'entry.navExpand': string;
  'entry.navCollapse': string;
  'entry.navNewProject': string;
  'entry.navHome': string;
  'entry.navProjects': string;
  'entry.navTasks': string;
  'entry.navPlugins': string;
  'entry.navDesignSystems': string;
  'entry.navBrands': string;
  'entry.navIntegrations': string;
  // Team-edition navigation shell.
  'entry.navMembers': string;
  'entry.navWorkspaceSettings': string;
  'entry.navDrafts': string;
  'entry.navAllProjects': string;
  'entry.draftsDescription': string;
  'entry.allProjectsDescription': string;
  'entry.navBoard': string;
  'entry.navTeamSection': string;
  'entry.teamSlotNote': string;
  'entry.cloudCalloutTitle': string;
  'entry.cloudRecovering': string;
  'entry.authExpiredBody': string;
  'home.createFailed': string;
  'home.daemonRecovering': string;
  /** Toast after POST /api/projects answered PROJECT_CREATE_PREPARATION_TIMEOUT. */
  'home.createTimedOut': string;
  'home.amrGateUnavailable': string;
  'home.bundledScenarioMissing': string;
  'entry.cloudCalloutBody': string;
  /** Name of the identity row on the local (signed-out) account dock. */
  'entry.localAccountName': string;
  'entry.cloudCalloutDismissAria': string;
  'entry.workspaceLockedNote': string;
  'entry.workspaceLockedRecover': string;
  // Client message center
  'messageCenter.openAria': string;
  'messageCenter.unreadCount': string;
  'messageCenter.title': string;
  'messageCenter.subtitle': string;
  'messageCenter.filterAll': string;
  'messageCenter.filterUnread': string;
  'messageCenter.filterRead': string;
  'messageCenter.markAllRead': string;
  'messageCenter.emptyAllTitle': string;
  'messageCenter.emptyUnreadTitle': string;
  'messageCenter.emptyReadTitle': string;
  'messageCenter.archive': string;
  'messageCenter.unarchive': string;
  'messageCenter.archivedTitle': string;
  'messageCenter.emptyArchivedTitle': string;
  'messageCenter.emptyBody': string;
  'messageCenter.close': string;
  'messageCenter.desktopSettings': string;
  'messageCenter.desktopSettingsHint': string;
  'goPlanSunset.closeAria': string;
  'goPlanSunset.title': string;
  'goPlanSunset.subtitle': string;
  'goPlanSunset.decisionsAria': string;
  'goPlanSunset.decisionsIntro': string;
  'goPlanSunset.decisionStopSales': string;
  'goPlanSunset.decisionRefund': string;
  'goPlanSunset.decisionUnaffected': string;
  'goPlanSunset.closing': string;
  'goPlanSunset.dismissError': string;
  'goPlanSunset.viewSubscriptions': string;
  'goPlanSunset.confirming': string;
  'goPlanSunset.acknowledge': string;
  'workspaceTabs.project': string;
  'workspaceTabs.pluginDetails': string;
  'workspaceTabs.marketplace': string;

  // Entry home
  'homeHero.title': string;
  'homeHero.titleWords': string;
  'homeHero.subtitle': string;
  'homeHero.startWithTemplate': string;
  'homeHero.startBlankProject': string;
  'homeHero.templatePicker.label': string;
  'homeHero.templatePicker.searchPlaceholder': string;
  'homeHero.templatePicker.projectTypes': string;
  'homeHero.subtitlePrefix': string;
  'homeHero.placeholder': string;
  'homeHero.placeholderActive': string;
  // Rotating typewriter placeholder scenarios + idle hint (home-hero carousel).
  'homeHero.carousel.hint': string;
  'homeHero.carousel.onePageBrief': string;
  'homeHero.carousel.notesToDeck': string;
  'homeHero.carousel.signupFlow': string;
  'homeHero.carousel.improveBrief': string;
  'homeHero.carousel.loadingAnimation': string;
  'homeHero.carousel.teamUpdateSlides': string;
  'homeHero.carousel.ordersDashboard': string;
  'homeHero.carousel.productDetail': string;
  'homeHero.carousel.caseStudy': string;
  'homeHero.carousel.landingIntro': string;
  'homeHero.carousel.pitchDeck': string;
  'homeHero.carousel.appIdea': string;
  'homeHero.carousel.landingLayout': string;
  'homeHero.skills': string;
  'homeHero.addMenu': string;
  'homeHero.addPlugin': string;
  'homeHero.addConnectors': string;
  'homeHero.addMcp': string;
  'homeHero.noPlugins': string;
  'homeHero.noMcp': string;
  'homeHero.noConnectors': string;
  'homeHero.applying': string;
  'homeHero.pluginTitle': string;
  'homeHero.pluginPrefix': string;
  'homeHero.skillPrefix': string;
  'homeHero.removePlugin': string;
  'homeHero.removePluginAria': string;
  'homeHero.clearActivePlugin': string;
  'homeHero.clearActiveSkill': string;
  'homeHero.contextItemsResolved': string;
  'homeHero.attachmentsScrollPrev': string;
  'homeHero.attachmentsScrollNext': string;
  'homeHero.removeFile': string;
  'homeHero.contextSearchResults': string;
  'homeHero.contextSurfaces': string;
  'homeHero.loadingContext': string;
  'homeHero.noResults': string;
  'homeHero.searchPrompt': string;
  'homeHero.parameters': string;
  'homeHero.details': string;
  'homeHero.toRun': string;
  'homeHero.forNewLine': string;
  'homeHero.run': string;
  'homeHero.typeSomethingToRun': string;
  'homeHero.promptExamples': string;
  'homeHero.footer.designSystem': string;
  'homeHero.footer.autoDesignSystem': string;
  'homeHero.footer.autoDesignSystemSummary': string;
  'homeHero.footer.ratio': string;
  'homeHero.footer.duration': string;
  'homeHero.footer.resolution': string;
  'homeHero.footer.speakerNotes': string;
  'homeHero.footer.noSpeakerNotes': string;
  'homeHero.footer.availableCount': string;
  'homeHero.footer.noMatches': string;
  'homeHero.moreShortcuts': string;
  'homeHero.railAria': string;
  'homeHero.subTypeAria': string;
  'homeHero.subTypeMore': string;
  'homeHero.confirmReplaceTitle': string;
  'homeHero.confirmReplaceBody': string;
  'homeHero.confirmReplace': string;
  'homeHero.chip.prototype': string;
  'homeHero.chip.webClone': string;
  'homeHero.chip.liveArtifact': string;
  'homeHero.chip.deck': string;
  'homeHero.chip.image': string;
  'homeHero.chip.video': string;
  'homeHero.chip.hyperframes': string;
  'homeHero.chip.audio': string;
  'homeHero.chip.createBrandKit': string;
  'homeHero.chip.createPlugin': string;
  'homeHero.chip.figma': string;
  'homeHero.chip.folder': string;
  'homeHero.chip.template': string;
  'homeHero.chip.liveArtifactHint': string;
  'homeHero.chip.hyperframesHint': string;
  'homeHero.chip.createBrandKitHint': string;
  'homeHero.chip.createPluginHint': string;
  'homeHero.chip.figmaHint': string;
  'homeHero.chip.folderHint': string;
  'homeHero.chip.templateHint': string;
  'homeHero.chip.wireframe': string;
  'homeHero.chip.mobile': string;
  'homeHero.chip.document': string;
  'homeHero.chip.prototypeDesc': string;
  'homeHero.chip.webCloneDesc': string;
  'homeHero.chip.wireframeDesc': string;
  'homeHero.chip.mobileDesc': string;
  'homeHero.chip.deckDesc': string;
  'homeHero.chip.documentDesc': string;
  'homeHero.chip.imageDesc': string;
  'homeHero.chip.videoDesc': string;
  'homeHero.chip.audioDesc': string;
  'homeHero.chip.hyperframesDesc': string;
  'homeHero.chip.webgl': string;
  'homeHero.chip.webglDesc': string;
  'homeHero.chip.worker': string;
  'homeHero.chip.workerDesc': string;
  'homeHero.chip.liveArtifactDesc': string;
  'homeHero.chip.createBrandKitDesc': string;
  'homeHero.chip.prototypeNext': string;
  'homeHero.chip.webCloneNext': string;
  'homeHero.chip.wireframeNext': string;
  'homeHero.chip.mobileNext': string;
  'homeHero.chip.deckNext': string;
  'homeHero.chip.documentNext': string;
  'homeHero.chip.imageNext': string;
  'homeHero.chip.videoNext': string;
  'homeHero.chip.audioNext': string;
  'homeHero.chip.webClonePromptSeed': string;
  'homeWorkingDir.trigger': string;
  'homeWorkingDir.triggerShort': string;
  'homeWorkingDir.pick': string;
  'homeWorkingDir.replace': string;
  'homeWorkingDir.recent': string;
  'homeWorkingDir.clear': string;
  'homeWorkingDir.hint': string;
  'homeWorkingDir.missing': string;
  'homeWorkingDir.applyFailed': string;
  'workingDirPicker.title': string;
  'workingDirPicker.homeTitle': string;
  'workingDirPicker.processing': string;
  'workingDirPicker.select': string;
  'workingDirPicker.clearAria': string;
  'workingDirPicker.replaceFailed': string;
  'workingDirPicker.unavailable': string;
  'workingDirPicker.openUnavailable': string;
  'workingDirPicker.openFailed': string;
  'workingDirPicker.showInFileManager': string;
  'workingDirPicker.replace': string;
  'workingDirPicker.recent': string;
  'workingDirPicker.defaultLabel': string;
  'handoff.toTarget': string;
  'handoff.openInTarget': string;
  'handoff.openAction': string;
  'handoff.menuTitle': string;
  'handoff.action': string;
  'handoff.fallbackTitle': string;
  'handoff.chooseTargetAria': string;
  'handoff.optionsAria': string;
  'handoff.editorSection': string;
  'handoff.cliSection': string;
  'handoff.clickOpen': string;
  'handoff.framework': string;
  'handoff.framework.react': string;
  'handoff.framework.vue': string;
  'handoff.framework.svelte': string;
  'handoff.framework.solid': string;
  'handoff.framework.next': string;
  'handoff.framework.vanilla': string;
  'handoff.frameworkPrompt.react': string;
  'handoff.frameworkPrompt.vue': string;
  'handoff.frameworkPrompt.svelte': string;
  'handoff.frameworkPrompt.solid': string;
  'handoff.frameworkPrompt.next': string;
  'handoff.frameworkPrompt.vanilla': string;
  'handoff.amrWebsite': string;
  'handoff.copyPrompt': string;
  'handoff.copyPromptForTarget': string;
  'handoff.copied': string;
  'handoff.projectPathUnavailable': string;
  'handoff.copyFailed': string;
  'handoff.promptIntro': string;
  'handoff.promptTarget': string;
  'handoff.promptCli': string;
  'handoff.promptStepsLead': string;
  'handoff.promptReadFiles': string;
  'handoff.promptKeepDesign': string;
  'handoff.promptProduceCode': string;
  'handoff.promptVerify': string;
  'handoff.promptCommandHint': string;
  'handoff.promptProject': string;
  'handoff.promptProjectId': string;
  'handoff.notInstalled': string;
  'handoff.notDetectedTitle': string;
  'designSystemPicker.select': string;
  'designSystemPicker.loading': string;
  'designSystemPicker.searchPlaceholder': string;
  'designSystemPicker.searchCompactPlaceholder': string;
  'designSystemPicker.noneTitle': string;
  'designSystemPicker.noneSummary': string;
  'designSystemPicker.empty': string;
  'designSystemPicker.openPreview': string;
  'designSystemPicker.loadingPreview': string;
  'designSystemPicker.noPreview': string;
  'designSystemPicker.previewHint': string;
  'designSystemPicker.fullscreenAria': string;
  'designSystemPicker.closeFullscreen': string;
  'designSystemPicker.closeEsc': string;
  'designSystemPicker.previewFrameTitle': string;
  'designSystemPicker.fullscreenFrameTitle': string;
  'recentProjects.title': string;
  'recentProjects.viewAll': string;
  'recentProjects.empty': string;
  'recentProjects.ownerAll': string;
  'recentProjects.ownerMine': string;
  'recentProjects.ownerOthers': string;
  'recentProjects.kindAll': string;
  'recentProjects.kindPrototype': string;
  'recentProjects.kindSlides': string;
  'recentProjects.kindDocument': string;
  'recentProjects.kindImage': string;
  'recentProjects.kindMedia': string;
  'recentProjects.kindOther': string;
  'recentProjects.inviteTeammates': string;
  'recentProjects.multiSelect': string;
  'recentProjects.sortAria': string;
  'recentProjects.sortNewest': string;
  'recentProjects.sortOldest': string;
  'recentProjects.sortName': string;
  /** Heading of the rail's recent-projects disclosure (最近浏览过). */
  'recentProjects.collectionRecent': string;
  'recentProjects.collectionPersonalProjects': string;
  'recentProjects.collectionTeamProjects': string;
  'recentProjects.viewList': string;
  'recentProjects.sharedBadge': string;
  'recentProjects.sharedProjectFallbackName': string;
  'recentProjects.creatorLine': string;
  'recentProjects.selfCreator': string;
  'recentProjects.teamMemberCreator': string;
  'recentProjects.ownOnlyMutation': string;
  'recentProjects.shareInProgress': string;
  'recentProjects.duplicateInProgress': string;
  'recentProjects.sharedInTeam': string;
  'recentProjects.moveToTeam': string;
  'recentProjects.moveToTeamDescPre': string;
  'recentProjects.moveToTeamDescStrong': string;
  'recentProjects.moveToTeamDescPost': string;
  'recentProjects.moveToPersonalDescPre': string;
  'recentProjects.moveToPersonalDescStrong': string;
  'recentProjects.moveToPersonalDescPost': string;
  'recentProjects.moveDontRemind': string;
  'recentProjects.confirmMoveToTeam': string;
  'recentProjects.confirmMoveToPersonal': string;
  'recentProjects.unshareInProgress': string;
  'recentProjects.moveOutOfTeam': string;
  'recentProjects.unshareFailed': string;
  'recentProjects.shareFailed': string;
  'recentProjects.shareOwnerConflict': string;
  'recentProjects.clearFilters': string;
  'pluginsHome.title': string;
  'pluginsHome.subtitle': string;
  'pluginsHome.browseRegistry': string;
  'pluginsHome.count': string;
  'pluginsHome.loadingCatalog': string;
  'pluginsHome.emptyCatalog': string;
  'pluginsHome.emptyFiltered': string;
  'pluginsHome.clearFilters': string;
  'pluginsHome.modeAria': string;
  'pluginsHome.featured': string;
  'pluginsHome.totalInCatalog': string;
  'pluginsHome.categoryFilterAria': string;
  'pluginsHome.subcategoryFilterAria': string;
  'pluginsHome.allCategory': string;
  'pluginsHome.searchPlaceholder': string;
  'pluginsHome.searchAria': string;
  'pluginsHome.clearSearch': string;
  'pluginsHome.sortAria': string;
  'pluginsHome.sortHot': string;
  'pluginsHome.sortNewest': string;
  'pluginsHome.facet.import': string;
  'pluginsHome.facet.create': string;
  'pluginsHome.facet.export': string;
  'pluginsHome.facet.share': string;
  'pluginsHome.facet.deploy': string;
  'pluginsHome.facet.refine': string;
  'pluginsHome.facet.extend': string;
  'pluginsHome.facet.figma': string;
  'pluginsHome.facet.github': string;
  'pluginsHome.facet.codeFolder': string;
  'pluginsHome.facet.url': string;
  'pluginsHome.facet.screenshot': string;
  'pluginsHome.facet.pdf': string;
  'pluginsHome.facet.pptx': string;
  'pluginsHome.facet.framer': string;
  'pluginsHome.facet.webflow': string;
  'pluginsHome.facet.slides': string;
  'pluginsHome.facet.publicLink': string;
  'pluginsHome.facet.githubPr': string;
  'pluginsHome.facet.githubGist': string;
  'pluginsHome.subfacet.business-dashboards': string;
  'pluginsHome.subfacet.app-prototypes': string;
  'pluginsHome.subfacet.landing-marketing': string;
  'pluginsHome.subfacet.developer-tools': string;
  'pluginsHome.subfacet.docs-reports': string;
  'pluginsHome.subfacet.brand-design': string;
  'pluginsHome.subfacet.pitch-business': string;
  'pluginsHome.subfacet.course-training': string;
  'pluginsHome.subfacet.reports-briefings': string;
  'pluginsHome.subfacet.product-sales': string;
  'pluginsHome.subfacet.engineering-talks': string;
  'pluginsHome.subfacet.creative-decks': string;
  'pluginsHome.subfacet.ui-product-mockups': string;
  'pluginsHome.subfacet.brand-visuals': string;
  'pluginsHome.subfacet.storyboards-motion-refs': string;
  'pluginsHome.subfacet.social-content': string;
  'pluginsHome.subfacet.avatar-portrait': string;
  'pluginsHome.subfacet.illustration-style': string;
  'pluginsHome.subfacet.motion-effects': string;
  'pluginsHome.subfacet.social-short-form': string;
  'pluginsHome.subfacet.marketing-product': string;
  'pluginsHome.subfacet.data-explainers': string;
  'pluginsHome.subfacet.cinematic-story': string;
  'pluginsHome.commercialCategory.student-coursework': string;
  'pluginsHome.commercialCategory.corporate-strategy': string;
  'pluginsHome.commercialCategory.professional-training': string;
  'pluginsHome.commercialCategory.b2b-sales': string;
  'pluginsHome.commercialCategory.academic-research': string;
  'pluginsHome.commercialCategory.marketing-gtm': string;
  'pluginsHome.commercialCategory.data-finance': string;
  'pluginsHome.commercialCategory.fundraising-pitch': string;
  'pluginsHome.commercialCategory.government-policy': string;
  'pluginsHome.commercialCategory.product-management': string;
  'pluginsHome.commercialCategory.consulting': string;
  'pluginsHome.commercialCategory.career': string;
  'pluginsHome.commercialCategory.ai-literacy': string;
  'pluginsHome.commercialCategory.life': string;
  'pluginsHome.commercialCategory.design-craft': string;
  'pluginsView.lede': string;
  'pluginsView.importPlugin': string;
  'pluginsView.agentContext': string;
  'pluginsView.summaryAria': string;
  'pluginsView.areasAria': string;
  'pluginsView.loading': string;
  'pluginsView.tab.installed': string;
  'pluginsView.tab.available': string;
  'pluginsView.tab.sources': string;
  'pluginsView.tab.team': string;
  'pluginsView.tabHint.installed': string;
  'pluginsView.tabHint.available': string;
  'pluginsView.tabHint.sources': string;
  'pluginsView.tabHint.team': string;
  'pluginsView.installedTitle': string;
  'pluginsView.installedSubtitle': string;
  'pluginsView.installedEmpty': string;
  'pluginsView.availableTitle': string;
  'pluginsView.availableSubtitle': string;
  'pluginsView.availableFiltersAria': string;
  'pluginsView.searchAvailableAria': string;
  'pluginsView.searchAvailablePlaceholder': string;
  'pluginsView.clearAvailableSearch': string;
  'pluginsView.source': string;
  'pluginsView.availableEmptyInstalled': string;
  'pluginsView.availableEmptyFiltered': string;
  'pluginsView.availableEmptyNoSources': string;
  'pluginsView.installing': string;
  'pluginsView.installWaiting': string;
  'pluginsView.installQueuedHint': string;
  'pluginsView.install': string;
  'pluginsView.sourcesTitle': string;
  'pluginsView.sourcesSubtitle': string;
  'pluginsView.sourceUrl': string;
  'pluginsView.defaultTrust': string;
  'pluginsView.trust.restricted': string;
  'pluginsView.trust.trusted': string;
  'pluginsView.trust.official': string;
  'pluginsView.adding': string;
  'pluginsView.addSource': string;
  'pluginsView.sourcesEmpty': string;
  'pluginsView.pluginsCount': string;
  'pluginsView.catalogVersion': string;
  'pluginsView.trustFor': string;
  'pluginsView.refreshing': string;
  'pluginsView.removing': string;
  'pluginsView.teamTitle': string;
  'pluginsView.teamBody': string;
  'pluginsView.marketplaceTitle': string;
  'pluginsView.marketplaceBody': string;
  'pluginsView.marketplaceModeAria': string;
  'pluginsView.marketplaceSourceFiltersAria': string;
  'pluginsView.scope.official': string;
  'pluginsView.scope.team': string;
  'pluginsView.scope.personal': string;
  'pluginsView.kind.plugins': string;
  'pluginsView.kind.skills': string;
  'pluginsView.searchPlugins': string;
  'pluginsView.searchSkills': string;
  'pluginsView.modeNote.plugins': string;
  'pluginsView.modeNote.skills': string;
  'pluginsView.create': string;
  'pluginsView.createTitle': string;
  'pluginsView.createWithAgent': string;
  'pluginsView.createWithAgentBody': string;
  'pluginsView.createWithAgentAction': string;
  'pluginsView.createPluginBody': string;
  'pluginsView.createSkillBody': string;
  'pluginsView.createClose': string;
  'pluginsView.createTypeAria': string;
  'pluginsView.importFromUrl': string;
  'pluginsView.importUrlBody': string;
  'pluginsView.uploadFolder': string;
  'pluginsView.uploadFolderBody': string;
  'pluginsView.importing': string;
  'pluginsView.importAndUpload': string;
  'pluginsView.uploading': string;
  'pluginsView.uploadKind': string;
  'pluginsView.filesSelected': string;
  'pluginsView.chooseFolder': string;
  'pluginsView.emptyNoMatchTitle': string;
  'pluginsView.emptyNoMatchHint': string;
  'pluginsView.emptyTeamTitle': string;
  'pluginsView.emptyTeamHint': string;
  'pluginsView.emptyPersonalPluginsTitle': string;
  'pluginsView.emptyPersonalSkillsTitle': string;
  'pluginsView.emptyPersonalHint': string;
  'pluginsView.emptyOfficialPluginsTitle': string;
  'pluginsView.emptyOfficialSkillsTitle': string;
  'pluginsView.emptyOfficialHint': string;
  'pluginsView.teamSharedBadge': string;
  'pluginsView.tryIt': string;
  'pluginsView.categoriesAria': string;
  'pluginsView.statSkills': string;
  'pluginsView.statConnectors': string;
  'pluginsView.uninstall': string;
  'pluginsView.uninstalling': string;
  'pluginsView.uninstallConfirm': string;
  'pluginsView.uninstallSuccess': string;
  'pluginsView.uninstallFailed': string;
  'pluginsView.sharing': string;
  'pluginsView.unsharing': string;
  'pluginsView.shareToTeam': string;
  'pluginsView.syncToTeam': string;
  'pluginsView.unshareFromTeam': string;
  'pluginsView.moreActions': string;
  'pluginsView.skillUrlUnsupported': string;
  'pluginsView.importPluginSuccess': string;
  'pluginsView.importFailed': string;
  'pluginsView.uploadPluginSuccess': string;
  'pluginsView.uploadFailed': string;
  'pluginsView.importSkillSuccess': string;
  'pluginsView.shareSuccess': string;
  'pluginsView.shareUnavailable': string;
  'pluginsView.shareFailed': string;
  'pluginsView.syncSuccess': string;
  'pluginsView.syncUnavailable': string;
  'pluginsView.syncFailed': string;
  'pluginsView.unshareSuccess': string;
  'pluginsView.unshareUnavailable': string;
  'pluginsView.unshareFailed': string;
  'pluginsView.installSuccess': string;
  'pluginsView.installFailed': string;
  'pluginsView.skillMissingFile': string;
  'pluginsView.skillReadFailed': string;
  'pluginsView.skillMissingName': string;
  'pluginsView.skillEmptyBody': string;
  'pluginCard.details': string;
  'pluginCard.use': string;
  'pluginCard.useWithQuery': string;
  'pluginCard.applying': string;
  'pluginCard.duplicate': string;
  'pluginCard.duplicating': string;
  'pluginCard.duplicateFailed': string;
  'pluginCard.saved': string;
  'pluginCard.publish': string;
  'pluginCard.contribute': string;
  'pluginCard.starting': string;
  'pluginCard.detailsAria': string;
  'pluginCard.chooseUseAria': string;
  'pluginCard.useOptionsAria': string;
  'pluginCard.duplicateAria': string;
  'pluginCard.saveAria': string;
  'pluginCard.savedAria': string;
  'pluginCard.shareAria': string;
  'pluginCard.publishAria': string;
  'pluginCard.publishTitle': string;
  'pluginCard.contributeAria': string;
  'pluginCard.contributeTitle': string;
  'integrations.kicker': string;
  'integrations.lede': string;
  'integrations.agentReady': string;
  'integrations.areasAria': string;
  'integrations.tabLabel.mcp': string;
  'integrations.tabLabel.skills': string;
  'integrations.tabHint.mcp': string;
  'integrations.tabHint.connectors': string;
  'integrations.tabHint.useEverywhere': string;
  'integrations.skillsTitle': string;
  'integrations.skillsBody': string;
  'mcpClient.title': string;
  'mcpClient.subtitle': string;
  'mcpClient.addServer': string;
  'mcpClient.emptyTitle': string;
  'mcpClient.emptyBody': string;
  'mcpClient.saveChanges': string;
  'mcpClient.storedAt': string;
  'mcpClient.daemonError': string;
  'mcpClient.saveFailed': string;
  'useEverywhere.modalAria': string;
  'useEverywhere.modalTitle': string;
  'useEverywhere.modalSubtitle': string;
  'useEverywhere.closeAria': string;
  'useEverywhere.closeTitle': string;
  'useEverywhere.tabsAria': string;
  'useEverywhere.footStrong': string;
  'useEverywhere.footBody': string;
  'useEverywhere.configureMcp': string;
  'useEverywhere.copyGuide': string;
  'useEverywhere.copy': string;
  'useEverywhere.copied': string;
  'useEverywhere.copyFailed': string;
  'useEverywhere.copySnippetAria': string;
  'useEverywhere.section.overview.tab': string;
  'useEverywhere.section.overview.heading': string;
  'useEverywhere.section.overview.intro': string;
  'useEverywhere.section.overview.bullet1': string;
  'useEverywhere.section.overview.bullet2': string;
  'useEverywhere.section.overview.bullet3': string;
  'useEverywhere.section.overview.bullet4': string;
  'useEverywhere.section.overview.bullet5': string;
  'useEverywhere.section.overview.snippet1': string;
  'useEverywhere.section.overview.snippet2': string;
  'useEverywhere.section.overview.snippet3': string;
  'useEverywhere.section.overview.footer': string;
  'useEverywhere.section.cli.tab': string;
  'useEverywhere.section.cli.heading': string;
  'useEverywhere.section.cli.intro': string;
  'useEverywhere.section.cli.bullet1': string;
  'useEverywhere.section.cli.bullet2': string;
  'useEverywhere.section.cli.bullet3': string;
  'useEverywhere.section.cli.bullet4': string;
  'useEverywhere.section.cli.bullet5': string;
  'useEverywhere.section.cli.bullet6': string;
  'useEverywhere.section.cli.snippet1': string;
  'useEverywhere.section.cli.snippet2': string;
  'useEverywhere.section.cli.snippet3': string;
  'useEverywhere.section.cli.snippet4': string;
  'useEverywhere.section.cli.snippet5': string;
  'useEverywhere.section.cli.footer': string;
  'useEverywhere.section.mcp.tab': string;
  'useEverywhere.section.mcp.heading': string;
  'useEverywhere.section.mcp.intro': string;
  'useEverywhere.section.mcp.bullet1': string;
  'useEverywhere.section.mcp.bullet2': string;
  'useEverywhere.section.mcp.bullet3': string;
  'useEverywhere.section.mcp.bullet4': string;
  'useEverywhere.section.mcp.snippet1': string;
  'useEverywhere.section.mcp.snippet2': string;
  'useEverywhere.section.mcp.snippet3': string;
  'useEverywhere.section.mcp.footer': string;
  'useEverywhere.section.http.tab': string;
  'useEverywhere.section.http.heading': string;
  'useEverywhere.section.http.intro': string;
  'useEverywhere.section.http.bullet1': string;
  'useEverywhere.section.http.bullet2': string;
  'useEverywhere.section.http.bullet3': string;
  'useEverywhere.section.http.bullet4': string;
  'useEverywhere.section.http.bullet5': string;
  'useEverywhere.section.http.bullet6': string;
  'useEverywhere.section.http.snippet1': string;
  'useEverywhere.section.http.snippet2': string;
  'useEverywhere.section.http.snippet3': string;
  'useEverywhere.section.http.footer': string;
  'useEverywhere.section.skills.tab': string;
  'useEverywhere.section.skills.heading': string;
  'useEverywhere.section.skills.intro': string;
  'useEverywhere.section.skills.bullet1': string;
  'useEverywhere.section.skills.bullet2': string;
  'useEverywhere.section.skills.bullet3': string;
  'useEverywhere.section.skills.bullet4': string;
  'useEverywhere.section.skills.bullet5': string;
  'useEverywhere.section.skills.snippet1': string;
  'useEverywhere.section.skills.snippet2': string;
  'useEverywhere.section.skills.snippet3': string;
  'useEverywhere.section.skills.snippet4': string;
  'useEverywhere.section.skills.footer': string;

  // Automations entry view
  'tasks.kicker': string;
  'tasks.comingSoon': string;
  'tasks.lede': string;
  'tasks.newAutomation': string;
  'tasks.previewNote': string;
  'tasks.primitivesAria': string;
  'tasks.primitive.orbit.title': string;
  'tasks.primitive.orbit.body': string;
  'tasks.primitive.orbit.enabled': string;
  'tasks.primitive.orbit.manualOnly': string;
  'tasks.primitive.routines.title': string;
  'tasks.primitive.routines.body': string;
  'tasks.primitive.routines.meta': string;
  'tasks.primitive.schedules.title': string;
  'tasks.primitive.schedules.body': string;
  'tasks.primitive.schedules.meta': string;
  'tasks.primitive.liveArtifacts.title': string;
  'tasks.primitive.liveArtifacts.body': string;
  'tasks.primitive.liveArtifacts.meta': string;
  'tasks.listAria': string;
  'tasks.routinesAndRuns': string;
  'tasks.filtersAria': string;
  'tasks.filter.scheduled': string;
  'tasks.filter.running': string;
  'tasks.filter.done': string;
  'tasks.configurationAria': string;
  'tasks.slot.trigger': string;
  'tasks.slot.pattern': string;
  'tasks.slot.runtime': string;
  'tasks.slot.output': string;
  'tasks.liveArtifact': string;
  'tasks.viewProgress': string;
  'tasks.pause': string;
  'tasks.runNow': string;
  'tasks.openArtifact': string;
  'tasks.status.dailyAt': string;
  'tasks.status.pausedManual': string;
  'tasks.sample.orbit.title': string;
  'tasks.sample.orbit.metaEnabled': string;
  'tasks.sample.orbit.metaDisabled': string;
  'tasks.sample.orbit.previewEnabled': string;
  'tasks.sample.orbit.previewDisabled': string;
  'tasks.sample.orbit.triggerEnabled': string;
  'tasks.sample.orbit.triggerDisabled': string;
  'tasks.sample.orbit.pattern': string;
  'tasks.sample.orbit.runtime': string;
  'tasks.sample.orbit.output': string;
  'tasks.sample.orbit.artifactMetaEnabled': string;
  'tasks.sample.orbit.artifactMetaDisabled': string;
  'tasks.sample.orbit.body1': string;
  'tasks.sample.orbit.body2': string;
  'tasks.sample.orbit.body3': string;
  'tasks.sample.mcp.title': string;
  'tasks.sample.mcp.status': string;
  'tasks.sample.mcp.meta': string;
  'tasks.sample.mcp.preview': string;
  'tasks.sample.mcp.trigger': string;
  'tasks.sample.mcp.pattern': string;
  'tasks.sample.mcp.runtime': string;
  'tasks.sample.mcp.output': string;
  'tasks.sample.mcp.artifactMeta': string;
  'tasks.sample.mcp.body1': string;
  'tasks.sample.mcp.body2': string;
  'tasks.sample.mcp.body3': string;
  'tasks.sample.mcp.body4': string;
  'tasks.sample.mcp.body5': string;
  'tasks.sample.weekly.title': string;
  'tasks.sample.weekly.status': string;
  'tasks.sample.weekly.meta': string;
  'tasks.sample.weekly.preview': string;
  'tasks.sample.weekly.trigger': string;
  'tasks.sample.weekly.pattern': string;
  'tasks.sample.weekly.runtime': string;
  'tasks.sample.weekly.output': string;
  'tasks.sample.weekly.artifactMeta': string;
  'tasks.sample.weekly.body1': string;
  'tasks.sample.weekly.body2': string;
  'tasks.sample.weekly.body3': string;
  'tasks.sample.weekly.body4': string;
  'tasks.sample.weekly.body5': string;
  'tasks.sample.pr.title': string;
  'tasks.sample.pr.status': string;
  'tasks.sample.pr.meta': string;
  'tasks.sample.pr.preview': string;
  'tasks.sample.pr.trigger': string;
  'tasks.sample.pr.pattern': string;
  'tasks.sample.pr.runtime': string;
  'tasks.sample.pr.output': string;
  'tasks.sample.pr.artifactMeta': string;
  'tasks.sample.pr.body1': string;
  'tasks.sample.pr.body2': string;
  'tasks.sample.pr.body3': string;
  'tasks.sample.meeting.title': string;
  'tasks.sample.meeting.status': string;
  'tasks.sample.meeting.meta': string;
  'tasks.sample.meeting.preview': string;
  'tasks.sample.meeting.trigger': string;
  'tasks.sample.meeting.pattern': string;
  'tasks.sample.meeting.runtime': string;
  'tasks.sample.meeting.output': string;
  'tasks.sample.meeting.artifactMeta': string;
  'tasks.sample.meeting.body1': string;
  'tasks.sample.meeting.body2': string;
  'tasks.sample.meeting.body3': string;
  'tasks.sample.candidate.title': string;
  'tasks.sample.candidate.status': string;
  'tasks.sample.candidate.meta': string;
  'tasks.sample.candidate.preview': string;
  'tasks.sample.candidate.trigger': string;
  'tasks.sample.candidate.pattern': string;
  'tasks.sample.candidate.runtime': string;
  'tasks.sample.candidate.output': string;
  'tasks.sample.candidate.artifactMeta': string;
  'tasks.sample.candidate.body1': string;
  'tasks.sample.candidate.body2': string;
  'tasks.sample.candidate.body3': string;
  // Routines / Automations settings page (issue #1372)
  'routines.title': string;
  'routines.subtitle': string;
  'routines.newAutomation': string;
  'routines.loading': string;
  'routines.empty': string;
  'routines.emptyHint': string;
  'routines.runHistoryLoading': string;
  'routines.runHistoryEmpty': string;
  'routines.fieldName': string;
  'routines.fieldNamePlaceholder': string;
  'routines.fieldPrompt': string;
  'routines.fieldPromptPlaceholder': string;
  'routines.fieldSchedule': string;
  'routines.fieldMinute': string;
  'routines.fieldTime': string;
  'routines.fieldTimezone': string;
  'routines.fieldsetProject': string;
  'routines.kind.hourly': string;
  'routines.kind.daily': string;
  'routines.kind.weekdays': string;
  'routines.kind.weekly': string;
  'routines.weekday.short.0': string;
  'routines.weekday.short.1': string;
  'routines.weekday.short.2': string;
  'routines.weekday.short.3': string;
  'routines.weekday.short.4': string;
  'routines.weekday.short.5': string;
  'routines.weekday.short.6': string;
  'routines.weekday.long.0': string;
  'routines.weekday.long.1': string;
  'routines.weekday.long.2': string;
  'routines.weekday.long.3': string;
  'routines.weekday.long.4': string;
  'routines.weekday.long.5': string;
  'routines.weekday.long.6': string;
  'routines.timeAm': string;
  'routines.timePm': string;
  'routines.describe.hourly': string;
  'routines.describe.daily': string;
  'routines.describe.weekdays': string;
  'routines.describe.weekly': string;
  'routines.modeCreate': string;
  'routines.modeCreateHint': string;
  'routines.modeReuse': string;
  'routines.modeReuseHint': string;
  'routines.pickProject': string;
  'routines.targetReuse': string;
  'routines.targetCreate': string;
  'routines.tagPaused': string;
  'routines.metaNext': string;
  'routines.metaLast': string;
  'routines.triggerManual': string;
  'routines.triggerScheduled': string;
  'routines.openProject': string;
  'routines.openProjectTitle': string;
  'routines.runNow': string;
  'routines.edit': string;
  'routines.pause': string;
  'routines.resume': string;
  'routines.history': string;
  'routines.hideHistory': string;
  'routines.delete': string;
  'routines.deleteTitle': string;
  'routines.cancel': string;
  'routines.save': string;
  'routines.saving': string;
  'routines.create': string;
  'routines.creating': string;
  'routines.status.queued': string;
  'routines.status.running': string;
  'routines.status.succeeded': string;
  'routines.status.failed': string;
  'routines.status.canceled': string;
  'routines.confirmDelete': string;
  'routines.errorPickProject': string;
  'routines.errorAgentEmptyOutput': string;
  // Automations tab (TasksView)
  'automations.eyebrow': string;
  'automations.title': string;
  'automations.lede': string;
  'automations.summaryAria': string;
  'automations.metricActive': string;
  'automations.metricPaused': string;
  'automations.metricTemplates': string;
  'automations.newAutomation': string;
  'automations.yourAutomations': string;
  'automations.loading': string;
  'automations.emptyTitle': string;
  'automations.emptyBody': string;
  'automations.targetNewEachRun': string;
  'automations.lastRun': string;
  'automations.openResult': string;
  'automations.run': string;
  'automations.runNowTitle': string;
  'automations.targetCreateEachRun': string;
  'automations.history': string;
  'automations.hideHistory': string;
  'automations.edit': string;
  'automations.pause': string;
  'automations.resume': string;
  'automations.deleteAria': string;
  'automations.deleteTitle': string;
  'automations.deleteConfirm': string;
  'automations.proposalsTitle': string;
  'automations.proposalsSub': string;
  'automations.proposalsPending': string;
  'automations.apply': string;
  'automations.reject': string;
  'automations.proposalsDismissReason': string;
  'automations.templatesTitle': string;
  'automations.templatesSub': string;
  'automations.templatesCount': string;
  'automations.templateFiltersAria': string;
  'automations.templatesEmptyTitle': string;
  'automations.templatesEmptyBody': string;
  'automations.useTemplate': string;
  'automations.runHistoryLoading': string;
  'automations.runHistoryEmpty': string;
  'automations.runHistoryTitle': string;
  'automations.runHistoryLatest': string;
  'automations.promptPlaceholder': string;
  'automations.crystallize': string;
  'automations.crystallizing': string;
  'automations.crystallizeTitle': string;
  'automations.crystallizeFailed': string;
  'automations.crystallizePartialSuccess': string;
  'automations.crystallizeRefreshFailed': string;
  'automations.crystallizeNoProposals': string;
  'automations.openConversation': string;
  'automations.runHistoryAria': string;
  'automations.proposalsAria': string;
  'automations.templatesAria': string;
  'automations.runInProgress': string;
  'automations.scheduleStatusPaused': string;
  'automations.nextRunManualOnly': string;
  'automations.nextRunScheduled': string;
  'automations.nextRunAt': string;
  'automations.statusSucceeded': string;
  'automations.statusFailed': string;
  'automations.statusRunning': string;
  'automations.statusQueued': string;
  'automations.statusCanceled': string;
  'automations.kindOrbit': string;
  'automations.kindLiveArtifact': string;
  'automations.kindAutomation': string;
  'automations.proposalTargetMemory': string;
  'automations.proposalTargetDesignSystem': string;
  'automations.proposalTargetSkill': string;
  'automations.proposalTargetTemplate': string;
  'automations.proposalActionCreate': string;
  'automations.proposalActionUpdate': string;
  'automations.proposalActionMerge': string;
  'automations.proposalActionMove': string;
  'automations.proposalActionDelete': string;
  'automations.proposalActionPromote': string;
  'automations.filterAll': string;
  'automations.filterOrbit': string;
  'automations.filterLiveArtifacts': string;
  'automations.filterMemory': string;
  'automations.filterDesignSystems': string;
  'automations.filterSkills': string;
  'automations.filterConnectors': string;
  'automations.filterCompression': string;
  'automations.filterRelease': string;
  'automations.filterQuality': string;
  'automations.tpl.memoryRefresh.title': string;
  'automations.tpl.memoryRefresh.desc': string;
  'automations.tpl.designSystemRefresh.title': string;
  'automations.tpl.designSystemRefresh.desc': string;
  'automations.tpl.liveArtifactRegistry.title': string;
  'automations.tpl.liveArtifactRegistry.desc': string;
  'automations.tpl.orbitDashboard.title': string;
  'automations.tpl.orbitDashboard.desc': string;
  'automations.tpl.releaseNotes.title': string;
  'automations.tpl.releaseNotes.desc': string;
  'automations.tpl.qualityRegressionWatch.title': string;
  'automations.tpl.qualityRegressionWatch.desc': string;
  'automations.tpl.orbitDaily.title': string;
  'automations.tpl.orbitDaily.desc': string;
  'automations.tpl.liveStatusBoard.title': string;
  'automations.tpl.liveStatusBoard.desc': string;
  // Design systems manager tab (DesignSystemsTab)
  'dsManager.areaAria': string;
  'dsManager.tabDesignSystem': string;
  'dsManager.tabTemplate': string;
  'dsManager.sourceAria': string;
  'dsManager.templateSourceAria': string;
  'dsManager.yourSystems': string;
  'dsManager.officialPresets': string;
  'dsManager.shareToTeam': string;
  'dsManager.shareToTeamFailed': string;
  'dsManager.syncToTeam': string;
  'dsManager.syncToTeamFailed': string;
  'dsManager.unshareFromTeam': string;
  'dsManager.unshareFromTeamFailed': string;
  'dsManager.teamSyncedReadOnly': string;
  'dsManager.enterprise': string;
  'dsManager.yourTemplates': string;
  'dsManager.yourSystemsAria': string;
  'dsManager.eyebrowDesignSystems': string;
  'dsManager.yourSystemsHeading': string;
  'dsManager.filterAria': string;
  'dsManager.filterAll': string;
  'dsManager.filterPublished': string;
  'dsManager.filterDraft': string;
  'dsManager.createTitle': string;
  'dsManager.createBody': string;
  'dsManager.createAction': string;
  'dsManager.emptyMine': string;
  'dsManager.badgeDefault': string;
  'dsManager.rowMetaUpdated': string;
  'dsManager.edit': string;
  'dsManager.openSystem': string;
  'dsManager.editWithAgent': string;
  'dsManager.downloadTitle': string;
  'dsManager.downloadAria': string;
  'dsManager.downloadFailed': string;
  'dsManager.deleteConfirm': string;
  'dsManager.deletePermissionDenied': string;
  'dsManager.makeDefault': string;
  'dsManager.statusPublished': string;
  'dsManager.statusDraft': string;
  'dsManager.openSystemAria': string;
  'dsManager.deleteSystemAria': string;
  'dsManager.presetsAria': string;
  'dsManager.eyebrowLibrary': string;
  'dsManager.eyebrowTemplates': string;
  'dsManager.yourTemplatesAria': string;
  'dsManager.emptyTemplates': string;
  'dsManager.templateDescFallback': string;
  'dsManager.comingSoonBadge': string;
  'dsManager.enterpriseDsTitle': string;
  'dsManager.enterpriseDsBody': string;
  'dsManager.enterpriseTplTitle': string;
  'dsManager.enterpriseTplBody': string;
  // Settings instructions + MCP forwarding banner
  'settings.instructionsTitle': string;
  'settings.instructionsSubtitle': string;
  'settings.instructionsNavSub': string;
  'settings.customInstructionsDesc': string;
  'mcpClient.forwardedToLabel': string;
  'mcpClient.notForwardedToLabel': string;
  'mcpClient.forwardedAcpNote': string;
  'mcpClient.notForwardedNote': string;
  // GitHub star pill in the top bar
  'entry.githubStarLabel': string;
  'entry.githubStarTitle': string;
  'entry.githubStarAria': string;

  // Connectors tab
  'connectors.title': string;
  'connectors.subtitle': string;
  'connectors.account': string;
  'connectors.noAccount': string;
  'connectors.tools': string;
  'connectors.connect': string;
  'connectors.disconnect': string;
  'connectors.authorizationPending': string;
  'connectors.authorizationPendingHint': string;
  'connectors.cancelAuthorization': string;
  'connectors.configure': string;
  'connectors.unavailable': string;
  'connectors.phaseStubTitle': string;
  'connectors.statusAvailable': string;
  'connectors.statusConnected': string;
  'connectors.statusError': string;
  'connectors.statusDisabled': string;
  'connectors.gateTitle': string;
  'connectors.gateBody': string;
  'connectors.aboutLabel': string;
  'connectors.detailsLabel': string;
  'connectors.statusLabel': string;
  'connectors.categoryLabel': string;
  'connectors.category.aiAgents': string;
  'connectors.category.aiInfrastructure': string;
  'connectors.category.accounting': string;
  'connectors.category.admin': string;
  'connectors.category.advertising': string;
  'connectors.category.analytics': string;
  'connectors.category.automation': string;
  'connectors.category.cms': string;
  'connectors.category.crm': string;
  'connectors.category.calendar': string;
  'connectors.category.commerce': string;
  'connectors.category.communication': string;
  'connectors.category.contacts': string;
  'connectors.category.dataPlatform': string;
  'connectors.category.database': string;
  'connectors.category.design': string;
  'connectors.category.developer': string;
  'connectors.category.documentation': string;
  'connectors.category.erp': string;
  'connectors.category.education': string;
  'connectors.category.email': string;
  'connectors.category.events': string;
  'connectors.category.fieldService': string;
  'connectors.category.finance': string;
  'connectors.category.fitness': string;
  'connectors.category.forms': string;
  'connectors.category.gaming': string;
  'connectors.category.hr': string;
  'connectors.category.hospitality': string;
  'connectors.category.itsm': string;
  'connectors.category.integration': string;
  'connectors.category.localization': string;
  'connectors.category.logistics': string;
  'connectors.category.maps': string;
  'connectors.category.marketing': string;
  'connectors.category.media': string;
  'connectors.category.meetings': string;
  'connectors.category.nonprofit': string;
  'connectors.category.observability': string;
  'connectors.category.payments': string;
  'connectors.category.personal': string;
  'connectors.category.presentations': string;
  'connectors.category.procurement': string;
  'connectors.category.product': string;
  'connectors.category.productivity': string;
  'connectors.category.projectManagement': string;
  'connectors.category.recruiting': string;
  'connectors.category.research': string;
  'connectors.category.salesIntelligence': string;
  'connectors.category.scheduling': string;
  'connectors.category.search': string;
  'connectors.category.security': string;
  'connectors.category.signing': string;
  'connectors.category.social': string;
  'connectors.category.spreadsheets': string;
  'connectors.category.storage': string;
  'connectors.category.support': string;
  'connectors.category.surveys': string;
  'connectors.category.tasks': string;
  'connectors.category.timeTracking': string;
  'connectors.category.video': string;
  'connectors.category.whiteboard': string;
  'connectors.providerLabel': string;
  'connectors.toolsSection': string;
  'connectors.toolsLoading': string;
  'connectors.noToolsAvailable': string;
  'connectors.toolDetailsUnavailable': string;
  'connectors.loadMoreTools': string;
  'connectors.openDetailsAria': string;
  'connectors.toolsBadgeNone': string;
  'connectors.toolsBadgeOne': string;
  'connectors.toolsBadgeMany': string;
  'connectors.searchPlaceholder': string;
  'connectors.searchAriaLabel': string;
  'connectors.searchClear': string;
  'connectors.emptyNoMatchTitle': string;
  'connectors.emptyNoMatchBody': string;
  'connectors.emptyNoMatchAction': string;

  // New project panel
  'newproj.tabPrototype': string;
  'newproj.tabLiveArtifact': string;
  'newproj.tabDeck': string;
  'newproj.tabTemplate': string;
  'newproj.tabMedia': string;
  'newproj.tabOther': string;
  'newproj.titlePrototype': string;
  'newproj.titleLiveArtifact': string;
  'newproj.titleDeck': string;
  'newproj.titleTemplate': string;
  'newproj.titleImage': string;
  'newproj.titleVideo': string;
  'newproj.titleAudio': string;
  'newproj.titleMedia': string;
  'newproj.titleOther': string;
  'newproj.namePlaceholder': string;
  'newproj.fidelityLabel': string;
  'newproj.fidelityWireframe': string;
  'newproj.fidelityHigh': string;
  'newproj.toggleSpeakerNotes': string;
  'newproj.toggleSpeakerNotesHint': string;
  'newproj.toggleAnimations': string;
  'newproj.toggleAnimationsHint': string;
  'newproj.targetPlatformsLabel': string;
  'newproj.targetPlatformsHint': string;
  'newproj.platform.responsive.label': string;
  'newproj.platform.responsive.hint': string;
  'newproj.platform.webDesktop.label': string;
  'newproj.platform.webDesktop.hint': string;
  'newproj.platform.mobileIos.label': string;
  'newproj.platform.mobileIos.hint': string;
  'newproj.platform.mobileAndroid.label': string;
  'newproj.platform.mobileAndroid.hint': string;
  'newproj.platform.tablet.label': string;
  'newproj.platform.tablet.hint': string;
  'newproj.platform.desktopApp.label': string;
  'newproj.platform.desktopApp.hint': string;
  'newproj.surfaceOptionsLabel': string;
  'newproj.includeLandingPage': string;
  'newproj.includeLandingPageHint': string;
  'newproj.includeOsWidgets': string;
  'newproj.includeOsWidgetsHint': string;
  'newproj.includeOsWidgetsDisabledHint': string;
  'newproj.templateLabel': string;
  'newproj.noTemplatesTitle': string;
  'newproj.noTemplatesBody': string;
  'newproj.savedTemplate': string;
  'newproj.fileSingular': string;
  'newproj.filePlural': string;
  'newproj.create': string;
  'newproj.locationLabel': string;
  'newproj.locationDefault': string;
  'newproj.locationExternalBase': string;
  'newproj.createLiveArtifact': string;
  'newproj.createFromTemplate': string;
  'newproj.createDisabledTitle': string;
  'newproj.openFolder': string;
  'newproj.openingFolder': string;
  'newproj.importClaudeZip': string;
  'newproj.importClaudeZipTitle': string;
  'newproj.importingClaudeZip': string;
  'newproj.privacyFooter': string;
  'newproj.designSystem': string;
  'newproj.dsNoneFreeform': string;
  'newproj.dsNoneSubtitleEmpty': string;
  'newproj.dsNoneSubtitleSelected': string;
  'newproj.dsCategoryFallback': string;
  'newproj.dsSearch': string;
  'newproj.dsModeAria': string;
  'newproj.dsModeSingle': string;
  'newproj.dsModeMulti': string;
  'newproj.dsNoneTitle': string;
  'newproj.dsNoneSub': string;
  'newproj.dsEmpty': string;
  'newproj.dsFootSingular': string;
  'newproj.dsFootPlural': string;
  'newproj.dsFootClear': string;
  'newproj.dsBadgeDefault': string;
  'newproj.dsPrimaryFallback': string;
  'newproj.surfaceImage': string;
  'newproj.surfaceVideo': string;
  'newproj.surfaceAudio': string;
  'newproj.modelLabel': string;
  'newproj.modelSearch': string;
  'newproj.modelEmpty': string;
  'newproj.modelRecommended': string;
  'newproj.modelMissingTitle': string;
  'newproj.modelMissingSub': string;
  'newproj.aspectLabel': string;
  'newproj.videoLengthLabel': string;
  'newproj.videoLengthSeconds': string;
  'newproj.audioKindLabel': string;
  'newproj.audioKindMusic': string;
  'newproj.audioKindSpeech': string;
  'newproj.audioKindSfx': string;
  'newproj.audioDurationLabel': string;
  'newproj.audioDurationSeconds': string;
  'newproj.voiceLabel': string;
  'newproj.voicePlaceholder': string;
  'newproj.connectorsLabel': string;
  'newproj.connectorsHint': string;
  'newproj.connectorsEmptyTitle': string;
  'newproj.connectorsEmptyBody': string;
  'newproj.connectorsEmptyCta': string;
  'newproj.connectorsLoading': string;
  'newproj.connectorsCountOne': string;
  'newproj.connectorsCountMany': string;
  'newproj.connectorsManage': string;
  'newproj.promptTemplateLabel': string;
  'newproj.promptTemplateNoneTitle': string;
  'newproj.promptTemplateNoneSub': string;
  'newproj.promptTemplateRefSub': string;
  'newproj.promptTemplateSearch': string;
  'newproj.promptTemplateEmpty': string;
  'newproj.promptTemplateBodyLabel': string;
  'newproj.promptTemplateOptimizeHint': string;
  'newproj.promptTemplateBodyEmpty': string;
  'newproj.deleteTemplateTitle': string;
  'newproj.deleteTemplateConfirm': string;
  'newproj.deleteTemplateConfirmCta': string;
  'newproj.deleteTemplateError': string;

  // Prompt templates
  'promptTemplates.emptyNoMatch': string;
  'promptTemplates.fetchError': string;
  'promptTemplates.promptLabel': string;
  'promptTemplates.copyPrompt': string;
  'promptTemplates.copyDone': string;
  'promptTemplates.allSources': string;
  'promptTemplates.retry': string;

  // Designs tab
  'designs.subRecent': string;
  'designs.subYours': string;
  'designs.filterAria': string;
  'designs.searchPlaceholder': string;
  'designs.emptyNoProjects': string;
  'designs.emptyNoMatch': string;
  'designs.deleteTitle': string;
  'designs.deleteConfirm': string;
  'designs.cardFreeform': string;
  'designs.badgeLive': string;
  'designs.liveArtifactBadgesAria': string;
  'designs.liveCount': string;
  'designs.statusLive': string;
  'designs.statusArchived': string;
  'designs.statusError': string;
  'designs.statusRefreshing': string;
  'designs.statusRefreshFailed': string;
  'designs.statusRefreshed': string;
  'designs.status.notStarted': string;
  'designs.status.queued': string;
  'designs.status.running': string;
  'designs.status.awaitingInput': string;
  'designs.status.incomplete': string;
  'designs.status.succeeded': string;
  'designs.status.failed': string;
  'designs.status.canceled': string;
  'designs.status.published': string;
  'designs.viewToggleAria': string;
  'designs.viewGrid': string;
  'designs.viewKanban': string;
  'designs.kanbanEmptyColumn': string;
  'designs.deleteAria': string;
  'designs.menuMore': string;
  'designs.menuRename': string;
  'designs.menuDuplicate': string;
  'designs.menuDelete': string;
  'designs.renamePrompt': string;
  'designs.selectMode': string;
  'designs.cancelSelect': string;
  'designs.deleteSelected': string;
  'designs.selectedCount': string;
  'designs.deleteSelectedConfirm': string;
  'designs.deleteSelectedSuccess': string;
  'designs.deleteSelectedPartial': string;
  'designs.tagPrototype': string;
  'designs.tagLiveArtifact': string;
  'designs.tagWebClone': string;
  'designs.tagSlide': string;
  'designs.tagMedia': string;
  'designs.renameTitle': string;
  'designs.renameSave': string;
  'designs.renameCancel': string;

  // Examples tab
  'examples.typeLabel': string;
  'examples.surfaceLabel': string;
  'examples.surfaceWeb': string;
  'examples.surfaceImage': string;
  'examples.surfaceVideo': string;
  'examples.surfaceAudio': string;
  'examples.scenarioLabel': string;
  'examples.modeAll': string;
  'examples.modePrototypeDesktop': string;
  'examples.modePrototypeMobile': string;
  'examples.modeDeck': string;
  'examples.modeDocument': string;
  'examples.modeOrbit': string;
  'examples.modeLive': string;
  'examples.scenarioGeneral': string;
  'examples.scenarioEngineering': string;
  'examples.scenarioProduct': string;
  'examples.scenarioDesign': string;
  'examples.scenarioMarketing': string;
  'examples.scenarioSales': string;
  'examples.scenarioFinance': string;
  'examples.scenarioHr': string;
  'examples.scenarioOperations': string;
  'examples.scenarioSupport': string;
  'examples.scenarioLegal': string;
  'examples.scenarioEducation': string;
  'examples.scenarioPersonal': string;
  'examples.searchPlaceholder': string;
  'examples.searchAria': string;
  'examples.emptyNoSkills': string;
  'examples.emptyNoMatch': string;
  'examples.openPreview': string;
  'examples.loadingPreview': string;
  'examples.hoverPreview': string;
  'examples.usePrompt': string;
  'examples.previewModalTitle': string;
  'examples.shareTitle': string;
  'examples.shareLoadFirst': string;
  // Card placeholder + share-button hint for skills whose
  // `od.preview.type` is not `html` (image / markdown / …) so the
  // gallery doesn't sit on a forever "Loading preview…" shimmer for
  // skills that ship no fetchable artifact. Issue #897.
  'examples.unavailablePlaceholder': string;
  'examples.shareUnavailable': string;
  'examples.shareMenu': string;
  'examples.exportPdfAllSlides': string;
  'examples.exportPptxLocked': string;
  'examples.tagSlideDeck': string;
  'examples.tagTemplate': string;
  'examples.tagDesignSystem': string;
  'examples.tagMobilePrototype': string;
  'examples.tagDesktopPrototype': string;
  'examples.tagImage': string;
  'examples.tagVideo': string;
  'examples.tagAudio': string;
  'examples.previewLabel': string;

  // Design systems tab
  'ds.surfaceLabel': string;
  'ds.surfaceWeb': string;
  'ds.surfaceImage': string;
  'ds.surfaceVideo': string;
  'ds.surfaceAudio': string;
  'ds.searchPlaceholder': string;
  'ds.emptyNoMatch': string;
  'ds.badgeDefault': string;
  'ds.preview': string;
  'ds.kitVisualize': string;
  'ds.kitEdit': string;
  'ds.kitSource': string;
  'ds.saveDesignMd': string;
  'ds.saving': string;
  'ds.copyDesignMd': string;
  'ds.editDesignMd': string;
  'ds.openDesignMd': string;
  'ds.copyDesignMdModule': string;
  'ds.editDesignMdModule': string;
  'ds.openDesignMdModule': string;
  'ds.uploadMd': string;
  'ds.uploadFont': string;
  'ds.pasteImage': string;
  'ds.refresh': string;
  'ds.download': string;
  'ds.importFolder': string;
  'ds.reset': string;
  'ds.editingDesignMdHint': string;
  'ds.editingModuleHint': string;
  'ds.manualEditModuleHint': string;
  'ds.saveModule': string;
  'ds.uploadLogo': string;
  'ds.uploadImage': string;
  'ds.uploading': string;
  'ds.moduleEmptyLogo': string;
  'ds.moduleEmptyFonts': string;
  'ds.actionDone': string;
  'ds.actionFailed': string;
  'ds.uploadDone': string;
  'ds.uploadFailed': string;
  'ds.deleteLogo': string;
  'ds.deleteImage': string;
  'ds.deleteProjectConfirm': string;
  'ds.deleteProjectAction': string;
  'ds.editColor': string;
  'ds.colorLabel': string;
  'ds.hexLabel': string;
  'ds.hexValueLabel': string;
  'ds.saveColor': string;
  'ds.invalidHexColor': string;
  'ds.colorSaveFailed': string;
  'ds.colorResetFailed': string;
  'ds.designMdSectionLabel': string;
  'ds.noOriginalColor': string;
  'ds.manifestReadFailed': string;
  'ds.reviewCollapseSection': string;
  'ds.reviewExpandSection': string;
  'ds.reviewActionsLabel': string;
  'ds.reviewNeedsReview': string;
  'ds.reviewLooksGood': string;
  'ds.reviewNeedsWork': string;
  'ds.reviewNeedsWorkEllipsis': string;
  'ds.reviewEditFile': string;
  'ds.reviewFeedbackLabel': string;
  'ds.reviewFeedbackPlaceholder': string;
  'ds.reviewChangedAfterFeedback': string;
  'ds.reviewChangedDuringRun': string;
  'ds.reviewLastFeedback': string;
  'ds.reviewLastReviewed': string;
  'ds.previewGenerating': string;
  'ds.creatingProjectTitle': string;
  'ds.creatingProjectSubtitle': string;
  'ds.generationProgressLabel': string;
  'ds.publishRepoRequiredTitle': string;
  'ds.publishDesignSystem': string;
  'ds.unpublishDesignSystem': string;
  'ds.publish': string;
  'ds.published': string;
  'ds.extractionRunningTitle': string;
  'ds.extractionCompleteTitle': string;
  'ds.extractionRunningBody': string;
  'ds.extractionCompleteBody': string;
  'ds.publishCardPublished': string;
  'ds.publishCardDraft': string;
  'ds.useSystemTitle': string;
  'ds.useSystemBody': string;
  'ds.createNewDesign': string;
  'ds.openSourceContext': string;
  'ds.manifestNeedsAttention': string;
  'ds.openManifest': string;
  'ds.workspacePreparing': string;
  'ds.workspaceLoadingLabel': string;
  'ds.sectionQueued': string;
  'ds.sectionReviewUpdatedFiles': string;
  'ds.sectionRequiredFileMissing': string;
  'ds.sectionNoFilesYet': string;
  'ds.generationSourceTitle': string;
  'ds.generationSourceDetail': string;
  'ds.generationGuidanceTitle': string;
  'ds.generationGuidanceDetail': string;
  'ds.generationTokensTitle': string;
  'ds.generationTokensDetail': string;
  'ds.generationPreviewsTitle': string;
  'ds.generationPreviewsDetail': string;
  'ds.generationUiKitTitle': string;
  'ds.generationUiKitDetail': string;
  'ds.generationAssetsTitle': string;
  'ds.generationAssetsDetail': string;
  'ds.phaseQueuedTypography': string;
  'ds.phaseQueuedTokens': string;
  'ds.phaseQueuedSpacing': string;
  'ds.phaseQueuedUiKit': string;
  'ds.phaseQueuedAssets': string;
  'ds.phaseReadingTypography': string;
  'ds.phaseReadingTokens': string;
  'ds.phaseReadingSpacing': string;
  'ds.phaseReadingUiKit': string;
  'ds.phaseReadingAssets': string;
  'ds.phaseWritingTypography': string;
  'ds.phaseWritingTokens': string;
  'ds.phaseWritingSpacing': string;
  'ds.phaseBuildingUiKit': string;
  'ds.phaseUpdatingAssets': string;
  'ds.phaseNeedsAttention': string;
  'ds.phaseUpdated': string;
  'ds.sectionActivityFromTodo': string;
  'ds.sectionActivityReadFiles': string;
  'ds.sectionActivityPhaseFiles': string;
  'ds.sectionRunningReadingContext': string;
  'ds.sectionRunningNow': string;
  'ds.agentFeedbackQueued': string;
  'ds.agentFeedbackSent': string;
  'ds.agentFeedbackSentAt': string;
  'ds.agentFeedbackFailedWithError': string;
  'ds.agentFeedbackFailed': string;
  'ds.agentFeedbackUnknown': string;
  'ds.repoConnectBannerTitle': string;
  'ds.repoConnectCardTitle': string;
  'ds.repoConnectPendingBody': string;
  'ds.repoConnectPendingButton': string;
  'ds.repoConnectedTitle': string;
  'ds.repoConnectedBody': string;
  'ds.repoImportButton': string;
  'ds.repoConnectBannerBody': string;
  'ds.repoConnectCardBody': string;
  'ds.repoConnectButton': string;
  'ds.kitErrorTitle': string;
  'ds.kitErrorRetry': string;
  'ds.shortcutsLabel': string;
  'ds.shortcutsHint': string;
  'ds.moduleEmptyImages': string;
  'ds.previewTitle': string;
  'ds.categoryAll': string;
  'ds.categoryUncategorized': string;
  'ds.showcase': string;
  'ds.tokens': string;
  'ds.specToggle': string;
  'ds.specLoading': string;

  // Avatar menu (project topbar)
  'avatar.title': string;
  'avatar.localCli': string;
  'avatar.anthropicApi': string;
  'avatar.useLocal': string;
  'avatar.useApi': string;
  'avatar.codeAgent': string;
  'avatar.rescan': string;
  'avatar.amrConsole': string;
  'avatar.amrConsoleMeta': string;
  'avatar.settings': string;
  'avatar.backToProjects': string;
  'avatar.metaActive': string;
  'avatar.metaOffline': string;
  'avatar.metaSelected': string;
  'avatar.noAgentSelected': string;
  'avatar.modelSection': string;
  'avatar.modelLabel': string;
  'avatar.reasoningLabel': string;
  'avatar.serviceTierLabel': string;
  'avatar.customSuffix': string;
  'socialShare.openDesignSection': string;
  'socialShare.openDesignTitle': string;
  'socialShare.openDesignText': string;
  'socialShare.openDesignCopyText': string;
  'socialShare.projectSection': string;
  'socialShare.publishPageTitle': string;
  'socialShare.publishPageSubtitle': string;
  'socialShare.projectTitle': string;
  'socialShare.projectText': string;
  'socialShare.projectCopyText': string;
  'socialShare.deployFirst': string;
  'socialShare.copied': string;
  'socialShare.platform.x': string;
  'socialShare.platform.linkedin': string;
  'socialShare.platform.facebook': string;
  'socialShare.platform.reddit': string;
  'socialShare.platform.telegram': string;
  'socialShare.platform.whatsapp': string;
  'socialShare.platform.weibo': string;
  'socialShare.platform.line': string;
  'socialShare.platform.instagram': string;
  'socialShare.platform.xiaohongshu': string;

  // Inline CLI / model switcher (entry topbar)
  'inlineSwitcher.chipTitle': string;
  'inlineSwitcher.chipCli': string;
  'inlineSwitcher.chipByok': string;
  'inlineSwitcher.modelDefault': string;
  'inlineSwitcher.noAgent': string;
  'inlineSwitcher.modeLabel': string;
  'inlineSwitcher.agentLabel': string;
  'inlineSwitcher.providerLabel': string;
  'inlineSwitcher.modelLabel': string;
  'inlineSwitcher.useCli': string;
  'inlineSwitcher.useByok': string;
  'inlineSwitcher.daemonOffline': string;
  'inlineSwitcher.noAgentsDetected': string;
  'inlineSwitcher.openSettingsForModel': string;
  'inlineSwitcher.missingApiKey': string;
  'inlineSwitcher.openFullSettings': string;
  'inlineSwitcher.customSuffix': string;
  'inlineSwitcher.unlimitedBadge': string;

  // Project view / chat pane / composer
  'project.backToProjects': string;
  'project.brandReadyTitle': string;
  'project.brandReadyTitleGeneric': string;
  'project.brandReadyCta': string;
  'project.brandReadyDismiss': string;
  'project.brandReadyRefineHint': string;
  'project.brandReadyAiOptimize': string;
  'project.brandReadyEditManually': string;
  'project.brandReadyPreviewOpened': string;
  'project.previewCommentSaveFailed': string;
  'project.previewCommentSaveFailedTitle': string;
  'project.previewCommentSaveFailedDescription': string;
  'project.previewCommentReorderFailed': string;
  'project.metaFreeform': string;
  'project.typePrototype': string;
  'project.typeDeck': string;
  'project.typeTemplate': string;
  'project.typeImage': string;
  'project.typeVideo': string;
  'project.typeAudio': string;
  'project.customInstructions': string;
  'project.customInstructionsPlaceholder': string;
  'project.instructionsActive': string;
  'project.missing': string;
  'project.resizeChatPanel': string;
  'chat.tabChat': string;
  'chat.amrCard.switchTitle': string;
  'chat.amrCard.switchBody': string;
  'chat.amrCard.chipOfficial': string;
  'chat.amrCard.chipNoKey': string;
  'chat.amrCard.chipAutoRetry': string;
  'chat.amrCard.switchCta': string;
  'chat.amrCard.switchedResend': string;
  'chat.amrError.authMessage': string;
  'chat.amrError.balanceMessage': string;
  'chat.amrError.authorizeCta': string;
  'chat.amrError.rechargeCta': string;
  'chat.amrBalanceGate.title': string;
  'chat.amrBalanceGate.message': string;
  'chat.amrBalanceGate.benefitsTitle': string;
  'chat.amrBalanceGate.benefit1': string;
  'chat.amrBalanceGate.benefit2': string;
  'chat.amrBalanceGate.benefit3': string;
  'chat.amrBalanceGate.benefit4': string;
  'chat.amrBalanceGate.laterCta': string;
  'chat.amrBalanceGate.plansCta': string;
  'chat.amrBalanceGate.signedOutTitle': string;
  'chat.amrBalanceGate.signedOutMessage': string;
  'chat.amrBalanceGate.signInCta': string;
  'chat.amrBalanceGate.watchingWallet': string;
  'chat.amrArtifactUpgrade.title': string;
  'chat.amrArtifactUpgrade.message': string;
  'chat.amrArtifactUpgrade.benefit1': string;
  'chat.amrArtifactUpgrade.benefit2': string;
  'chat.amrArtifactUpgrade.benefit3': string;
  'chat.amrArtifactUpgrade.benefit4': string;
  'chat.amrArtifactUpgrade.promoBanner': string;
  'chat.amrArtifactUpgrade.countdownLabel': string;
  'chat.amrArtifactUpgrade.plansCta': string;
  'chat.amrArtifactUpgrade.homePlansCta': string;
  'chat.amrArtifactUpgrade.laterCta': string;
  'chat.amrArtifactUpgrade.homeTitle': string;
  'chat.amrArtifactUpgrade.homeMessage': string;
  'chat.amrArtifactUpgrade.homeArtifactCta': string;
  'chat.antigravityError.launchTerminalCta': string;
  'chat.antigravityError.launchSwitchModelCta': string;
  'chat.connectionDropped': string;
  'chat.runError.title.authRequired': string;
  'chat.runError.title.balance': string;
  'chat.runError.title.connectionDropped': string;
  /**
   * S02 · 本地 agent 没登录 / 登录过期。`{agent}` 由报错卡在渲染时填 ——
   * 「哪一个 agent 没登录」是这句话的全部信息量,主语不能省。
   */
  'chat.runError.title.signInRequired.other': string;
  /** S04 · Open Design 智能体没登录 / 授权过期。主语固定,没有插值槽。 */
  'chat.runError.title.signInRequired.amr': string;
  'chat.runError.title.rateLimited': string;
  'chat.runError.title.modelWindowLimit': string;
  'chat.runError.title.membershipConcurrencyLimit': string;
  'chat.runError.title.cliMissing': string;
  'chat.runError.title.promptTooLarge': string;
  'chat.runError.title.modelUnavailable': string;
  /**
   * S13 ·「模型能力不支持」。和 `title.modelUnavailable`(S07「当前模型不可用」)
   * 是产品文档里的两行,不是一句话的两种说法 —— 一个说「用不了」,一个说「做不了」。
   */
  'chat.runError.title.modelCapabilityUnsupported': string;
  'chat.runError.title.upstreamUnavailable': string;
  'chat.runError.title.toolLoop': string;
  'chat.runError.title.outputInvalid': string;
  'chat.runError.title.runtimeConfig': string;
  /**
   * S05 · 自带 API key 没配好(daemon `failure_detail: invalid_api_key`)。
   *
   * 主语固定,没有插值槽 —— 说的是「你填的那把 key」,不是「哪一个 agent」,
   * 所以它和 S02 的 `title.signInRequired.other` 不是同一句话:那边要点名是哪个
   * 本地 agent 还没登录,这边说的是 key 本身填错了。
   *
   * 只给 BYOK / API 提供商那一档用(判据 `byokApiKeyIsEditableInSettings`,
   * `utils/byokProvider.ts`)。本机 CLI 报同一条 detail 时留在 S02 —— 它们的登录
   * 在用户自己的终端里,详见 `runtime/amr-guidance.ts` 的 `apiKeyInvalidCardFor`。
   */
  'chat.runError.title.apiKeyInvalid': string;
  'chat.runError.title.quotaExhausted': string;
  'chat.runError.title.timedOut': string;
  'chat.runError.title.emptyOutput': string;
  'chat.runError.title.sessionExpired': string;
  'chat.runError.title.gitBashMissing': string;
  'chat.runError.title.cpuUnsupported': string;
  'chat.runError.title.cliSessionRefused': string;
  'chat.runError.title.strategyTaskHalted': string;
  'chat.runError.title.agentReplyIncomplete': string;
  'chat.runError.title.noDeliverable': string;
  'chat.runError.agentReplyIncompleteMessage': string;
  'chat.runError.noDeliverableMessage': string;
  'chat.runError.title.clarificationRepeated': string;
  'chat.runError.clarificationRepeatedMessage': string;
  'chat.runError.title.regionNotSupported': string;
  'chat.runError.title.clientEnvironment': string;
  'chat.runError.title.certificateFailure': string;
  'chat.runError.certificateFailureMessage': string;
  'chat.runError.title.proxyConfiguration': string;
  'chat.runError.proxyConfigurationMessage': string;
  'chat.runError.title.networkConfiguration': string;
  'chat.runError.networkConfigurationMessage': string;
  'chat.runError.title.hostPolicyBlock': string;
  'chat.runError.hostPolicyBlockMessage': string;
  'chat.runError.title.localStorageFailure': string;
  'chat.runError.localStorageFailureMessage': string;
  'chat.runError.title.tierUpgradeRequired': string;
  'chat.runError.tierUpgradeRequiredMessage': string;
  'chat.runError.title.generic': string;
  'chat.runError.title.artifactMissing': string;
  'chat.runError.signInMessage.amr': string;
  'chat.runError.signInMessage.other': string;
  'chat.runError.cliMissingMessage': string;
  'chat.runError.promptTooLargeMessage': string;
  'chat.runError.modelUnavailableMessage': string;
  'chat.runError.modelCapabilityUnsupportedMessage': string;
  /** S23 · 以前这一格没有正文,报错卡因此落到兜底句上。 */
  'chat.runError.artifactMissingMessage': string;
  'chat.runError.rateLimitedMessage': string;
  'chat.runError.modelWindowLimitMessage': string;
  'chat.runError.modelWindowLimitMessageNoTime': string;
  'chat.runError.membershipConcurrencyLimitMessage': string;
  'chat.runError.membershipConcurrencyLimitMessageNoTime': string;
  'chat.runError.upstreamUnavailableMessage': string;
  'chat.runError.toolLoopMessage': string;
  'chat.runError.outputInvalidMessage': string;
  'chat.runError.runtimeConfigMessage': string;
  /** S05 的正文。同样没有插值槽。 */
  'chat.runError.apiKeyInvalidMessage': string;
  'chat.runError.quotaExhaustedMessage': string;
  'chat.runError.workspaceCreditsMessage': string;
  'chat.runError.timedOutMessage': string;
  'chat.runError.inactivityTimeoutMessage': string;
  'chat.runError.emptyOutputMessage': string;
  'chat.runError.sessionExpiredMessage': string;
  'chat.runError.gitBashMissingMessage': string;
  'chat.runError.cpuUnsupportedMessage': string;
  'chat.runError.title.agentCrashed': string;
  'chat.runError.agentCrashedMessage': string;
  'chat.runError.title.accountSuspended': string;
  'chat.runError.accountSuspendedMessage': string;
  'chat.runError.fallbackMessage': string;
  'chat.runError.cliSessionRefusedMessage': string;
  'chat.runError.strategyTaskStateMismatchMessage': string;
  'chat.runError.regionNotSupportedMessage': string;
  'chat.runError.clientEnvironmentMessage': string;
  'chat.runError.clientEnvironmentCause.certificate': string;
  'chat.runError.clientEnvironmentCause.proxy': string;
  'chat.runError.clientEnvironmentCause.network': string;
  'chat.runError.clientEnvironmentCause.hostPolicy': string;
  'chat.runError.clientEnvironmentCause.localStorage': string;
  'chat.runError.agentFallback': string;
  'chat.runError.sourceLabel': string;
  'chat.runError.sourceExpandAria': string;
  'chat.runError.sourceCollapseAria': string;
  'chat.tabComments': string;
  'chat.commentsSoon': string;
  'chat.comments.attached': string;
  'chat.comments.emptyAttached': string;
  'chat.comments.saved': string;
  'chat.comments.emptySaved': string;
  'chat.comments.add': string;
  'chat.comments.addAll': string;
  'chat.comments.remove': string;
  'chat.comments.placeholder': string;
  'chat.comments.addSend': string;
  'chat.comments.updateSend': string;
  'chat.comments.removeAttachment': string;
  'chat.comments.removeAttachmentAria': string;
  'chat.comments.comment': string;
  'chat.comments.sendToChat': string;
  'chat.comments.viewAll': string;
  'chat.comments.sending': string;
  'chat.comments.edit': string;
  'chat.comments.select': string;
  'chat.comments.selectAll': string;
  'chat.comments.deselect': string;
  'chat.comments.nSelected': string;
  'chat.comments.pin': string;
  'chat.comments.addNote': string;
  'chat.comments.savedToast': string;
  'chat.comments.pinSavedToast': string;
  'chat.comments.pinAtCoords': string;
  'chat.comments.capturedItems': string;
  'chat.comments.clear': string;
  'chat.comments.targetImage': string;
  'chat.comments.targetControl': string;
  'chat.comments.targetLink': string;
  'chat.comments.targetText': string;
  'chat.comments.targetSection': string;
  'chat.comments.targetPage': string;
  'chat.comments.targetArea': string;
  'chat.annotationNotePlaceholder': string;
  'chat.annotationAttachImage': string;
  'chat.annotationAttachedImages': string;
  'chat.annotationAttachedRemove': string;
  'chat.annotationAddToInput': string;
  'chat.annotationAddingToInput': string;
  'chat.annotationQueue': string;
  'chat.annotationQueueing': string;
  'chat.annotationSubmitOptions': string;
  'chat.annotationSending': string;
  'chat.annotationSendDisabledReason': string;
  'chat.annotationPreviewMissing': string;
  'chat.annotationPreviewMissingInk': string;
  'chat.annotationSentWithoutScreenshot': string;
  'chat.annotationTimeout': string;
  'chat.annotationFailed': string;
  'chat.annotationProjectCreateFailed': string;
  'chat.annotationUploadFailed': string;
  'chat.attachmentFolderUnsupported': string;
  'chat.inspect.noEditableTargets': string;
  'chat.inspect.noCommentTargets': string;
  'chat.inspect.editHint': string;
  'chat.inspect.commentHint': string;
  'chat.conversationsTitle': string;
  'chat.conversationsAria': string;
  'chat.newConversation': string;
  /**
   * 面板头图标键的名字,也是**聊天面板内唯一**的「新会话」说法。稿子 `729fa43ce7` 的
   * `docs/design/chat-panel/src/body-scene.html:8`:`aria-label="新会话" data-tip="新会话"`。
   *
   * 产品裁决 2026-09-03:**只统一聊天面板内**。面板内原来三种说法收成一句 ——
   * 这一条、回合动作行的 `assistant.forkConversation`、以及历史下拉里那颗
   * 「新建」(`chat.new`,连按钮一起删了)。面板**外**的 `chat.newConversation`
   * (「新建对话」)不动。
   *
   * ⚠️ 稿子自己这两处不一致:面板头写「新会话」,回合动作行写「新开会话」
   * (`src/body-components.html:1189`)。取「新会话」的依据也在稿子里 ——
   * `src/body-components.html:1243` 那条 fork 分界线是 `aria-label="新会话从这里开始"`,
   * 稿子自己把 Fork 产出的东西叫「新会话」。于是词族定成 名词「新会话」+ 动词「开始」。
   * 非中文语种沿用各自既有的「新建对话」措辞,不引入新词。
   */
  'chat.newSession': string;
  'chat.newConversationsTitle': string;
  'chat.conversationsHeading': string;
  'chat.emptyConversations': string;
  'chat.conversationsSearchPlaceholder': string;
  'chat.conversationsNoMatches': string;
  'chat.deleteConversation': string;
  'chat.renameConversationLabel': string;
  'chat.deleteConversationConfirm': string;
  'chat.untitledConversation': string;
  /**
   * 回合动作行那颗按钮失败时的提示。key 名还带 `fork` 是历史,**文案不带** ——
   * 它和 `assistant.forkConversation` / `assistant.forkingConversation` 是同一颗
   * 按钮的三个态,过去 hover 说「新开会话」、按下去变「正在分叉…」、失败说
   * 「无法分叉这个对话」,一颗按钮两套词。产品裁决 2026-09-03 统一成
   * 名词「新会话」+ 动词「开始」那一族。
   */
  'chat.forkConversationFailed': string;
  'chat.startTitle': string;
  'chat.startHint': string;
  'chat.brandBrowserAssistDesktopOnly': string;
  'chat.brandBrowserAssistReadFailed': string;
  'chat.memoryWrittenSummary': string;
  'chat.brandBrowserAssistMessage': string;
  'chat.brandBrowserLocalSnapshotMissing': string;
  'chat.brandBrowserLocalSnapshotReadFailed': string;
  'chat.brandBrowserSnapshotSaveFailed': string;
  'chat.brandBrowserAssistDownloadGuideTitle': string;
  'chat.brandBrowserAssistDownloadGuideDetails': string;
  'brandEnrichment.title': string;
  'brandEnrichment.body': string;
  'brandEnrichment.cta': string;
  'brandEnrichment.busy': string;
  'brandExtractionTranscript.sourceDesignMd': string;
  'brandExtractionTranscript.user': string;
  'brandExtractionTranscript.doneTitle': string;
  'brandExtractionTranscript.doneBody': string;
  'brandExtractionTranscript.next': string;
  'chat.fillInputTitle': string;
  'chat.jumpToLatest': string;
  'chat.scrollToLatest': string;
  'chat.messageRail.aria': string;
  'chat.messageRail.jumpAria': string;
  'chat.messageRail.empty': string;
  'chat.you': string;
  'chat.record.retryImage': string;
  'chat.quote.add': string;
  'chat.quote.count': string;
  /** 重复添加同一段文案时的轻提示(OPEND-2546) */
  'chat.quote.duplicate': string;
  'chat.quote.removeAria': string;
  'chat.support.title': string;
  'chat.support.join': string;
  'chat.sendFailedRetryAria': string;
  'chat.audio.play': string;
  'chat.audio.pause': string;
  'chat.audio.download': string;
  'chat.upgrade.balance': string;
  'chat.upgrade.whyLow': string;
  'chat.upgrade.whyOut': string;
  'chat.openFile': string;
  'chat.copyPrompt': string;
  'chat.copyErrorDiagnostic': string;
  'chat.copyDone': string;
  'chat.composerPlaceholder': string;
  'chat.activeFileEditingLabel': string;
  'chat.activeFilePlaceholder': string;
  'chat.designArtifactsShowMore': string;
  'chat.mode.chat.label': string;
  'chat.mode.chat.title': string;
  'chat.mode.chat.summary': string;
  'chat.mode.chat.solves': string;
  'chat.mode.chat.query1': string;
  'chat.mode.chat.query2': string;
  'chat.mode.chat.query3': string;
  'chat.mode.plan.label': string;
  'chat.mode.plan.title': string;
  'chat.mode.plan.summary': string;
  'chat.mode.plan.solves': string;
  'chat.mode.plan.query1': string;
  'chat.mode.plan.query2': string;
  'chat.mode.plan.query3': string;
  'chat.mode.design.label': string;
  'chat.mode.design.title': string;
  'chat.mode.design.summary': string;
  'chat.mode.design.solves': string;
  'chat.mode.design.query1': string;
  'chat.mode.design.query2': string;
  'chat.mode.design.query3': string;
  'chat.mode.cardBestFor': string;
  'chat.mode.cardTry': string;
  'chat.mode.cardCost': string;
  'chat.mode.chat.cost': string;
  'chat.mode.chat.costNote': string;
  'chat.mode.plan.cost': string;
  'chat.mode.plan.costNote': string;
  'chat.mode.design.cost': string;
  'chat.mode.design.costNote': string;
  // Composer mode picker (#5517): trigger/clear aria copy. Mode names and
  // descriptions reuse `chat.mode.*.label` / `chat.mode.*.summary`.
  'chat.modePicker.choose': string;
  'chat.modePicker.current': string;
  'chat.modePicker.clear': string;
  'chat.modePicker.mode': string;
  'chat.composerHint': string;
  'chat.cliSettingsTitle': string;
  'chat.cliSettingsAria': string;
  'chat.attachTitle': string;
  'chat.addContextTitle': string;
  'chat.attachAria': string;
  'chat.importTitle': string;
  'chat.importLabel': string;
  'chat.importComingSoon': string;
  'chat.importSoon': string;
  'chat.importFig': string;
  'chat.importGitHub': string;
  'chat.importWeb': string;
  'chat.importFolder': string;
  'chat.importSkills': string;
  'chat.importProject': string;
  'chat.importDesignSystemHeader': string;
  'chat.importDesignSystemBack': string;
  'chat.importDesignSystemSearch': string;
  'chat.importDesignSystemEmpty': string;
  'chat.importDesignSystemNone': string;
  'chat.importDesignSystemNoneSub': string;
  'chat.importDesignSystemActive': string;
  'chat.importDesignSystemSwitched': string;
  'chat.importDesignSystemFailed': string;
  'chat.importDesignSystemLoadFailed': string;
  'browserUse.title': string;
  'browserUse.summary': string;
  'browserUse.searchAria': string;
  'browserUse.searchPlaceholder': string;
  'browserUse.empty': string;
  'browserUse.unavailable': string;
  'browserUse.added': string;
  // Design Browser
  'designBrowser.aria': string;
  'designBrowser.goBack': string;
  'designBrowser.goForward': string;
  'designBrowser.reload': string;
  'designBrowser.stopLoading': string;
  'designBrowser.viewportAria': string;
  'designBrowser.addressPlaceholder': string;
  'designBrowser.addressAria': string;
  'designBrowser.suggestion.reference': string;
  'designBrowser.suggestion.history': string;
  'designBrowser.menu.copyScreenshot': string;
  'designBrowser.menu.hardReload': string;
  'designBrowser.menu.copyUrl': string;
  'designBrowser.menu.openInBrowser': string;
  'designBrowser.menu.savePageBrief': string;
  'designBrowser.menu.clearBrowsingHistory': string;
  'designBrowser.menu.clearCookies': string;
  'designBrowser.menu.clearAllData': string;
  'designBrowser.status.openHttpUrlFirst': string;
  'designBrowser.status.openPageBeforeScreenshot': string;
  'designBrowser.status.screenshotCopied': string;
  'designBrowser.status.openPageBeforeBrief': string;
  'designBrowser.status.openPageBeforeTools': string;
  'designBrowser.status.clickElementToComment': string;
  'designBrowser.status.clickElementToTune': string;
  'designBrowser.status.noElementSelected': string;
  'designBrowser.status.addBrowserComment': string;
  'designBrowser.status.tuneElementThenSaveHtml': string;
  'designBrowser.status.tuneLiveOnly': string;
  'designBrowser.status.pickerFailed': string;
  'designBrowser.status.applyStyleFailed': string;
  'designBrowser.status.editTextFailed': string;
  'designBrowser.status.onlyProjectHtmlCanSave': string;
  'designBrowser.status.htmlSaved': string;
  'designBrowser.status.htmlSaveFailed': string;
  'designBrowser.status.commentSavingUnavailable': string;
  'designBrowser.status.commentSaved': string;
  'designBrowser.status.commentSendingUnavailable': string;
  'designBrowser.comments.aria': string;
  'designBrowser.comments.openFor': string;
  'designBrowser.comment.dialog': string;
  'designBrowser.comment.elementFallback': string;
  'designBrowser.comment.close': string;
  'designBrowser.comment.noteAria': string;
  'designBrowser.comment.placeholder': string;
  'designBrowser.comment.addNote': string;
  'designBrowser.comment.saveComment': string;
  'designBrowser.comment.sending': string;
  'designBrowser.comment.sendToChat': string;
  'designBrowser.inspect.editTitle': string;
  'designBrowser.inspect.tuneTitle': string;
  'designBrowser.inspect.closeTune': string;
  'designBrowser.inspect.colors': string;
  'designBrowser.inspect.text': string;
  'designBrowser.inspect.fill': string;
  'designBrowser.inspect.type': string;
  'designBrowser.inspect.size': string;
  'designBrowser.inspect.weight': string;
  'designBrowser.inspect.spacing': string;
  'designBrowser.inspect.pad': string;
  'designBrowser.inspect.radius': string;
  'designBrowser.inspect.content': string;
  'designBrowser.inspect.elementText': string;
  'designBrowser.inspect.saving': string;
  'designBrowser.inspect.saveHtml': string;
  'designBrowser.inspect.liveOnly': string;
  'designBrowser.reference.kicker': string;
  'designBrowser.reference.title': string;
  'designBrowser.reference.subtitle': string;
  'designBrowser.reference.categoryAria': string;
  'designBrowser.reference.searchPlaceholder': string;
  'designBrowser.reference.searchAria': string;
  'designBrowser.reference.clearSearch': string;
  'designBrowser.reference.noMatches': string;
  'designBrowser.reference.clearFilters': string;
  'designBrowser.reference.open': string;
  'designBrowser.reference.group.inspiration': string;
  'designBrowser.reference.group.interfaces': string;
  'designBrowser.reference.group.motion': string;
  'designBrowser.reference.group.color': string;
  'designBrowser.reference.group.type': string;
  'designBrowser.reference.group.icons': string;
  'designBrowser.reference.group.illustration': string;
  'designBrowser.reference.group.photography': string;
  'designBrowser.reference.group.3d': string;
  'designBrowser.reference.group.mockups': string;
  'designBrowser.reference.group.systems': string;
  'designBrowser.reference.group.components': string;
  'designBrowser.reference.group.guidelines': string;
  'designBrowser.reference.group.tools': string;
  'designBrowser.reference.site.dribbble.detail': string;
  'designBrowser.reference.site.behance.detail': string;
  'designBrowser.reference.site.awwwards.detail': string;
  'designBrowser.reference.site.godly.detail': string;
  'designBrowser.reference.site.land_book.detail': string;
  'designBrowser.reference.site.mobbin.detail': string;
  'designBrowser.reference.site.screenlane.detail': string;
  'designBrowser.reference.site.pageflows.detail': string;
  'designBrowser.reference.site.uisources.detail': string;
  'designBrowser.reference.site.collectui.detail': string;
  'designBrowser.reference.site.gsap.detail': string;
  'designBrowser.reference.site.animations.detail': string;
  'designBrowser.reference.site.transitions.detail': string;
  'designBrowser.reference.site.motionsites.detail': string;
  'designBrowser.reference.site.motion.detail': string;
  'designBrowser.reference.site.animography.detail': string;
  'designBrowser.reference.site.reactbits.detail': string;
  'designBrowser.reference.site.coolors.detail': string;
  'designBrowser.reference.site.colorhunt.detail': string;
  'designBrowser.reference.site.realtimecolors.detail': string;
  'designBrowser.reference.site.color_adobe.detail': string;
  'designBrowser.reference.site.happyhues.detail': string;
  'designBrowser.reference.site.fonts_google.detail': string;
  'designBrowser.reference.site.fontshare.detail': string;
  'designBrowser.reference.site.typewolf.detail': string;
  'designBrowser.reference.site.fontpair.detail': string;
  'designBrowser.reference.site.fontsinuse.detail': string;
  'designBrowser.reference.site.thesvg.detail': string;
  'designBrowser.reference.site.svglogos.detail': string;
  'designBrowser.reference.site.icons_lobehub.detail': string;
  'designBrowser.reference.site.icon_sets_iconify.detail': string;
  'designBrowser.reference.site.lucide.detail': string;
  'designBrowser.reference.site.heroicons.detail': string;
  'designBrowser.reference.site.svgrepo.detail': string;
  'designBrowser.reference.site.storyset.detail': string;
  'designBrowser.reference.site.undraw.detail': string;
  'designBrowser.reference.site.blush.detail': string;
  'designBrowser.reference.site.lummi.detail': string;
  'designBrowser.reference.site.whirrls.detail': string;
  'designBrowser.reference.site.worldindots.detail': string;
  'designBrowser.reference.site.unsplash.detail': string;
  'designBrowser.reference.site.pexels.detail': string;
  'designBrowser.reference.site.pixabay.detail': string;
  'designBrowser.reference.site.cosmos.detail': string;
  'designBrowser.reference.site.spline.detail': string;
  'designBrowser.reference.site.threejs.detail': string;
  'designBrowser.reference.site.womp.detail': string;
  'designBrowser.reference.site.pixcap.detail': string;
  'designBrowser.reference.site.shots.detail': string;
  'designBrowser.reference.site.mockuuups.detail': string;
  'designBrowser.reference.site.angle.detail': string;
  'designBrowser.reference.site.rotato.detail': string;
  'designBrowser.reference.site.impeccable.detail': string;
  'designBrowser.reference.site.styles_refero.detail': string;
  'designBrowser.reference.site.brandfetch.detail': string;
  'designBrowser.reference.site.designsystemsrepo.detail': string;
  'designBrowser.reference.site.startups.detail': string;
  'designBrowser.reference.site.base_ui.detail': string;
  'designBrowser.reference.site.ui_shadcn.detail': string;
  'designBrowser.reference.site.heroui.detail': string;
  'designBrowser.reference.site.radix_ui.detail': string;
  'designBrowser.reference.site.react_spectrum_adobe.detail': string;
  'designBrowser.reference.site.headlessui.detail': string;
  'designBrowser.reference.site.mui.detail': string;
  'designBrowser.reference.site.mantine.detail': string;
  'designBrowser.reference.site.chakra_ui.detail': string;
  'designBrowser.reference.site.ant.detail': string;
  'designBrowser.reference.site.ark_ui.detail': string;
  'designBrowser.reference.site.daisyui.detail': string;
  'designBrowser.reference.site.developer_apple.detail': string;
  'designBrowser.reference.site.m3_material.detail': string;
  'designBrowser.reference.site.lawsofux.detail': string;
  'designBrowser.reference.site.webaim.detail': string;
  'designBrowser.reference.site.a11yproject.detail': string;
  'designBrowser.reference.site.toolfolio.detail': string;
  'designBrowser.reference.site.getdesign.detail': string;
  'designBrowser.reference.site.tasteskill.detail': string;
  'designBrowser.reference.site.uigoodies.detail': string;
  'designBrowser.reference.site.sidebar.detail': string;
  'designBrowser.reference.site.github.detail': string;
  'browserUse.category.assets': string;
  'browserUse.category.tokens': string;
  'browserUse.category.motion': string;
  'browserUse.category.visual': string;
  'browserUse.category.structure': string;
  'browserUse.category.project': string;
  'browserUse.category.general': string;
  'browserUse.input.none': string;
  'browserUse.input.assetKind': string;
  'browserUse.input.optionalSelector': string;
  'browserUse.input.requirementSelector': string;
  'browserUse.input.selectorOptional': string;
  'browserUse.input.scaleOne': string;
  'browserUse.input.selectorScaleTwo': string;
  'browserUse.input.gridOverlay': string;
  'browserUse.input.bodySelector': string;
  'browserUse.input.navigate': string;
  'browserUse.input.selector': string;
  'browserUse.input.selectorText': string;
  'browserUse.input.scroll': string;
  'browserUse.input.terminalRun': string;
  'browserUse.input.command': string;
  'browserUse.input.maxChars': string;
  'browserUse.input.custom': string;
  'browserUse.action.extract_logo.output': string;
  'browserUse.action.list_images.output': string;
  'browserUse.action.download_assets.output': string;
  'browserUse.action.extract_svgs.output': string;
  'browserUse.action.optimize_svgs.output': string;
  'browserUse.action.extract_colors.output': string;
  'browserUse.action.extract_fonts.output': string;
  'browserUse.action.extract_design_tokens.output': string;
  'browserUse.action.extract_type_scale.output': string;
  'browserUse.action.extract_buttons.output': string;
  'browserUse.action.extract_grid_system.output': string;
  'browserUse.action.extract_breakpoints.output': string;
  'browserUse.action.extract_gradients.output': string;
  'browserUse.action.extract_shadows.output': string;
  'browserUse.action.extract_easings.output': string;
  'browserUse.action.export_tokens.output': string;
  'browserUse.action.extract_animations.output': string;
  'browserUse.action.validate_view.output': string;
  'browserUse.action.audit_layout.output': string;
  'browserUse.action.audit_accessibility.output': string;
  'browserUse.action.responsive_screenshots.output': string;
  'browserUse.action.screenshot_full.output': string;
  'browserUse.action.screenshot_element.output': string;
  'browserUse.action.screenshot_with_grid.output': string;
  'browserUse.action.screenshot_dark_mode.output': string;
  'browserUse.action.generate_styleguide.output': string;
  'browserUse.action.extract_html.output': string;
  'browserUse.action.extract_component_inventory.output': string;
  'browserUse.action.extract_copy.output': string;
  'browserUse.action.extract_nav.output': string;
  'browserUse.action.extract_forms.output': string;
  'browserUse.action.run_project.output': string;
  'browserUse.action.detect_project.output': string;
  'browserUse.action.page_info.output': string;
  'browserUse.action.snapshot.output': string;
  'browserUse.action.navigate.output': string;
  'browserUse.action.click.output': string;
  'browserUse.action.type_text.output': string;
  'browserUse.action.scroll.output': string;
  'browserUse.action.extract_og_metadata.output': string;
  'browserUse.action.terminal_run.output': string;
  'browserUse.action.terminal_run_background.output': string;
  'browserUse.action.terminal_read.output': string;
  "chat.designToolbox.title": string;
  "chat.designToolbox.tooltip": string;
  "chat.designToolbox.aria": string;
  "chat.designToolbox.searchPlaceholder": string;
  "chat.designToolbox.searchAria": string;
  "chat.designToolbox.followupSection": string;
  "chat.designToolbox.resourcesSection": string;
  "chat.designToolbox.selected": string;
  "chat.designToolbox.noResources": string;
  "chat.designToolbox.badge.video": string;
  "chat.designToolbox.badge.image": string;
  "chat.designToolbox.badge.motion": string;
  "chat.designToolbox.badge.polish": string;
  "chat.designToolbox.action.auto-match.title": string;
  "chat.designToolbox.action.auto-match.badge": string;
  "chat.designToolbox.action.auto-match.description": string;
  "chat.designToolbox.action.asset-search.title": string;
  "chat.designToolbox.action.asset-search.badge": string;
  "chat.designToolbox.action.asset-search.description": string;
  "chat.designToolbox.action.icon-workflow.title": string;
  "chat.designToolbox.action.icon-workflow.badge": string;
  "chat.designToolbox.action.icon-workflow.description": string;
  "chat.designToolbox.action.image-replace.title": string;
  "chat.designToolbox.action.image-replace.badge": string;
  "chat.designToolbox.action.image-replace.description": string;
  "chat.designToolbox.action.reference-extract.title": string;
  "chat.designToolbox.action.reference-extract.badge": string;
  "chat.designToolbox.action.reference-extract.description": string;
  "chat.designToolbox.action.motion.title": string;
  "chat.designToolbox.action.motion.badge": string;
  "chat.designToolbox.action.motion.description": string;
  "chat.designToolbox.action.motion-polish.title": string;
  "chat.designToolbox.action.motion-polish.badge": string;
  "chat.designToolbox.action.motion-polish.description": string;
  "chat.designToolbox.action.transition-motion.title": string;
  "chat.designToolbox.action.transition-motion.badge": string;
  "chat.designToolbox.action.transition-motion.description": string;
  "chat.designToolbox.action.threejs-scene.title": string;
  "chat.designToolbox.action.threejs-scene.badge": string;
  "chat.designToolbox.action.threejs-scene.description": string;
  "chat.designToolbox.action.anti-ai-polish.title": string;
  "chat.designToolbox.action.anti-ai-polish.badge": string;
  "chat.designToolbox.action.anti-ai-polish.description": string;
  "chat.designToolbox.action.visual-polish.title": string;
  "chat.designToolbox.action.visual-polish.badge": string;
  "chat.designToolbox.action.visual-polish.description": string;
  "chat.designToolbox.action.image-gen.title": string;
  "chat.designToolbox.action.image-gen.badge": string;
  "chat.designToolbox.action.image-gen.description": string;
  "chat.designToolbox.action.chart-gen.title": string;
  "chat.designToolbox.action.chart-gen.badge": string;
  "chat.designToolbox.action.chart-gen.description": string;
  "chat.designToolbox.action.logo-gen.title": string;
  "chat.designToolbox.action.logo-gen.badge": string;
  "chat.designToolbox.action.logo-gen.description": string;
  "chat.designToolbox.action.plan-outline.title": string;
  "chat.designToolbox.action.plan-outline.badge": string;
  "chat.designToolbox.action.plan-outline.description": string;
  "chat.designToolbox.action.video-gen.title": string;
  "chat.designToolbox.action.video-gen.badge": string;
  "chat.designToolbox.action.video-gen.description": string;
  "chat.designToolbox.kind.skill": string;
  "chat.designToolbox.kind.plugin": string;
  "chat.designToolbox.kind.mcp": string;
  "chat.designToolbox.kind.mcpTemplate": string;
  "chat.designToolbox.kind.connector": string;
  "chat.designToolbox.kind.designFile": string;
  "chat.designToolbox.context.browser": string;
  "chat.designToolbox.context.designFiles": string;
  "chat.designToolbox.context.designSystem": string;
  "chat.designToolbox.context.folder": string;
  "chat.designToolbox.context.terminal": string;
  "chat.designToolbox.context.sideChat": string;
  "chat.designToolbox.context.liveArtifact": string;
  "chat.designToolbox.context.file": string;
  "chat.designToolbox.prompt.contextGeneric": string;
  "chat.designToolbox.prompt.contextSpecific": string;
  "chat.designToolbox.prompt.preserveDraft": string;
  "chat.designToolbox.prompt.selectedSkill": string;
  "chat.designToolbox.prompt.noSkill": string;
  "chat.designToolbox.prompt.autoMatchIntro": string;
  "chat.designToolbox.prompt.autoMatchStep1": string;
  "chat.designToolbox.prompt.autoMatchStep2": string;
  "chat.designToolbox.prompt.autoMatchStep3": string;
  "chat.designToolbox.prompt.autoMatchStep4": string;
  "chat.designToolbox.prompt.assetSearch": string;
  "chat.designToolbox.prompt.iconWorkflow": string;
  "chat.designToolbox.prompt.imageReplace": string;
  "chat.designToolbox.prompt.referenceExtract": string;
  "chat.designToolbox.prompt.motion": string;
  "chat.designToolbox.prompt.motionPolish": string;
  "chat.designToolbox.prompt.transitionMotion": string;
  "chat.designToolbox.prompt.planOutline": string;
  "chat.designToolbox.prompt.threejsScene": string;
  "chat.designToolbox.prompt.antiAiPolish": string;
  "chat.designToolbox.prompt.visualPolish": string;
  "chat.designToolbox.prompt.imageGen": string;
  "chat.designToolbox.prompt.chartGen": string;
  "chat.designToolbox.prompt.logoGen": string;
  "chat.designToolbox.prompt.videoGen": string;
  "chat.designToolbox.prompt.useSkill": string;
  "chat.designToolbox.prompt.skillInstruction": string;
  "chat.designToolbox.prompt.selectedResource": string;
  "chat.designToolbox.prompt.resourceDescription": string;
  "chat.designToolbox.prompt.pluginResource": string;
  "chat.designToolbox.prompt.mcpResource": string;
  "chat.designToolbox.prompt.mcpTemplateResource": string;
  "chat.designToolbox.prompt.connectorResource": string;
  "chat.designToolbox.prompt.fileResource": string;
  "chat.designToolbox.prompt.resourceIndex": string;
  "chat.designToolbox.prompt.searchableSkills": string;
  "chat.designToolbox.prompt.searchablePlugins": string;
  "chat.designToolbox.prompt.availableMcp": string;
  "chat.designToolbox.prompt.connectedConnectors": string;
  "chat.designToolbox.prompt.referenceDesignFiles": string;
  "chat.designToolbox.prompt.mcpTemplateName": string;
  "chat.designToolbox.prompt.processRule": string;
  "chat.designToolbox.prompt.moreSuffix": string;
  "chat.designToolbox.prompt.compactLine": string;
  'chat.mentionTabsAria': string;
  'chat.mentionTabAll': string;
  'chat.mentionTabTabs': string;
  'chat.mentionTabPlugins': string;
  'chat.mentionTabSkills': string;
  'chat.mentionTabMcp': string;
  'chat.mentionTabConnectors': string;
  'chat.mentionTabFiles': string;
  'chat.mentionNoResults': string;
  'chat.mentionSearchPrompt': string;
  'chat.mentionSectionTabs': string;
  'chat.mentionSectionPlugins': string;
  'chat.mentionSectionSkills': string;
  'chat.mentionSectionMcp': string;
  'chat.mentionSectionConnectors': string;
  'chat.mentionSectionFiles': string;
  'chat.mentionActiveSkill': string;
  'chat.mentionUseMcpTitle': string;
  'chat.mentionUseConnectorTitle': string;
  'chat.mentionPluginOfficial': string;
  'chat.mentionPluginMine': string;
  'chat.linkedFolderRemoveAria': string;
  'chat.linkedFolderNotFound': string;
  'chat.linkedFolderAlready': string;
  'chat.linkedFolderPickError': string;
  'chat.queuedEditQueuedTaskAria': string;
  'chat.queuedSave': string;
  'chat.queuedCancel': string;
  'chat.queuedReorder': string;
  'chat.queuedEdit': string;
  'chat.queuedHeader': string;
  /** B11: visible label on the queue row's third button. */
  'chat.queuedSteer': string;
  /**
   * B11 hover copy: pressing that button interrupts the run in progress and
   * sends this queued row straight away (OPEND-2602). Names the button first so
   * the accessible name still contains the visible label.
   */
  'chat.queuedSteerInterrupts': string;
  /** Why steering is unavailable: the current agent's CLI stops reading input mid-turn. */
  'chat.queuedSteerUnsupported': string;
  /** Steering was refused because the turn already stopped reading input. */
  'chat.queuedSteerClosed': string;
  /** Steering failed for any other reason (network / daemon). */
  'chat.queuedSteerFailed': string;
  /** This queued item carries attachments, which a mid-turn message cannot deliver. */
  'chat.queuedSteerTextOnly': string;
  'chat.queuedFollowUpFallback': string;
  'chat.send': string;
  'chat.stop': string;
  'chat.removeAria': string;
  'chat.example1Title': string;
  'chat.example1Tag': string;
  'chat.example1Prompt': string;
  'chat.example2Title': string;
  'chat.example2Tag': string;
  'chat.example2Prompt': string;
  'chat.example3Title': string;
  'chat.example3Tag': string;
  'chat.example3Prompt': string;

  // Home personalized recommendation (first-run onboarding starting point)
  'home.recommendation.eyebrow': string;
  'home.recommendation.primaryCta': string;
  'home.recommendation.change': string;
  'home.recommendation.browseAll': string;
  'home.recommendation.defaultProjectName': string;
  'home.recommendation.startFailed': string;
  'studio.firstArtifactHint.title': string;
  'studio.firstArtifactHint.body': string;
  'studio.firstArtifactHint.dismiss': string;
  'home.starter.product_ui_prototype.title': string;
  'home.starter.product_ui_prototype.desc': string;
  'home.starter.product_ui_prototype.firstPrompt': string;
  'home.starter.product_ui_component.title': string;
  'home.starter.product_ui_component.desc': string;
  'home.starter.product_ui_component.firstPrompt': string;
  'home.starter.product_ui_lowfi.title': string;
  'home.starter.product_ui_lowfi.desc': string;
  'home.starter.product_ui_lowfi.firstPrompt': string;
  'home.starter.marketing_landing.title': string;
  'home.starter.marketing_landing.desc': string;
  'home.starter.marketing_landing.firstPrompt': string;
  'home.starter.marketing_multivariant.title': string;
  'home.starter.marketing_multivariant.desc': string;
  'home.starter.marketing_multivariant.firstPrompt': string;
  'home.starter.internal_dashboard.title': string;
  'home.starter.internal_dashboard.desc': string;
  'home.starter.internal_dashboard.firstPrompt': string;
  'home.starter.internal_report.title': string;
  'home.starter.internal_report.desc': string;
  'home.starter.internal_report.firstPrompt': string;
  'home.starter.general_menu.title': string;
  'home.starter.general_menu.desc': string;
  'home.starter.general_menu.firstPrompt': string;
  'chat.example4Title': string;
  'chat.example4Tag': string;
  'chat.example4Prompt': string;

  // Preview modal
  'preview.usePlugin': string;
  'preview.usePluginOnly': string;
  'preview.usePluginOnlyDesc': string;
  'preview.replicateContent': string;
  'preview.replicateContentDesc': string;
  'preview.duplicateTemplate': string;
  'preview.duplicateTemplateDesc': string;
  'preview.shareMenu': string;
  'preview.exportMenu': string;
  'preview.shareTemplateBadge': string;
  'preview.shareToX': string;
  'preview.shareToReddit': string;
  'preview.shareToFacebook': string;
  'preview.shareToLinkedIn': string;
  'preview.shareToInstagram': string;
  'preview.shareToXiaohongshu': string;
  'preview.copyTemplateLink': string;
  'preview.copyShareText': string;
  'preview.shareSocialGroup': string;
  'preview.shareCopyGroup': string;
  'preview.shareExportGroup': string;
  'preview.shareCopied': string;
  'preview.shareCopyFailed': string;
  'preview.shareTextDefault': string;
  'preview.openInNewTab': string;
  'preview.exit': string;
  'preview.fullscreen': string;
  'preview.closeTitle': string;
  'preview.loading': string;
  'preview.errorTitle': string;
  'preview.errorBody': string;
  'preview.retry': string;
  // Friendly placeholder copy for surfaces whose `od.preview.type` is
  // not `html`, or whose manifest declares a preview entry that doesn't
  // ship on disk — they have no fetchable example artifact, so the
  // loading / error states are misleading. Issues #897, #2840, #3216.
  // Body uses the `{kind}` placeholder (raw `od.preview.type` token,
  // e.g. "markdown" or "image"); both keys use the `{noun}` placeholder
  // so the same wording reads correctly on skills, plugins, and design
  // templates (filled from one of the `preview.noun.*` keys below).
  'preview.unavailableTitle': string;
  'preview.unavailableBody': string;
  // Noun variants so the unavailable placeholder reads with the right
  // word for each surface — Skills tab vs. Community/Plugins vs. deck
  // design-templates. Keep these short, capitalised by the host
  // language's conventions, and translatable in every locale.
  'preview.nounSkill': string;
  'preview.nounPlugin': string;
  'preview.nounTemplate': string;
  'preview.showSidebar': string;
  'preview.hideSidebar': string;

  // Misc fallback names
  'misc.savedTemplate': string;
  'misc.primary': string;
  'misc.designSystem': string;

  // Workspace / file viewer / design files panel
  'workspace.designFiles': string;
  'workspace.focusMode': string;
  'workspace.showChat': string;
  'workspace.closeTab': string;
  'workspace.deleteFileConfirm': string;
  'workspace.readonlyNotice': string;
  'workspace.readonlyNoticeBy': string;
  'workspace.fileSyncDownloading': string;
  'workspace.fileSyncUploading': string;
  'workspace.deleteSelectedFilesConfirm': string;
  'workspace.deleteSelectedFilesPartial': string;
  'workspace.openFromDesignFiles': string;
  'workspace.designFilesLink': string;
  'workspace.loadingSketch': string;
  'workspace.newTab': string;
  'workspace.searchFilesPlaceholder': string;
  'workspace.openTabs': string;
  'workspace.searchTabs': string;
  'workspace.noTabsFound': string;
  'workspace.openFile': string;
  'workspace.noFilesMatch': string;
  'workspace.allFiles': string;
  'workspace.createNew': string;
  'workspace.tabOpen': string;
  'workspace.sideChatContextBanner': string;
  'workspace.sideChatDefaultTitle': string;
  'workspace.newTerminal': string;
  'workspace.newTerminalDescription': string;
  'workspace.newBrowser': string;
  'workspace.newBrowserDescription': string;
  'workspace.newSketchDescription': string;
  'workspace.terminalStarting': string;
  'workspace.terminalStartingDescription': string;
  'workspace.terminalSessionEnded': string;
  'workspace.terminalReconnecting': string;
  'workspace.terminalStartFailed': string;
  'workspace.terminalRestart': string;
  'designFiles.title': string;
  'designFiles.upload': string;
  'designFiles.pasteText': string;
  'designFiles.newSketch': string;
  'designFiles.newDocument': string;
  'designFiles.newDocumentTitle': string;
  'designFiles.empty': string;
  'designFiles.buildingPreview': string;
  'designFiles.syncing': string;
  'designFiles.refresh': string;
  'designFiles.delete': string;
  'designFiles.searchPlaceholder': string;
  'designFiles.up': string;
  'designFiles.back': string;
  'designFiles.crumbs': string;
  'designFiles.rowMenu': string;
  'designFiles.openInTab': string;
  'designFiles.download': string;
  'designFiles.downloadSelected': string;
  'designFiles.deleteSelected': string;
  'designFiles.clearSelection': string;
  'designFiles.selectPage': string;
  'designFiles.selectAll': string;
  'designFiles.dropTitle': string;
  'designFiles.dropDesc': string;
  'designFiles.dropLabel': string;
  'designFiles.upload.title': string;
  'designFiles.paste.title': string;
  'designFiles.upload.label': string;
  'designFiles.paste.label': string;
  'designFiles.documentTemplate.titleFallback': string;
  'designFiles.documentTemplate.goalHeading': string;
  'designFiles.documentTemplate.goalBody': string;
  'designFiles.documentTemplate.capabilitiesHeading': string;
  'designFiles.documentTemplate.capabilityMarkdown': string;
  'designFiles.documentTemplate.capabilityAgent': string;
  'designFiles.documentTemplate.capabilityImages': string;
  'designFiles.documentTemplate.scenarioHeading': string;
  'designFiles.documentTemplate.scenario.prototype': string;
  'designFiles.documentTemplate.scenario.wireframe': string;
  'designFiles.documentTemplate.scenario.mobile': string;
  'designFiles.documentTemplate.scenario.slideDeck': string;
  'designFiles.documentTemplate.scenario.document': string;
  'designFiles.documentTemplate.scenario.image': string;
  'designFiles.documentTemplate.scenario.video': string;
  'designFiles.documentTemplate.scenario.hyperframes': string;
  'designFiles.documentTemplate.scenario.audio': string;
  'designFiles.documentTemplate.scenario.liveArtifact': string;
  'designFiles.documentTemplate.scenario.designSystem': string;
  'designFiles.documentTemplate.scenario.default': string;
  'designFiles.documentTemplate.nextHeading': string;
  'designFiles.documentTemplate.nextBody': string;

  'designFiles.projectMenu': string;
  'designFiles.createDesignSystemFromProject': string;
  'designFiles.duplicateProject': string;

  'designFiles.previewOpen': string;
  'designFiles.previewClose': string;
  'designFiles.modified': string;
  'designFiles.weeksAgo': string;
  'designFiles.groupBy': string;
  'designFiles.groupByKind': string;
  'designFiles.groupByModified': string;
  'designFiles.filterBy': string;
  'designFiles.filterClear': string;
  'designFiles.filterCount': string;
  'designFiles.expandGroup': string;
  'designFiles.collapseGroup': string;
  'designFiles.sectionPages': string;
  'designFiles.sectionScripts': string;
  'designFiles.sectionImages': string;
  'designFiles.sectionSketches': string;
  'designFiles.sectionLiveArtifacts': string;
  'designFiles.sectionOther': string;
  'designFiles.modifiedToday': string;
  'designFiles.modifiedYesterday': string;
  'designFiles.modifiedPrevious7Days': string;
  'designFiles.modifiedPrevious30Days': string;
  'designFiles.modifiedOlder': string;
  'designFiles.showMore': string;
  'designFiles.kindHtml': string;
  'designFiles.kindImage': string;
  'designFiles.kindSketch': string;
  'designFiles.kindText': string;
  'designFiles.kindCode': string;
  'designFiles.kindPdf': string;
  'designFiles.kindDocument': string;
  'designFiles.kindPresentation': string;
  'designFiles.kindSpreadsheet': string;
  'designFiles.kindLiveArtifact': string;
  'designFiles.kindBinary': string;
  'designFiles.kindFolder': string;
  'designFiles.folderCount': string;
  'designFiles.newFolderTitle': string;
  'designFiles.newFolderLabel': string;
  'designFiles.moveTitle': string;
  'designFiles.moveLabel': string;
  'designFiles.moveRoot': string;
  'designFiles.moveNewFolder': string;
  'designFiles.move': string;
  'designFiles.copyPath': string;
  'designFiles.copiedPath': string;
  'designFiles.copyLocalPath': string;
  'designFiles.copiedLocalPath': string;
  'designFiles.deleteFolderConfirm': string;
  'designFiles.folderCreated': string;
  'designFiles.folderDeleted': string;
  'designFiles.filesMoved': string;
  'designFiles.colName': string;
  'designFiles.colKind': string;
  'designFiles.colModified': string;
  'designFiles.perPage': string;
  'designFiles.all': string;
  'designFiles.prev': string;
  'designFiles.next': string;
  'designFiles.jumpToPage': string;
  'designFiles.pageInfo': string;
  'designFiles.sectionFolders': string;
  'designFiles.sectionStylesheets': string;
  'designFiles.sectionDocuments': string;
  'designFiles.kindStylesheet': string;
  'designFiles.modifiedExt': string;
  'designFiles.usefulInfoLabel': string;
  'designFiles.usefulInfoTip': string;
  'designFiles.usefulInfoTip2': string;
  'designFiles.usefulInfoTip4': string;
  'designFiles.usefulInfoTip5': string;
  'designFiles.usefulInfoTip6': string;
  'designFiles.usefulInfoTip7': string;
  'designFiles.usefulInfoTip8': string;
  'designFiles.usefulInfoTip9': string;
  'designFiles.usefulInfoTip10': string;
  'designFiles.usefulInfoTip11': string;
  'designFiles.usefulInfoTip12': string;
  'designFiles.usefulInfoTip13': string;
  'designFiles.usefulInfoTip14': string;
  'designFiles.usefulInfoTip15': string;
  'designFiles.usefulInfoTip16': string;
  'designFiles.usefulInfoTip17': string;
  'designFiles.usefulInfoTip18': string;
  'designFiles.usefulInfoTip19': string;
  'designFiles.usefulInfoTip20': string;
  'quickSwitcher.placeholder': string;
  'quickSwitcher.empty': string;
  'quickSwitcher.noMatches': string;
  'quickSwitcher.navigate': string;
  'quickSwitcher.open': string;
  'quickSwitcher.close': string;
  'sketch.save': string;
  'sketch.cancel': string;
  'sketch.saving': string;
  'sketch.saved': string;
  'sketch.tooltipMainMenu': string;
  'sketch.tooltipLock': string;
  'sketch.tooltipHand': string;
  'sketch.tooltipSelection': string;
  'sketch.tooltipRectangle': string;
  'sketch.tooltipDiamond': string;
  'sketch.tooltipEllipse': string;
  'sketch.tooltipArrow': string;
  'sketch.tooltipLine': string;
  'sketch.tooltipFreedraw': string;
  'sketch.tooltipText': string;
  'sketch.tooltipImage': string;
  'sketch.tooltipEraser': string;
  'sketch.tooltipFrame': string;
  'sketch.tooltipEmbeddable': string;
  'sketch.tooltipLaser': string;
  'sketch.tooltipMoreTools': string;
  'sketch.tooltipDirty': string;
  'sketch.tooltipClean': string;
  'fileViewer.empty': string;
  'fileViewer.loading': string;
  'fileViewer.exportPptx': string;
  'fileViewer.openInNewTab': string;
  'fileViewer.copyPath': string;
  'fileViewer.copied': string;
  'fileViewer.share': string;
  'fileViewer.binaryMeta': string;
  'fileViewer.binaryNote': string;
  'fileViewer.pdfMeta': string;
  'fileViewer.documentMeta': string;
  'fileViewer.presentationMeta': string;
  'fileViewer.spreadsheetMeta': string;
  'fileViewer.previewUnavailable': string;
  'fileViewer.download': string;
  'fileViewer.open': string;
  'fileViewer.imageMeta': string;
  'fileViewer.reactMeta': string;
  'fileViewer.sketchMeta': string;
  'fileViewer.markdownStreamingMeta': string;
  'fileViewer.markdownErrorMeta': string;
  'fileViewer.markdownStreamingStatus': string;
  'fileViewer.markdownErrorStatus': string;
  'fileViewer.markdownSaving': string;
  'fileViewer.markdownSaveFailed': string;
  'fileViewer.markdownSaved': string;
  'fileViewer.markdownAutoSaved': string;
  'fileViewer.markdownAutoSaveHint': string;
  'fileViewer.markdownViewMode': string;
  'fileViewer.split': string;
  'fileViewer.markdownEditor': string;
  'fileViewer.markdownEditorPlaceholder': string;
  'fileViewer.markdownPreview': string;
  'fileViewer.videoMeta': string;
  'fileViewer.audioMeta': string;
  'fileViewer.reload': string;
  'fileViewer.reloadDisk': string;
  'fileViewer.copy': string;
  'fileViewer.copyTitle': string;
  'fileViewer.saveDisabled': string;
  'fileViewer.save': string;
  'fileViewer.preview': string;
  'fileViewer.source': string;
  'fileViewer.tweaks': string;
  'fileViewer.tweaksUnavailable': string;
  'fileViewer.jsxModuleTitle': string;
  'fileViewer.jsxModuleBody': string;
  'fileViewer.jsxModuleCta': string;
  'fileViewer.comment': string;
  'fileViewer.edit': string;
  'fileViewer.draw': string;
  'fileViewer.mark': string;
  'fileViewer.markTool': string;
  'fileViewer.boxSelect': string;
  'fileViewer.textTool': string;
  'fileViewer.textAnnotationRemove': string;
  'fileViewer.screenshot': string;
  'fileViewer.versions.entry': string;
  'fileViewer.versions.entryFull': string;
  'fileViewer.versions.title': string;
  'fileViewer.versions.countOne': string;
  'fileViewer.versions.countMany': string;
  'fileViewer.versions.current': string;
  'fileViewer.versions.sourceAi': string;
  'fileViewer.versions.sourceManual': string;
  'fileViewer.versions.sourceRestore': string;
  'fileViewer.versions.restoredFrom': string;
  'fileViewer.versions.versionLabel': string;
  'fileViewer.versions.listAria': string;
  'fileViewer.versions.loading': string;
  'fileViewer.versions.empty': string;
  'fileViewer.versions.loadFailed': string;
  'fileViewer.versions.previewFailed': string;
  'fileViewer.versions.previewLoading': string;
  'fileViewer.versions.noPrompt': string;
  'fileViewer.versions.noPromptBody': string;
  'fileViewer.versions.promptTitle': string;
  'fileViewer.versions.copyPrompt': string;
  'fileViewer.versions.fullscreen': string;
  'fileViewer.versions.open': string;
  'fileViewer.versions.currentHelp': string;
  'fileViewer.versions.restoreHelp': string;
  'fileViewer.versions.restore': string;
  'fileViewer.versions.restoreConfirmTitle': string;
  'fileViewer.versions.restoreConfirmCta': string;
  'fileViewer.versions.restoring': string;
  'fileViewer.versions.restoreFailed': string;
  'fileViewer.versions.restoreSuccess': string;
  'fileViewer.editScreenshotToChat': string;
  'manualEdit.layers': string;
  'manualEdit.editableCount': string;
  'manualEdit.hiddenBadge': string;
  'manualEdit.title': string;
  'manualEdit.fallbackTitle': string;
  'manualEdit.movePanel': string;
  'manualEdit.editParams': string;
  'manualEdit.closePanel': string;
  'manualEdit.selectLayer': string;
  'manualEdit.empty': string;
  'manualEdit.noEditableLayers': string;
  'manualEdit.noClass': string;
  'manualEdit.tabsAria': string;
  'manualEdit.tabContent': string;
  'manualEdit.tabStyle': string;
  'manualEdit.tabAttributes': string;
  'manualEdit.tabHtml': string;
  'manualEdit.tabSource': string;
  'manualEdit.attributesJson': string;
  'manualEdit.selectedHtml': string;
  'manualEdit.fullSource': string;
  'manualEdit.applyContent': string;
  'manualEdit.applyStyle': string;
  'manualEdit.applyAttributes': string;
  'manualEdit.applyHtml': string;
  'manualEdit.applySource': string;
  'manualEdit.invalidAttributes': string;
  'manualEdit.changes': string;
  'manualEdit.undo': string;
  'manualEdit.redo': string;
  'manualEdit.noChanges': string;
  'manualEdit.imageUrl': string;
  'manualEdit.altText': string;
  'manualEdit.label': string;
  'manualEdit.text': string;
  'manualEdit.href': string;
  'manualEdit.textColor': string;
  'manualEdit.background': string;
  'manualEdit.fontSize': string;
  'manualEdit.weight': string;
  'manualEdit.align': string;
  'manualEdit.textAlignLeft': string;
  'manualEdit.textAlignCenter': string;
  'manualEdit.textAlignRight': string;
  'manualEdit.padding': string;
  'manualEdit.margin': string;
  'manualEdit.radius': string;
  'manualEdit.referenceValues': string;
  'manualEdit.referenceValuesEmpty': string;
  'manualEdit.referenceValuesLoading': string;
  'manualEdit.lineHeight': string;
  'manualEdit.letterSpacing': string;
  'manualEdit.borderStyle': string;
  'manualEdit.borderStyleSolid': string;
  'manualEdit.borderStyleDashed': string;
  'manualEdit.borderStyleDotted': string;
  'manualEdit.borderStyleDouble': string;
  'manualEdit.borderStyleNone': string;
  'manualEdit.border': string;
  'manualEdit.width': string;
  'manualEdit.minHeight': string;
  'manualEdit.deleteElement': string;
  'manualEdit.deleteElementConfirm': string;
  'manualEdit.alignCenter': string;
  'manualEdit.opacity': string;
  'manualEdit.uploadImage': string;
  'manualEdit.uploadingImage': string;
  'manualEdit.uploadImageFailed': string;
  'manualEdit.focusSlides': string;
  'manualEdit.showPanels': string;
  'manualEdit.parameters': string;
  'manualEdit.fontFamily': string;
  'manualEdit.borderColor': string;
  'manualEdit.borderWidth': string;
  'manualEdit.height': string;
  'manualEdit.sideTop': string;
  'manualEdit.sideBottom': string;
  'manualEdit.sideLeft': string;
  'manualEdit.sideRight': string;
  'manualEdit.layoutDirection': string;
  'manualEdit.distribution': string;
  'manualEdit.gap': string;
  'manualEdit.layoutUnavailable': string;
  'manualEdit.directionRow': string;
  'manualEdit.directionRowReverse': string;
  'manualEdit.directionColumn': string;
  'manualEdit.directionColumnReverse': string;
  'manualEdit.justifyStart': string;
  'manualEdit.justifyCenter': string;
  'manualEdit.justifyEnd': string;
  'manualEdit.justifyBetween': string;
  'manualEdit.justifyAround': string;
  'manualEdit.justifyEvenly': string;
  'manualEdit.alignStart': string;
  'manualEdit.alignEnd': string;
  'manualEdit.alignStretch': string;
  'manualEdit.alignBaseline': string;
  'fileViewer.zoomOut': string;
  'fileViewer.zoomIn': string;
  'fileViewer.resetZoom': string;
  'fileViewer.viewportAria': string;
  'fileViewer.viewportDesktop': string;
  'fileViewer.viewportDesktopTitle': string;
  'fileViewer.viewportTablet': string;
  'fileViewer.viewportTabletTitle': string;
  'fileViewer.viewportMobile': string;
  'fileViewer.viewportMobileTitle': string;
  'fileViewer.reloadAria': string;
  'fileViewer.previousSlide': string;
  'fileViewer.nextSlide': string;
  'fileViewer.slideNavAria': string;
  'fileViewer.speakerNotes': string;
  'fileViewer.speakerNotesSlide': string;
  'fileViewer.speakerNotesEmpty': string;
  'fileViewer.speakerNotesEdit': string;
  'fileViewer.speakerNotesSave': string;
  'fileViewer.speakerNotesSaving': string;
  'fileViewer.speakerNotesSaved': string;
  'fileViewer.speakerNotesSaveFailed': string;
  'fileViewer.speakerNotesPlaceholder': string;
  'fileViewer.previewAssetBlockedTitle': string;
  'fileViewer.previewAssetBlockedDetail': string;
  'fileViewer.presenterReset': string;
  'fileViewer.present': string;
  'fileViewer.presentInTab': string;
  'fileViewer.presentFullscreen': string;
  'fileViewer.presentNewTab': string;
  'fileViewer.presentFromBeginning': string;
  'fileViewer.presentFromCurrentSlide': string;
  'fileViewer.presenterMode': string;
  'fileViewer.exitPresentation': string;
  'fileViewer.shareLabel': string;
  'fileViewer.shareMenuShareLink': string;
  'fileViewer.shareMenuPublishOnline': string;
  'fileViewer.shareMenuDownload': string;
  'fileViewer.shareMenuPresentation': string;
  'fileViewer.shareMenuSourceFiles': string;
  'fileViewer.shareMenuSave': string;
  'fileViewer.shareMenuPublishViaOd': string;
  'fileViewer.unifiedShareAria': string;
  'fileViewer.unifiedShareTab': string;
  'fileViewer.unifiedExportTab': string;
  'fileViewer.unifiedSendTab': string;
  'fileViewer.openFileForHistory': string;
  'fileViewer.workspaceShareTitle': string;
  'fileViewer.workspaceSharePrivateDescription': string;
  'fileViewer.workspaceShareWorkspaceDescription': string;
  'fileViewer.workspaceAccessPrivate': string;
  'fileViewer.workspaceAccessMembers': string;
  'fileViewer.publishSingleFileTitle': string;
  'fileViewer.publishSingleFileDescription': string;
  'fileViewer.openFileRequired': string;
  'fileViewer.publishFile': string;
  /** Shown on the publish button in place of `publishFile` while the publish
   *  request is in flight (it takes several seconds server-side) — feedback
   *  for the "no loading state, looks like nothing happened" report
   *  (recvqae7v8yMlk). */
  'fileViewer.publishingFile': string;
  'fileViewer.unpublishFile': string;
  /** @deprecated Orphaned (recvqgif6Xa7Wb): rendered the "no team to share
   *  with yet" bridge card with its create-team CTA in the share panel.
   *  Product ruled that card out entirely — it was never a designed surface,
   *  just a stopgap added for recvqae3pK5hyx/recvq6W8GX8NaH to avoid a blank
   *  tab. A personal workspace's share tab is now allowed to show only the
   *  cards that legitimately apply (or nothing, if none do). */
  'fileViewer.shareEmptyStateTitle': string;
  /** @deprecated Orphaned (recvqgif6Xa7Wb): see `shareEmptyStateTitle`. */
  'fileViewer.shareEmptyStateDescription': string;
  /** @deprecated Orphaned (recvqgif6Xa7Wb): see `shareEmptyStateTitle`. */
  'fileViewer.shareEmptyStateCreateTeam': string;
  /** @deprecated Orphaned (recvqgif6Xa7Wb): see `shareEmptyStateTitle`. */
  'fileViewer.shareTeamMissingTitle': string;
  /** @deprecated Orphaned: the public-file gate is no longer team-scoped.
   *  `fileViewer.publishFileRequiresWorkspace` replaced it. */
  'fileViewer.publishFileRequiresTeam': string;
  'fileViewer.publishFileRequiresWorkspace': string;
  'fileViewer.publishFileFailed': string;
  'fileViewer.workspaceShareSuccess': string;
  'fileViewer.workspaceShareFailed': string;
  'fileViewer.workspaceUnshareSuccess': string;
  'fileViewer.workspaceUnshareFailed': string;
  'fileViewer.readonlySharedNoExport': string;
  'fileViewer.copyShareLink': string;
  'fileViewer.openSharePage': string;
  'fileViewer.shareLinkRequiresDeploy': string;
  'fileViewer.shareLinkPublishGuide': string;
  'fileViewer.shareAfterGenerationComplete': string;
  'fileViewer.copyProviderLink': string;
  'fileViewer.copyCloudflareLink': string;
  'fileViewer.screenshotCopying': string;
  'fileViewer.screenshotCopied': string;
  'fileViewer.screenshotClipboardDenied': string;
  'fileViewer.screenshotPreviewLoading': string;
  'fileViewer.screenshotCaptureFailed': string;
  'fileViewer.exportPdf': string;
  'fileViewer.exportPdfAllSlides': string;
  'fileViewer.exportPptxBusy': string;
  'fileViewer.exportPptxHint': string;
  'fileViewer.exportPptxNa': string;
  'fileViewer.exportDaemonUnreachable': string;
  'fileViewer.exportZip': string;
  'fileViewer.exportHtml': string;
  'fileViewer.exportMd': string;
  'fileViewer.exportImage': string;
  'fileViewer.exportPptxImages': string;
  'fileViewer.exportPptxEditable': string;
  'fileViewer.exportPptxEditableHint': string;
  'fileViewer.exportPptxModalSubtitle': string;
  'fileViewer.exportPptxScreenshot': string;
  'fileViewer.exportPptxScreenshotHint': string;
  'fileViewer.exportPptxConfirm': string;
  'fileViewer.exportPptxRecommended': string;
  'fileViewer.exportingProgress': string;
  'fileViewer.exportSlideProgress': string;
  'fileViewer.exportingElapsed': string;
  'fileViewer.exportSlideEta': string;
  'fileViewer.exportFailed': string;
  'fileViewer.exportDone': string;
  'fileViewer.exportImageFailed': string;
  'fileViewer.exportImageModalSubtitle': string;
  'fileViewer.exportImageFormatLabel': string;
  'fileViewer.exportImageSaving': string;
  'fileViewer.exportImageSaved': string;
  'fileViewer.exportImageDownloadStarted': string;
  'fileViewer.exportImageDownloadDetails': string;
  'fileViewer.exportJsx': string;
  'fileViewer.exportReactHtml': string;
  'fileViewer.exportStarted': string;
  'fileViewer.saveAsTemplate': string;
  'fileViewer.savingTemplate': string;
  'fileViewer.savedTemplate': string;
  'fileViewer.savedTemplateFail': string;
  'fileViewer.templateNamePrompt': string;
  'fileViewer.templateNameDefault': string;
  'fileViewer.templateDescPrompt': string;
  'liveArtifact.refresh.button': string;
  'liveArtifact.refresh.buttonTitle': string;
  'liveArtifact.refresh.loadingTitle': string;
  'liveArtifact.refresh.noSourceTitle': string;
  'liveArtifact.refresh.running': string;
  'liveArtifact.refresh.runningMessage': string;
  'liveArtifact.refresh.runningAction': string;
  'liveArtifact.refresh.successOne': string;
  'liveArtifact.refresh.successMany': string;
  'liveArtifact.refresh.successAction': string;
  'liveArtifact.refresh.previousFailure': string;
  'liveArtifact.refresh.failureAction': string;
  'liveArtifact.refresh.networkFailure': string;
  'liveArtifact.refresh.genericFailure': string;
  'liveArtifact.refresh.statusNever': string;
  'liveArtifact.refresh.statusReady': string;
  'liveArtifact.refresh.statusSucceeded': string;
  'liveArtifact.refresh.statusFailed': string;
  'liveArtifact.refresh.statusRunning': string;
  'liveArtifact.refresh.statusRunningDescription': string;
  'liveArtifact.refresh.statusSucceededDescription': string;
  'liveArtifact.refresh.statusFailedDescription': string;
  'liveArtifact.refresh.statusReadyDescription': string;
  'liveArtifact.refresh.statusNeverDescription': string;
  'liveArtifact.refresh.eventStarted': string;
  'liveArtifact.refresh.eventSucceeded': string;
  'liveArtifact.refresh.eventFailed': string;
  'liveArtifact.refresh.eventStartedDetail': string;
  'liveArtifact.refresh.sourcesUpdatedOne': string;
  'liveArtifact.refresh.sourcesUpdatedMany': string;
  'liveArtifact.refresh.timelineEmpty': string;
  'liveArtifact.refresh.heroLastRefreshedLabel': string;
  'liveArtifact.refresh.heroLastRefreshedNever': string;
  'liveArtifact.refresh.justNow': string;
  'liveArtifact.refresh.factCreated': string;
  'liveArtifact.refresh.factLastUpdated': string;
  'liveArtifact.refresh.factUnknown': string;
  'liveArtifact.refresh.persistedTitle': string;
  'liveArtifact.refresh.persistedHint': string;
  'liveArtifact.refresh.persistedEmpty': string;
  'liveArtifact.refresh.persistedStatusSucceeded': string;
  'liveArtifact.refresh.persistedStatusRunning': string;
  'liveArtifact.refresh.persistedStatusFailed': string;
  'liveArtifact.refresh.persistedStatusCancelled': string;
  'liveArtifact.refresh.persistedStatusSkipped': string;
  'liveArtifact.refresh.sessionTitle': string;
  'liveArtifact.refresh.sessionHint': string;
  'liveArtifact.refresh.docSourceTitle': string;
  'liveArtifact.refresh.docSourceHint': string;
  'liveArtifact.refresh.docSourceType': string;
  'liveArtifact.refresh.docSourceTool': string;
  'liveArtifact.refresh.docSourceConnector': string;
  'liveArtifact.refresh.debugSummary': string;
  'liveArtifact.refresh.debugNote': string;
  'fileViewer.deployProviderLabel': string;
  'fileViewer.vercelProvider': string;
  'fileViewer.cloudflarePagesProvider': string;
  'fileViewer.deployTargetLabel': string;
  'fileViewer.deployTargetPreview': string;
  'fileViewer.deployTargetProduction': string;
  'fileViewer.deployToProvider': string;
  'fileViewer.redeployToProvider': string;
  'fileViewer.deployingToProvider': string;
  'liveArtifact.viewer.tabPreview': string;
  'liveArtifact.viewer.tabCode': string;
  'liveArtifact.viewer.tabData': string;
  'liveArtifact.viewer.tabRefreshHistory': string;
  'liveArtifact.viewer.dataEmpty': string;
  'liveArtifact.viewer.code.templateHeading': string;
  'liveArtifact.viewer.code.renderedHeading': string;
  'liveArtifact.viewer.code.templateHelp': string;
  'liveArtifact.viewer.code.renderedHelp': string;
  'liveArtifact.viewer.code.variantAria': string;
  'liveArtifact.viewer.code.variantTemplate': string;
  'liveArtifact.viewer.code.variantRendered': string;
  'liveArtifact.viewer.code.loading': string;
  'liveArtifact.viewer.code.unavailable': string;
  'liveArtifact.viewer.code.empty': string;
  'fileViewer.deployToVercel': string;
  'fileViewer.redeployToVercel': string;
  'fileViewer.deployingToVercel': string;
  'fileViewer.preparingPublicLink': string;
  'fileViewer.copyDeployLink': string;
  'fileViewer.deployModalTitle': string;
  'fileViewer.deployModalSubtitle': string;
  'fileViewer.vercelToken': string;
  'fileViewer.vercelTokenGetLink': string;
  'fileViewer.vercelTokenPlaceholder': string;
  'fileViewer.vercelTokenReuseHint': string;
  'fileViewer.vercelTokenRequired': string;
  'fileViewer.cloudflareApiToken': string;
  'fileViewer.cloudflareApiTokenGetLink': string;
  'fileViewer.cloudflareApiTokenPlaceholder': string;
  'fileViewer.cloudflareApiTokenReuseHint': string;
  'fileViewer.cloudflareApiTokenRequired': string;
  'fileViewer.cloudflareApiTokenScopeHint': string;
  'fileViewer.vercelTeamId': string;
  'fileViewer.vercelTeamSlug': string;
  'fileViewer.cloudflareAccountId': string;
  'fileViewer.cloudflareAccountIdHint': string;
  'fileViewer.cloudflareAccountIdRequired': string;
  'fileViewer.cloudflareZoneLabel': string;
  'fileViewer.cloudflareZonePlaceholder': string;
  'fileViewer.cloudflareZoneRequired': string;
  'fileViewer.cloudflareZonesLoading': string;
  'fileViewer.cloudflareZonesRefresh': string;
  'fileViewer.cloudflareZonesLoadFailed': string;
  'fileViewer.cloudflareZonesEmpty': string;
  'fileViewer.cloudflareDomainPrefixLabel': string;
  'fileViewer.cloudflareDomainPrefixPlaceholder': string;
  'fileViewer.cloudflareDomainPrefixInvalid': string;
  'fileViewer.cloudflareHostnamePreview': string;
  'fileViewer.cloudflareCustomDomainHint': string;
  'fileViewer.cloudflarePagesDevLinkLabel': string;
  'fileViewer.cloudflareCustomDomainLinkLabel': string;
  'fileViewer.optional': string;
  'fileViewer.vercelPreviewOnly': string;
  'fileViewer.cloudflarePagesPreviewHint': string;
  'fileViewer.savingConfig': string;
  'fileViewer.deployConfigSaveFailed': string;
  'fileViewer.deployFailed': string;
  'fileViewer.deployProviderConfigSaveFailed': string;
  'fileViewer.deployProviderFailed': string;
  'fileViewer.deployResultLabel': string;
  'fileViewer.deployLinkReady': string;
  'fileViewer.deploySuccessToast': string;
  'fileViewer.deploySuccessToastDetails': string;
  'fileViewer.deployLinkPreparingLabel': string;
  'fileViewer.deployLinkDelayed': string;
  'fileViewer.deployLinkFailed': string;
  'fileViewer.deployLinkProtectedLabel': string;
  'fileViewer.deployLinkProtected': string;
  'fileViewer.retryLink': string;

  // Question form (assistant question UX)
  'questionForm.submit': string;
  'questionForm.skip': string;
  'questionForm.locked': string;



  // Tool cards (assistant action cards)
  'tool.openInTab': string;
  'tool.open': string;
  'tool.todos': string;
  'tool.todosExpand': string;
  'tool.todosCollapse': string;
  'tool.todosDone': string;
  'tool.todosDismiss': string;
  'tool.write': string;
  'tool.edit': string;
  'tool.read': string;
  'tool.delete': string;
  'tool.bash': string;
  'tool.glob': string;
  'tool.grep': string;
  'tool.fetch': string;
  'tool.search': string;
  'tool.lines': string;
  'tool.changeSingular': string;
  'tool.changePlural': string;
  'tool.in': string;
  'tool.hide': string;
  'tool.output': string;
  'tool.running': string;
  'tool.error': string;
  'tool.done': string;

  // Assistant message scaffolding
  'assistant.role': string;
  'assistant.workingLabel': string;
  'assistant.doneLabel': string;
  /** Run title while the last turn's question form is still unanswered (OPEND-2744). */
  'assistant.awaitingReplyLabel': string;
  'assistant.canceledLabel': string;
  'assistant.copyMarkdown': string;
  /**
   * 回合动作行那颗按钮的名字。**必须和 `chat.newSession` 同字** —— 聊天面板内
   * 只有一句「新会话」(产品裁决 2026-09-03),守卫在
   * `tests/components/chat/w129-new-session-single-entry.test.tsx`。
   */
  'assistant.forkConversation': string;
  /** 上面那颗按钮的进行态。同一族词:名词「新会话」+ 动词「开始」。 */
  'assistant.forkingConversation': string;
  /** 分叉分界线下面那行脚注 —— 告诉人 Fork 不是跳走,上文已经带过去了。 */
  'assistant.forkNote': string;
  'assistant.shareToOpenDesign': string;
  'assistant.shareToOpenDesignBusy': string;
  'assistant.feedbackPrompt': string;
  'assistant.feedbackPositive': string;
  'assistant.feedbackNegative': string;
  'assistant.feedbackReasonTitle': string;
  /** 点踩那一路的问句(交付稿第 40 格写的是「哪里不对?」);点赞仍用上面那条中性文案 */
  'assistant.feedbackReasonTitleNegative': string;
  /** 社区入口那一句。`{discord}` 会被替换成可点的 Discord 链接 */
  'assistant.feedbackDiscordNegative': string;
  'assistant.feedbackDiscordPositive': string;
  'assistant.feedbackReasonPositiveMatched': string;
  'assistant.feedbackReasonPositiveVisual': string;
  'assistant.feedbackReasonPositiveUseful': string;
  'assistant.feedbackReasonPositiveEasy': string;
  'assistant.feedbackReasonPositiveDesignSystem': string;
  'assistant.feedbackReasonNegativeMissed': string;
  'assistant.feedbackReasonNegativeVisual': string;
  'assistant.feedbackReasonNegativeIncomplete': string;
  'assistant.feedbackReasonNegativeHard': string;
  'assistant.feedbackReasonNegativeCouldNotRun': string;
  'assistant.feedbackReasonNegativeTooSlow': string;
  'assistant.feedbackReasonNegativeDesignSystem': string;
  'assistant.feedbackReasonOther': string;
  'assistant.feedbackReasonPlaceholder': string;
  'assistant.feedbackReasonSubmit': string;
  'assistant.emptyResponseLabel': string;
  'assistant.emptyResponseMessage': string;
  'assistant.unfinishedLabel': string;
  'assistant.unfinishedSummary': string;
  'assistant.unfinishedMore': string;
  'assistant.continueRemaining': string;
  'chat.resumeRunCta': string;
  'chat.runError.contactSupportCta': string;
  'chat.runError.exportLogsCta': string;
  'chat.runError.switchModelCta': string;
  'chat.runError.openSettingsCta': string;
  /* 恢复动作被挡住时,卡面上那一句「为什么现在动不了」(OPEND-2821)。
     四句对应 `runtime/chat/recovery-gating.ts` 的四档,不是四个新的门。 */
  'chat.runError.title.readOnlyAccess': string;
  'chat.runError.title.messagesUnavailable': string;
  'chat.runError.actionBlocked.readOnly': string;
  'chat.runError.actionBlocked.messagesUnavailable': string;
  'chat.runError.actionBlocked.billing': string;
  'chat.runError.actionBlocked.busy': string;
  'chat.support.channel.feishu': string;
  'chat.support.channel.discord': string;
  'chat.amrBalanceOwner.title': string;
  /** {name} = the workspace owner's display name. */
  'chat.amrBalanceOwner.message': string;
  /** Same sentence with the owner's role in place of their name (T57). */
  'chat.amrBalanceOwner.messageNoOwnerName': string;
  'chat.amrBalanceOwner.dismissCta': string;
  'assistant.outTokens': string;
  'assistant.producedFiles': string;
  'assistant.openFile': string;
  'assistant.downloadFile': string;
  'nextStep.imageContinue': string;
  'nextStep.imageVariants': string;
  'nextStep.imageStyle': string;
  'nextStep.title': string;
  // Accessible name for the three agent-written follow-up rows under a
  // delivered turn. The suggestions themselves are model-written in the
  // conversation's own language and deliberately never enter the dictionary.
  'nextStep.suggestionsLabel': string;
  'nextStep.more': string;
  'nextStep.share': string;
  'nextStep.download': string;
  'nextStep.createDesignSystemTitle': string;
  'nextStep.createDesignSystemBody': string;
  'nextStep.createDesignSystemBusy': string;
  'nextStep.contribute': string;
  'nextStep.planGenerateTitle': string;
  'nextStep.planGenerateBody': string;
  'nextStep.planGeneratePrompt': string;
  'nextStep.planImproveTitle': string;
  'nextStep.planImproveBody': string;
  'nextStep.planImprovePrompt': string;
  'nextStep.planImproveArtifactTitle': string;
  'nextStep.planImproveArtifactBody': string;
  'nextStep.planImproveArtifactPrompt': string;
  'nextStep.planMergeTitle': string;
  'nextStep.planMergeBody': string;
  'nextStep.planMergePrompt': string;

  'nextStep.projectContinueTitle': string;
  'nextStep.projectGenerateArtifactTitle': string;
  'nextStep.designSystemAiRefineTitle': string;
  'nextStep.designSystemAuditKitTitle': string;

  'nextStep.brandAiOptimizeTitle': string;
  'nextStep.brandAiOptimizeBody': string;
  'nextStep.brandCreateDesignTitle': string;
  'nextStep.brandCreateDesignBody': string;
  'nextStep.brandCreateDesignPrompt': string;
  'nextStep.brandContinueExtractionTitle': string;
  'nextStep.brandContinueExtractionBody': string;
  'nextStep.brandContinueExtractionBusy': string;
  'nextStep.brandContinueAiExtractionTitle': string;
  'nextStep.brandContinueAiExtractionProgrammaticBody': string;
  'nextStep.brandContinueAiExtractionAiBody': string;
  'nextStep.brandContinueAiExtractionBusy': string;
  'nextStep.createDesignBusy': string;
  'assistant.thinking': string;
  'assistant.thought': string;
  'assistant.thoughtFor': string;
  'assistant.designSystemDirectionLocked': string;
  'assistant.waitingFirstOutput': string;
  'assistant.statusBootingAgent': string;
  'assistant.statusPreparing': string;
  'assistant.statusStarting': string;
  'assistant.statusRequesting': string;
  'assistant.statusThinking': string;
  'assistant.statusStreaming': string;
  'assistant.statusCompactingContext': string;
  'assistant.slowHint': string;
  'assistant.verbEditing': string;
  'assistant.verbWriting': string;
  'assistant.verbReading': string;
  'assistant.verbSearching': string;
  'assistant.verbRunning': string;
  'assistant.verbTodos': string;
  'assistant.verbFetching': string;
  'assistant.verbCalling': string;

  // Question form labels
  'qf.answered': string;
  'qf.unanswered': string;
  'qf.unansweredContinued': string;
  /** 「已回答」陈述块的标题(交付稿 `.answered .k`) */
  'qf.answeredConfirmed': string;
  /**
   * 「已回答」陈述块里,被跳过那道题的值。提交出去的文本给它写的是 `(skipped)`,
   * 这一行就是同一件事的人话版本 —— 不能整行吞掉,吞掉之后收口会退回
   * 「答案已发送」那句兜底,而实际上一个答案都没发。
   */
  'qf.answeredSkipped': string;
  'chat.att.cancelUpload': string;
  'chat.att.retry': string;
  'chat.attachments.preview': string;
  'chat.attachments.scrollNext': string;
  'chat.attachments.scrollPrev': string;
  'chat.input.collapse': string;
  'chat.input.expandFull': string;
  'chat.input.viewAll': string;
  'chat.artifact.export': string;
  'chat.artifact.pending': string;
  'chat.artifact.publish': string;
  'chat.edge.paused': string;
  'chat.edge.reconnectCta': string;
  'chat.edge.reconnectDetail': string;
  'chat.edge.reconnectFailed': string;
  'chat.edge.reconnectFailedDescription': string;
  'chat.edge.reconnecting': string;
  'chat.edge.reconnectingDescription': string;
  'chat.edge.retrying': string;
  'qf.visualNext': string;
  'qf.visualPrev': string;
  'qf.visualRandom': string;
  'qf.visualReshuffle': string;
  'qf.visualViewFan': string;
  'qf.visualViewGrid': string;
  'qf.confirmed': string;
  'qf.picked': string;
  'qf.moreOptions': string;
  'qf.colorPresets': string;
  'qf.colorCustom': string;
  'qf.colorPickerLabel': string;
  'qf.colorHexLabel': string;
  'qf.colorPreview': string;
  'qf.colorInvalid': string;
  'qf.choose': string;
  'qf.customLabel': string;
  'qf.customPlaceholder': string;
  'qf.otherOption': string;
  /** 「自己填」—— 选项列表最后一项的标签(交付稿 `.own-l`) */
  'qf.ownAnswer': string;
  'qf.required': string;
  'qf.lockedSubmitted': string;
  'qf.lockedPrev': string;
  'qf.hint': string;
  'qf.submitDefault': string;
  'qf.submitDisabledTitle': string;
  'qf.submitTitle': string;
  'questions.tabLabel': string;
  'questions.banner': string;
  'questions.bannerAnswered': string;
  'questions.strategyBlockedNotice': string;
  'questions.continue': string;
  'questions.generating': string;
  'questions.skipAll': string;
  'questions.autoSkipHint': string;
  'questions.submitting': string;

  // Inline <od-card> memory cards (display-only siblings of question-form)

  // Pet (Codex-style floating companion)
  'pet.title': string;
  'pet.subtitle': string;
  'pet.navTitle': string;
  'pet.navHint': string;
  // Tabs in pet settings — split sources so the choice feels exclusive
  'pet.tabBuiltIn': string;
  'pet.tabBuiltInHint': string;
  'pet.builtInEmpty': string;
  'pet.tabCustom': string;
  'pet.tabCustomHint': string;
  'pet.tabCommunity': string;
  'pet.tabCommunityHint': string;
  'pet.tabsAria': string;
  'pet.adopt': string;
  'pet.adoptedBadge': string;
  'pet.adoptCallout': string;
  'pet.changePet': string;
  'pet.wake': string;
  'pet.tuck': string;
  'pet.wakeTitle': string;
  'pet.tuckTitle': string;
  'pet.settingsTitle': string;
  'pet.useCustom': string;
  'pet.customTitle': string;
  'pet.customHint': string;
  'pet.customGreetingPlaceholder': string;
  'pet.fieldName': string;
  'pet.fieldGlyph': string;
  'pet.fieldGlyphHint': string;
  'pet.fieldGreeting': string;
  'pet.fieldAccent': string;
  'pet.fieldAccentCustom': string;
  'pet.fieldAccentDefault': string;
  'pet.overlayAria': string;
  'pet.spriteAria': string;
  'pet.spriteTitle': string;
  'pet.taskSummarySingle': string;
  'pet.taskSummaryMultiple': string;
  'pet.taskSummaryRecentSingle': string;
  'pet.taskSummaryRecentMultiple': string;
  'pet.taskListAria': string;
  'pet.taskOpenProject': string;
  'pet.taskGroup.running': string;
  'pet.taskGroup.queued': string;
  'pet.taskGroup.recent': string;
  'pet.idleQuote.leonardo.text': string;
  'pet.idleQuote.leonardo.author': string;
  'pet.idleQuote.michelangelo.text': string;
  'pet.idleQuote.michelangelo.author': string;
  'pet.idleQuote.bernini.text': string;
  'pet.idleQuote.bernini.author': string;
  'pet.idleQuote.raphael.text': string;
  'pet.idleQuote.raphael.author': string;
  'pet.idleQuote.caravaggio.text': string;
  'pet.idleQuote.caravaggio.author': string;
  'pet.idleQuote.rodin.text': string;
  'pet.idleQuote.rodin.author': string;
  // Composer pet menu
  'pet.composerTitle': string;
  'pet.composerMenuTitle': string;
  'pet.composerMenuHint': string;
  'pet.composerOpenSettings': string;
  // Welcome modal teaser
  'pet.welcomeTeaserTitle': string;
  'pet.welcomeTeaserBody': string;
  'pet.welcomeTeaserCta': string;
  // Image upload + spritesheet controls
  'pet.imageUpload': string;
  'pet.imageReplace': string;
  'pet.imageRemove': string;
  'pet.imageHintIdle': string;
  'pet.imageHintActive': string;
  'pet.fieldFrames': string;
  'pet.fieldFramesHint': string;
  'pet.fieldFps': string;
  'pet.fieldFpsHint': string;

  // Codex hatch-pet skill — atlas import + AI generation
  'pet.atlasImport': string;
  'pet.atlasImportTitle': string;
  'pet.atlasPickerTitle': string;
  'pet.atlasPickerHint': string;
  'pet.atlasCancel': string;
  'pet.atlasAdopt': string;
  'pet.atlasAdoptFull': string;
  'pet.atlasAdoptFullTitle': string;
  'pet.atlasAdoptRowTitle': string;
  'pet.atlasActiveHint': string;
  'pet.atlasRow.idle': string;
  'pet.atlasRow.running-right': string;
  'pet.atlasRow.running-left': string;
  'pet.atlasRow.waving': string;
  'pet.atlasRow.jumping': string;
  'pet.atlasRow.failed': string;
  'pet.atlasRow.waiting': string;
  'pet.atlasRow.running': string;
  'pet.atlasRow.review': string;
  'pet.hatchTitle': string;
  'pet.hatchHint': string;
  'pet.hatchConcept': string;
  'pet.hatchConceptPlaceholder': string;
  'pet.hatchCopy': string;
  'pet.hatchCopied': string;
  'pet.hatchFoot': string;
  // Slash-command popover in the chat composer
  'pet.slashPopoverAria': string;
  'pet.slashPopoverTitle': string;
  'pet.slashPopoverHint': string;
  'pet.slashPet': string;
  'pet.slashMcp': string;
  'pet.slashPetWake': string;
  'pet.slashPetTuck': string;
  'pet.slashHatch': string;
  'pet.slashHatchArg': string;
  'pet.slashSearch': string;
  'pet.slashSearchArg': string;
  // Recently-hatched section in pet settings
  'pet.codexTitle': string;
  'pet.codexSubtitle': string;
  'pet.codexSubtitleWithDir': string;
  'pet.codexEmpty': string;
  'pet.codexLoading': string;
  'pet.codexRefresh': string;
  'pet.codexAdopt': string;
  'pet.codexAdopting': string;
  'pet.communitySync': string;
  'pet.communitySyncing': string;
  'pet.communitySyncTitle': string;
  'pet.communitySyncDone': string;
  'pet.communitySyncFailed': string;
  'pet.codexBundled': string;
  'pet.codexBundledTitle': string;

  // Sketch editor
  'sketch.toolSelect': string;
  'sketch.toolPen': string;
  'sketch.toolText': string;
  'sketch.toolRect': string;
  'sketch.toolArrow': string;
  'sketch.toolEraser': string;
  'sketch.color': string;
  'sketch.strokeSize': string;
  'sketch.undo': string;
  'sketch.clear': string;
  'sketch.close': string;
  'sketch.closeConfirm': string;
  'sketch.textPrompt': string;
  'sketch.textModalTitle': string;
  // Critique Theater (Phase 8 components; Phase 9 fills non-English locales)
  'critiqueTheater.userFacingName': string;
  'critiqueTheater.roleDesigner': string;
  'critiqueTheater.roleCritic': string;
  'critiqueTheater.roleBrand': string;
  'critiqueTheater.roleA11y': string;
  'critiqueTheater.roleCopy': string;
  'critiqueTheater.roundLabel': string;
  'critiqueTheater.mustFix': string;
  'critiqueTheater.composite': string;
  'critiqueTheater.threshold': string;
  'critiqueTheater.consensus': string;
  'critiqueTheater.interrupt': string;
  'critiqueTheater.interrupting': string;
  'critiqueTheater.interrupted': string;
  'critiqueTheater.degradedHeading': string;
  'critiqueTheater.degradedReasonMalformed': string;
  'critiqueTheater.degradedReasonOversize': string;
  'critiqueTheater.degradedReasonAdapter': string;
  'critiqueTheater.degradedReasonProtocol': string;
  'critiqueTheater.degradedReasonMissingArtifact': string;
  'critiqueTheater.replay': string;
  'critiqueTheater.replaySpeed': string;
  'critiqueTheater.readOnly': string;
  'critiqueTheater.shippedSummary': string;
  'critiqueTheater.interruptedSummary': string;
  'critiqueTheater.shippedBadge': string;
  'critiqueTheater.belowThresholdBadge': string;
  'critiqueTheater.timedOutBadge': string;
  'critiqueTheater.failedHeading': string;
  'critiqueTheater.failedReasonCliExit': string;
  'critiqueTheater.failedReasonPerRoundTimeout': string;
  'critiqueTheater.failedReasonTotalTimeout': string;
  'critiqueTheater.failedReasonOrchestrator': string;
  'critiqueTheater.transcriptEmpty': string;
  'critiqueTheater.transcriptLoading': string;
  'critiqueTheater.transcriptError': string;
  'critiqueTheater.replaySpeedPaused': string;
  'critiqueTheater.replaySpeedInstant': string;
  'critiqueTheater.replaySpeedLive': string;
  'critiqueTheater.replaySpeedFast': string;
  'critiqueTheater.settingsNav': string;
  'critiqueTheater.settingsNavHint': string;
  'critiqueTheater.settingsEnabledLabel': string;
  'critiqueTheater.settingsEnabledDescription': string;
  'critiqueTheater.settingsEnabledProjectHint': string;
  'critiqueTheater.settingsEnabledNoProjectHint': string;

  // Diagnostics export
  'diagnostics.exportTitle': string;
  'diagnostics.exportButton': string;
  'diagnostics.exportHint': string;
  'diagnostics.exporting': string;
  'diagnostics.exportSuccess': string;
  'diagnostics.exportFailed': string;

  // Brands library + extraction
  'brand.libraryTitle': string;
  'brand.librarySubtitle': string;
  'brand.newBrand': string;
  'brand.searchPlaceholder': string;
  'brand.empty': string;
  'brand.colorsCount': string;
  'brand.extracting': string;
  'brand.failed': string;
  'brand.needsInput': string;
  'brand.needsInputHint': string;
  'brand.appliedToChat': string;
  'brand.previewEmpty': string;
  'brand.viewDetails': string;
  'newBrand.title': string;
  'newBrand.subtitle': string;
  'newBrand.urlLabel': string;
  'newBrand.urlPlaceholder': string;
  'newBrand.extract': string;
  'newBrand.cancel': string;
  'newBrand.close': string;
  'newBrand.stage1': string;
  'newBrand.stage2': string;
  'newBrand.stage3': string;
  'newBrand.measured': string;
  'newBrand.done': string;
  'brandDetail.identity': string;
  'brandDetail.designLanguage': string;
  'brandDetail.colors': string;
  'brandDetail.fonts': string;
  'brandDetail.tone': string;
  'brandDetail.aesthetic': string;
  'brandDetail.tagline': string;
  'brandDetail.voice': string;
  'brandDetail.useInChat': string;
  'brandDetail.delete': string;
  'brandDetail.back': string;
  'brandDetail.refining': string;
  'brandDetail.notFound': string;
  'brandDetail.logo': string;
  'brandDetail.typography': string;
  'brandDetail.palette': string;
  'brandDetail.voiceTone': string;
  'brandDetail.imageryLayout': string;
  'brandDetail.images': string;
  'brandDetail.designSystem': string;
  'brandDetail.brandAssets': string;
  'brandDetail.openProject': string;
  'brandDetail.themeLight': string;
  'brandDetail.themeDark': string;
  'brandDetail.subjects': string;
  'brandDetail.treatment': string;
  'brandDetail.useLabel': string;
  'brandDetail.avoidLabel': string;
  'brandDetail.layoutPosture': string;
  'brandDetail.deleteConfirm': string;
  'brandDetail.viewMore': string;
  'brandDetail.viewLess': string;
  'settings.resetOnboarding': string;
  'settings.resetOnboardingDesc': string;
  'settings.resetOnboardingButton': string;
  // Manual updater/launcher cache clear (disaster recovery, About pane).
  'settings.clearUpdaterCacheTitle': string;
  'settings.clearUpdaterCacheHint': string;
  'settings.clearUpdaterCacheButton': string;
  'settings.clearUpdaterCacheConfirmButton': string;
  'settings.clearUpdaterCacheSuccess': string;
  'settings.clearUpdaterCacheFailed': string;
  'onboarding.brandTitle': string;
  'onboarding.brandSubtitle': string;
  'onboarding.brandSkip': string;
  'onboarding.brandDone': string;
  // Final onboarding step — invite the user to build with brands + design systems
  'onboarding.buildTitle': string;
  'onboarding.buildBody': string;
  'onboarding.buildBenefitMemoryTitle': string;
  'onboarding.buildBenefitMemoryBody': string;
  'onboarding.buildBenefitAlignedTitle': string;
  'onboarding.buildBenefitAlignedBody': string;
  'onboarding.buildBenefitSourcesTitle': string;
  'onboarding.buildBenefitSourcesBody': string;
  'onboarding.buildPreviewLabel': string;
  'onboarding.buildStart': string;
  'onboarding.buildHome': string;
  'dsCreate.referenceLabel': string;
  'dsCreate.referenceLoading': string;
  'dsCreate.referenceLoadFailed': string;
  'dsCreate.heroShowGuideAria': string;
  'dsCreate.heroHideGuideAria': string;
  'dsCreate.heroShowGuideTitle': string;
  'dsCreate.heroHideGuideTitle': string;
  'dsCreate.heroGuide': string;
  'dsCreate.missingSourceError': string;
  'dsCreate.continueToGeneration': string;
  'dsCreate.embeddedTitle': string;
  'dsCreate.embeddedBody': string;
  'dsCreate.sourceSectionTitle': string;
  'dsCreate.sourceSectionBody': string;
  'dsCreate.githubWebsiteLabel': string;
  'dsCreate.add': string;
  'dsCreate.startFromBrand': string;
  'dsCreate.brandPickerSubtitle': string;
  'dsCreate.brandPickerQuickPicks': string;
  'dsCreate.addedSourceLinks': string;
  'dsCreate.openSourceLabel': string;
  'dsCreate.removeSourceLabel': string;
  'dsCreate.addFiles': string;
  'dsCreate.describeBrand': string;
  'dsCreate.optional': string;
  'dsCreate.describeBrandHelp': string;
  'dsCreate.companyPlaceholder': string;
  'dsCreate.pasteDesignMd': string;
  'dsCreate.pasteDesignMdHelp': string;
  'dsCreate.reference': string;
  'dsCreate.designMdViewMode': string;
  'dsCreate.advancedToggle': string;
  'dsCreate.githubRepo': string;
  'dsCreate.localCodeLabel': string;
  'dsCreate.localCodeHelper': string;
  'dsCreate.localCodePrompt': string;
  'dsCreate.uploadFigLabel': string;
  'dsCreate.uploadFigHelper': string;
  'dsCreate.uploadFigPrompt': string;
  'dsCreate.figmaUrl': string;
  'dsCreate.figmaPlaceholder': string;
  'dsCreate.addedFigmaUrls': string;
  'dsCreate.savedFigmaHelp': string;
  'dsCreate.notes': string;
  'dsCreate.notesPlaceholder': string;
  'dsCreate.back': string;
  'dsCreate.generate': string;
  'dsCreate.libraryPickerTitle': string;
  'dsCreate.libraryPickerConfirm': string;
  'dsCreate.designMdPreviewKicker': string;
  'dsCreate.designMdPreviewTitle': string;
  'dsCreate.componentKit': string;
  'dsCreate.defaultTheme': string;
  'dsCreate.componentKitTitle': string;
  'dsCreate.designMdGeneratedDescription': string;
  'dsCreate.previewButtons': string;
  'dsCreate.previewButtonsHelp': string;
  'dsCreate.buttonPrimary': string;
  'dsCreate.buttonDashed': string;
  'dsCreate.buttonText': string;
  'dsCreate.buttonLink': string;
  'dsCreate.sizeSmall': string;
  'dsCreate.sizeMedium': string;
  'dsCreate.sizeLarge': string;
  'dsCreate.previewTypeScale': string;
  'dsCreate.extractedTokens': string;
  // Brand reference picker — pick a brand to extract from
  'brandPicker.categorySoftware': string;
  'brandPicker.categoryFinance': string;
  'brandPicker.categoryFashion': string;
  'brandPicker.categoryActivewear': string;
  'brandPicker.categoryBeauty': string;
  'brandPicker.categoryWellness': string;
  'brandPicker.categoryFood': string;
  'brandPicker.categoryBeverage': string;
  'brandPicker.categoryMedia': string;
  'brandPicker.categoryEducation': string;
  'brandPicker.categoryElectronics': string;
  'brandPicker.categoryAutomotive': string;
  'brandPicker.categoryHealthcare': string;
  'brandPicker.categoryTravel': string;
  'brandPicker.heading': string;
  'brandPicker.subtext': string;
  'brandPicker.quickPicksLabel': string;
  'brandPicker.searchPlaceholder': string;
  'brandPicker.allCategories': string;
  'brandPicker.extractAction': string;
  'brandPicker.showMore': string;
  'brandPicker.opening': string;
  'newproj.startFromLabel': string;
  'newproj.startBlank': string;
  'newproj.startBlankHint': string;
  'workspace.pages': string;
  'workspace.allProjectFiles': string;
  'workspace.newBlankPage': string;
  'workspace.noPagesYet': string;
  'workspace.pageCreateFailed': string;
  'workspace.pageCreatorTitle': string;
  'workspace.pageCreatorSearch': string;
  'workspace.pageCreatorCategoryAll': string;
  'workspace.pageCreatorEmpty': string;
  'workspace.pageCreatorPreview': string;
  'workspace.pageCreatorUse': string;
  'fileViewer.presentInTabDeckHint': string;
  'fileViewer.presentEscHint': string;
  'fileViewer.presenterPause': string;
  'fileViewer.presenterResume': string;
  'fileViewer.presenterPrevious': string;
  'fileViewer.presenterNext': string;
  'questions.uploadingFiles': string;
  'questions.uploadNeedsProject': string;
  'questions.uploadPartialFailed': string;
  'questions.uploadFailed': string;
  'manualEdit.sectionImage': string;
  'manualEdit.sectionContent': string;
  'manualEdit.sectionPage': string;
  'manualEdit.pageBackground': string;
  'manualEdit.pageBaseSize': string;
  'manualEdit.pageStylesHtmlOnly': string;
  'pluginsView.createKindPlugin': string;
  'pluginsView.createKindSkill': string;
  'pluginsView.importUrlLabel': string;
  'pluginsView.shareActionBadge': string;
  'pluginsView.shareCloseAria': string;
  'pluginsView.shareWhatStarts': string;
  'pluginsView.shareSourcePlugin': string;
  'pluginsView.shareFactPlugin': string;
  'pluginsView.shareFactId': string;
  'pluginsView.shareFactCopiedTo': string;
  'pluginsView.shareFactTrust': string;
  'pluginsView.shareActionPrompt': string;
  'pluginsView.shareStarting': string;
  'community.title': string;
  'community.searchPlaceholder': string;
  'community.searchAria': string;
  'community.filtersAria': string;
  'community.closePreview': string;
  'community.typePrototype': string;
  'community.typeLiveArtifact': string;
  'community.typeSlides': string;
  'community.typeDocument': string;
  'community.typeImage': string;
  'community.typeVideo': string;
  'community.typeHyperFrames': string;
  'community.typeAudio': string;
  'community.typeWebGL': string;
  'community.usePrompt': string;
  /** Empty gallery tab; `{type}` is the tab's own label. */
  'community.emptyTitle': string;
  'community.emptyBody': string;
  'marketplace.title': string;
  'marketplace.filterTrusted': string;
  'marketplace.filterRestricted': string;
  'marketplace.emptyBefore': string;
  'marketplace.emptyAfter': string;
  'marketplace.trustPrefix': string;
  'marketplace.catalogsTitle': string;
  'marketplace.catalogsEmpty': string;
  'marketplace.pluginCount': string;
  'genui.continue': string;
  'genui.authorize': string;
  'genui.skip': string;
  'genui.acceptAll': string;
  'genui.rejectAll': string;
  'genui.submit': string;
  'genui.submitPartial': string;
  'genui.patchNotesPlaceholder': string;
  'inspect.close': string;
  'inspect.colors': string;
  'inspect.text': string;
  'inspect.background': string;
  'inspect.typography': string;
  'inspect.size': string;
  'inspect.weight': string;
  'inspect.align': string;
  'inspect.padding': string;
  'inspect.radius': string;
  'inspect.resetElement': string;
  'library.title': string;
  'library.headerHint': string;
  'library.getClipper': string;
  'library.searchPlaceholder': string;
  'library.filterByKind': string;
  'library.filterBySource': string;
  'library.kindAll': string;
  'library.kindImages': string;
  'library.kindElements': string;
  'library.kindDesignSystems': string;
  'library.kindVideo': string;
  'library.kindFonts': string;
  'library.kindColors': string;
  'library.kindText': string;
  'library.kindLinks': string;
  'library.sourceAll': string;
  'library.sourceClipper': string;
  'library.sourceAgent': string;
  'library.sourceDesignSystem': string;
  'library.sourceGenerated': string;
  'library.viewMode': string;
  'library.viewGrid': string;
  'library.viewGridTooltip': string;
  'library.viewTimeline': string;
  'library.viewTimelineTooltip': string;
  'library.refresh': string;
  'library.refreshTooltip': string;
  'library.sync': string;
  'library.syncing': string;
  'library.syncTooltip': string;
  'library.upload': string;
  'library.uploadTooltip': string;
  'library.selectedCount': string;
  'library.selectAll': string;
  'library.clear': string;
  'library.chatToDesign': string;
  'library.chatToDesignTitle': string;
  'library.working': string;
  'library.useInDesignSystem': string;
  'library.createDesignSystem': string;
  'library.createDesignSystemSub': string;
  'library.refineExisting': string;
  'library.noEditableDesignSystems': string;
  'library.addAssetsAndRefine': string;
  'library.deleteCount': string;
  'library.loading': string;
  'library.emptyTitle': string;
  'library.emptyHintBefore': string;
  'library.emptyHintAfter': string;
  'library.dropToUpload': string;
  'library.confirmDeleteTitleOne': string;
  'library.confirmDeleteTitleMany': string;
  'library.confirmDeleteBodyOne': string;
  'library.confirmDeleteBodyMany': string;
  'library.cancel': string;
  'library.today': string;
  'library.yesterday': string;
  'library.previewAsset': string;
  'library.selectAsset': string;
  'library.deselectAsset': string;
  'library.openDesignSystem': string;
  'library.openProject': string;
  'library.opening': string;
  'library.editAsPage': string;
  'library.viewSource': string;
  'library.remove': string;
  'mcp.aboutTemplate': string;
  'mcp.approvedRefresh': string;
  'mcp.approveInBrowserHint': string;
  'mcp.argsPlaceholder': string;
  'mcp.authModeNone': string;
  'mcp.authModeOAuth': string;
  'mcp.browserDidntOpen': string;
  'mcp.categoryDataViz': string;
  'mcp.categoryDataVizHint': string;
  'mcp.categoryDesignSystems': string;
  'mcp.categoryDesignSystemsHint': string;
  'mcp.categoryImageEditing': string;
  'mcp.categoryImageEditingHint': string;
  'mcp.categoryImageGeneration': string;
  'mcp.categoryImageGenerationHint': string;
  'mcp.categoryPublishing': string;
  'mcp.categoryPublishingHint': string;
  'mcp.categoryUiComponents': string;
  'mcp.categoryUiComponentsHint': string;
  'mcp.categoryUtilities': string;
  'mcp.categoryUtilitiesHint': string;
  'mcp.categoryWebCapture': string;
  'mcp.categoryWebCaptureHint': string;
  'mcp.checking': string;
  'mcp.clickConnectHint': string;
  'mcp.closePicker': string;
  'mcp.collapse': string;
  'mcp.collapseServerAria': string;
  'mcp.commandPlaceholder': string;
  'mcp.commandRequired': string;
  'mcp.completedAuthTitle': string;
  'mcp.connect': string;
  'mcp.connected': string;
  'mcp.connecting': string;
  'mcp.customServer': string;
  'mcp.customServerDesc': string;
  'mcp.disabled': string;
  'mcp.disconnect': string;
  'mcp.disconnectFailed': string;
  'mcp.disconnecting': string;
  'mcp.displayNamePlaceholder': string;
  'mcp.enabled': string;
  'mcp.enableServerAria': string;
  'mcp.expand': string;
  'mcp.expandServerAria': string;
  'mcp.expandToEdit': string;
  'mcp.fieldArgs': string;
  'mcp.fieldCommand': string;
  'mcp.fieldEnv': string;
  'mcp.fieldEnvShort': string;
  'mcp.fieldHeaders': string;
  'mcp.fieldId': string;
  'mcp.fieldOAuthMode': string;
  'mcp.fieldTransport': string;
  'mcp.fieldUrl': string;
  'mcp.homepage': string;
  'mcp.idInvalid': string;
  'mcp.jsonHelperExampleHead': string;
  'mcp.jsonHelperHttpNote': string;
  'mcp.jsonHelperToggle': string;
  'mcp.moveDown': string;
  'mcp.moveUp': string;
  'mcp.noManagedOAuth': string;
  'mcp.noManagedOAuthSaveDirect': string;
  'mcp.noManagedOAuthUseAsConfigured': string;
  'mcp.nonExpiringToken': string;
  'mcp.notConnected': string;
  'mcp.openAuthorizationPage': string;
  'mcp.pickerFilterPlaceholder': string;
  'mcp.pickerHint': string;
  'mcp.pickerNoMatch': string;
  'mcp.pickTemplate': string;
  'mcp.reauthenticateTitle': string;
  'mcp.recheckTokenTitle': string;
  'mcp.reconnect': string;
  'mcp.refresh': string;
  'mcp.removeServer': string;
  'mcp.saveFirstThenClick': string;
  'mcp.starting': string;
  'mcp.toGrantOAuthAccess': string;
  'mcp.tokenExpires': string;
  'mcp.transportAria': string;
  'mcp.tryLabel': string;
  'mcp.tryPromptTitle': string;
  'mcp.unnamedServer': string;
  'mcp.urlMalformed': string;
  'mcp.urlProtocolInvalid': string;
  'mcp.urlRequired': string;
  'mcp.waitingForAuthorization': string;
  'memory.connectorReadFailed': string;
  'memory.connectorNoReadableContent': string;
  'memory.providerLocalCli': string;
  'memory.providerFallbackName': string;
  'memory.extractionSourceConnector': string;
  'memory.extractionSourceChat': string;
  'memory.failureAuthExpiredTitle': string;
  'memory.failureAuthActionCli': string;
  'memory.failureAuthActionKey': string;
  'memory.failureQuotaTitle': string;
  'memory.failureQuotaAction': string;
  'memory.failureRequestTitle': string;
  'memory.failureRequestActionCli': string;
  'memory.failureRequestActionProvider': string;
  'memory.failureGenericTitle': string;
  'memory.failureGenericActionCli': string;
  'memory.failureGenericActionSettings': string;
  'memory.noData': string;
  'memory.attemptReadTitle': string;
  'memory.attemptFailedTitle': string;
  'memory.attemptSkippedTitle': string;
  'memory.skipChatDisabled': string;
  'memory.kindConnectedApps': string;
  'memory.timeAgo': string;
  'memory.scanRunningTitle': string;
  'memory.scanFailedTitle': string;
  'memory.scanSkippedTitle': string;
  'memory.scanSavedOne': string;
  'memory.scanSavedOther': string;
  'memory.scanNoNewMemories': string;
  'memory.scanTitle': string;
  'memory.scanMetaChecking': string;
  'memory.scanMetaNeedsAttention': string;
  'memory.scanMetaFromConnectedApps': string;
  'memory.scanMetaCheckedApps': string;
  'memory.scanBusyLabel': string;
  'memory.scanSelectPrompt': string;
  'memory.scanSelectedApps': string;
  'memory.connectorReadFailedRetry': string;
  'memory.appCountOne': string;
  'memory.appCountOther': string;
  'memory.foundSuggestionsOne': string;
  'memory.foundSuggestionsOther': string;
  'memory.noSuggestionsFound': string;
  'memory.checkedNoSuggestionsOne': string;
  'memory.checkedNoSuggestionsOther': string;
  'memory.savedFromConnectedAppsOne': string;
  'memory.savedFromConnectedAppsOther': string;
  'memory.savedPartial': string;
  'memory.tabManual': string;
  'memory.tabManualCaption': string;
  'memory.tabConnected': string;
  'memory.tabConnectedCaption': string;
  'memory.storagePathAria': string;
  'memory.flowOnboarding': string;
  'memory.flowBrandContext': string;
  'memory.flowChatSignals': string;
  'memory.flowSavedMemory': string;
  'memory.howCopy': string;
  'memory.areasAria': string;
  'memory.manualPanelHint': string;
  'memory.connectedPanelHint': string;
  'memory.loading': string;
  'memory.connectedCount': string;
  'memory.manage': string;
  'memory.chooseSources': string;
  'memory.chooseSourcesHint': string;
  'memory.selectedCount': string;
  'memory.connectorListAria': string;
  'memory.readToolsCount': string;
  'memory.checkingConnectionStatus': string;
  'memory.finishAuthorization': string;
  'memory.connectBeforeExtraction': string;
  'memory.useConnectorAria': string;
  'memory.selected': string;
  'memory.select': string;
  'memory.reconnectConnectorAria': string;
  'memory.connectConnectorAria': string;
  'memory.connectStateChecking': string;
  'memory.connectStateWaiting': string;
  'memory.connectStateConnecting': string;
  'memory.reconnect': string;
  'memory.selectedOfConnectedOne': string;
  'memory.selectedOfConnectedOther': string;
  'memory.suggestedMemories': string;
  'memory.suggestedMemoriesHint': string;
  'memory.saving': string;
  'memory.saveSelected': string;
  'memory.discard': string;
  'memory.diagnosticsAria': string;
  'memory.lastScan': string;
  'memory.bytesRead': string;
  'memory.recentScans': string;
  'memory.runHistoryAria': string;
  'memory.savedMemoryHint': string;
  'memory.savedCount': string;
  'memory.extractionCountOne': string;
  'memory.extractionCountOther': string;
  'memory.emptyHintBefore': string;
  'memory.emptyHintAfter': string;
  'memory.advancedHint': string;
  'memory.memoryTree': string;
  'memory.memoryTreeHint': string;
  'memory.nodeCountOne': string;
  'memory.nodeCountOther': string;
  'dsCreate.addingSourceMaterial': string;
  'dsCreate.browseFolder': string;
  'dsCreate.confirmBody': string;
  'dsCreate.confirmTitle': string;
  'dsCreate.designMdPreviewFallbackName': string;
  'dsCreate.dropZoneSelections': string;
  'dsCreate.extractDesignSystem': string;
  'dsCreate.extractionAlreadyStarting': string;
  'dsCreate.figmaFileFallbackLabel': string;
  'dsCreate.figmaUrlInvalid': string;
  'dsCreate.githubAccessAutoBody': string;
  'dsCreate.githubAccessAutoTitle': string;
  'dsCreate.githubAccessMethodsAria': string;
  'dsCreate.githubAuthorizeFailed': string;
  'dsCreate.githubBadgeAutomatic': string;
  'dsCreate.githubBadgeChecking': string;
  'dsCreate.githubBadgeComingSoon': string;
  'dsCreate.githubBadgeConnected': string;
  'dsCreate.githubBadgeNeedsAttention': string;
  'dsCreate.githubBadgeNotConfigured': string;
  'dsCreate.githubBadgeOptional': string;
  'dsCreate.githubBadgePending': string;
  'dsCreate.githubComposioCheckingDesc': string;
  'dsCreate.githubComposioConnectedAsDesc': string;
  'dsCreate.githubComposioConnectedDesc': string;
  'dsCreate.githubComposioDefaultDesc': string;
  'dsCreate.githubComposioErrorDesc': string;
  'dsCreate.githubComposioNotConfiguredDesc': string;
  'dsCreate.githubComposioPendingDesc': string;
  'dsCreate.githubConfigureComposio': string;
  'dsCreate.githubConnecting': string;
  'dsCreate.githubConnectorCheckFailed': string;
  'dsCreate.githubConnectorCheckTimeout': string;
  'dsCreate.githubConnectViaComposio': string;
  'dsCreate.githubDisconnect': string;
  'dsCreate.githubDisconnectFailed': string;
  'dsCreate.githubDisconnecting': string;
  'dsCreate.githubHideAccessMethods': string;
  'dsCreate.githubMethodConnectorPlatform': string;
  'dsCreate.githubMethodOdAccount': string;
  'dsCreate.githubMethodOdAccountDesc': string;
  'dsCreate.githubMethodThisDevice': string;
  'dsCreate.githubMethodThisDeviceDesc': string;
  'dsCreate.githubOpenAuthorization': string;
  'dsCreate.githubShowAccessMethods': string;
  'dsCreate.libraryPartiallyAdded': string;
  'dsCreate.localCodeFilesSelected': string;
  'dsCreate.prepareProjectFailed': string;
  'dsCreate.removeFile': string;
  'dsCreate.startingExtraction': string;
  'dsFlow.activityDetailFailed': string;
  'dsFlow.activityDetailReady': string;
  'dsFlow.activityDetailRunning': string;
  'dsFlow.activityFailedTitle': string;
  'dsFlow.activityProgressAria': string;
  'dsFlow.activityReadyTitle': string;
  'dsFlow.activityRunningTitle': string;
  'dsFlow.addBrandFonts': string;
  'dsFlow.agentMsgCapturedDirection': string;
  'dsFlow.agentMsgCreating': string;
  'dsFlow.agentMsgReady': string;
  'dsFlow.agentMsgRevision': string;
  'dsFlow.agentMsgTokenRebuild': string;
  'dsFlow.agentPushLayer': string;
  'dsFlow.assetsLabel': string;
  'dsFlow.auditNeedsRepair': string;
  'dsFlow.brandFontsMissingBody': string;
  'dsFlow.brandFontsMissingTitle': string;
  'dsFlow.chatCreateRequest': string;
  'dsFlow.chatSeedUpdateSystem': string;
  'dsFlow.composerPlaceholder': string;
  'dsFlow.conversationCreateFailed': string;
  'dsFlow.evidenceFallbackTokens': string;
  'dsFlow.evidenceFonts': string;
  'dsFlow.evidenceIndexes': string;
  'dsFlow.evidenceScannedFiles': string;
  'dsFlow.evidenceSnippets': string;
  'dsFlow.evidenceSourceTokens': string;
  'dsFlow.fileDraftPreview': string;
  'dsFlow.filesTouched': string;
  'dsFlow.forceRebuild': string;
  'dsFlow.generalRevision': string;
  'dsFlow.generationCompleted': string;
  'dsFlow.generationProgressAria': string;
  'dsFlow.generationStopped': string;
  'dsFlow.generationStoppedWithError': string;
  'dsFlow.jobCompleted': string;
  'dsFlow.jobDetailGeneration': string;
  'dsFlow.jobDetailReady': string;
  'dsFlow.jobDetailRevision': string;
  'dsFlow.jobDetailTokenRebuild': string;
  'dsFlow.jobNeedsAttention': string;
  'dsFlow.jobNounGeneration': string;
  'dsFlow.jobNounRevision': string;
  'dsFlow.jobNounTokenRebuild': string;
  'dsFlow.jobRebuildingTokens': string;
  'dsFlow.jobRevising': string;
  'dsFlow.jobStillWorking': string;
  'dsFlow.loadingDesignSystem': string;
  'dsFlow.metaEvidenceNotes': string;
  'dsFlow.metaScannedInventory': string;
  'dsFlow.metaSnippetIndex': string;
  'dsFlow.metaTokenExtraction': string;
  'dsFlow.metaTokenReport': string;
  'dsFlow.movedBackToDraft': string;
  'dsFlow.needsWorkSeed': string;
  'dsFlow.no': string;
  'dsFlow.openingDesignSystemWorkspace': string;
  'dsFlow.openingWorkspace': string;
  'dsFlow.packageFallbackMeta': string;
  'dsFlow.packageLegacy': string;
  'dsFlow.packageManifestMeta': string;
  'dsFlow.packagePillFallback': string;
  'dsFlow.packagePillReady': string;
  'dsFlow.packageStructured': string;
  'dsFlow.pendingRevision': string;
  'dsFlow.pickLocalAgentFirst': string;
  'dsFlow.previewPages': string;
  'dsFlow.proposedChanges': string;
  'dsFlow.publishCardReady': string;
  'dsFlow.publishCardRevision': string;
  'dsFlow.publishCardTokenRebuild': string;
  'dsFlow.publishCardWorking': string;
  'dsFlow.pullLayer': string;
  'dsFlow.rebuildTokenContract': string;
  'dsFlow.reviewDraftTitle': string;
  'dsFlow.revisionAccept': string;
  'dsFlow.revisionAccepted': string;
  'dsFlow.revisionAcceptFailed': string;
  'dsFlow.revisionHistory': string;
  'dsFlow.revisionMsgAccepted': string;
  'dsFlow.revisionMsgPending': string;
  'dsFlow.revisionMsgRejected': string;
  'dsFlow.revisionProgressUnavailable': string;
  'dsFlow.revisionReady': string;
  'dsFlow.revisionReject': string;
  'dsFlow.revisionRejected': string;
  'dsFlow.revisionRejectFailed': string;
  'dsFlow.revisionStopped': string;
  'dsFlow.revisionStoppedWithError': string;
  'dsFlow.saveChangesFailed': string;
  'dsFlow.savedDesignMd': string;
  'dsFlow.sectionDraftBody': string;
  'dsFlow.sectionFallbackTitle': string;
  'dsFlow.sectionMarkedLooksGood': string;
  'dsFlow.sectionNoContent': string;
  'dsFlow.sectionNoDetails': string;
  'dsFlow.setDefaultAction': string;
  'dsFlow.setDefaultTitle': string;
  'dsFlow.sourceEvidence': string;
  'dsFlow.sourceTypeBundled': string;
  'dsFlow.sourceTypeDefault': string;
  'dsFlow.sourceTypeGithub': string;
  'dsFlow.sourceTypeInstalled': string;
  'dsFlow.sourceTypeLocal': string;
  'dsFlow.sourceTypeUser': string;
  'dsFlow.statusUpdateFailed': string;
  'dsFlow.stepReadCurrentSystem': string;
  'dsFlow.stepRefreshReview': string;
  'dsFlow.stepUpdateDesignFiles': string;
  'dsFlow.subtitleBrand': string;
  'dsFlow.subtitleColor': string;
  'dsFlow.subtitleComponents': string;
  'dsFlow.subtitleDefault': string;
  'dsFlow.subtitleSpacing': string;
  'dsFlow.subtitleTypography': string;
  'dsFlow.tabDesignFiles': string;
  'dsFlow.tabDesignSystem': string;
  'dsFlow.tokenContractLabel': string;
  'dsFlow.tokenContractRebuildRecommended': string;
  'dsFlow.tokenContractReportAvailable': string;
  'dsFlow.tokenContractScore': string;
  'dsFlow.tokenRebuildStarted': string;
  'dsFlow.tokenRebuildStartFailed': string;
  'dsFlow.workspaceOpenFailed': string;
  'dsFlow.workspaceUpdatedReview': string;
  'dsFlow.workspaceUpdatedSynced': string;
  'dsFlow.workspaceUpdatedWithAudit': string;
  'dsFlow.yes': string;
  'pluginDetail.backToList': string;
  'pluginDetail.quickCommands': string;
  'pluginDetail.noQuickCommands': string;
  'pluginDetail.dataConnections': string;
  'pluginDetail.noDataConnections': string;
  'pluginDetail.knowledgeSkills': string;
  'pluginDetail.noKnowledgeSkills': string;
  'pluginDetail.officialBadge': string;
  'pluginDetail.loadFailed': string;
  'pluginDetail.applyFailed': string;
  'pluginDetail.examples': string;
  'skillDetail.providedBy': string;
  'skillDetail.markdownNotice': string;
  'skillDetail.loadFailed': string;
  'skillDetail.previewAria': string;
  'common.dismiss': string;
  'campaign.deepseekV4Flash.headline': string;
  'campaign.deepseekV4Flash.description': string;
  'campaign.deepseekV4Flash.benefit': string;
  'campaign.deepseekV4Flash.timing': string;
  'campaign.deepseekV4Flash.ruleSummary': string;
  'campaign.deepseekV4Flash.windowLabel': string;
  'campaign.deepseekV4Flash.weekFreeSuffix': string;
  'campaign.deepseekV4Flash.boundary': string;
  'campaign.deepseekV4Flash.countdownLabel': string;
  'campaign.deepseekV4Flash.countdownEnded': string;
  'campaign.deepseekV4Flash.countdownRemaining': string;
  'campaign.deepseekV4Flash.closeAria': string;
  'campaign.deepseekV4Flash.unlocked': string;
  'campaign.deepseekV4Flash.locked': string;
  'campaign.deepseekV4Flash.later': string;
  'campaign.deepseekV4Flash.paid.eyebrow': string;
  'campaign.deepseekV4Flash.paid.status': string;
  'campaign.deepseekV4Flash.paid.cta': string;
  'campaign.deepseekV4Flash.paid.modelBadge': string;
  'campaign.deepseekV4Flash.unpaid.eyebrow': string;
  'campaign.deepseekV4Flash.unpaid.status': string;
  'campaign.deepseekV4Flash.unpaid.cta': string;
  'campaign.deepseekV4Flash.unpaid.modelBadge': string;
  'campaign.deepseekV4Flash.unpaid.tooltip': string;
  'campaign.deepseekV4Flash.restricted.modelBadge': string;
  'campaign.deepseekV4Flash.restricted.tooltip': string;
  'campaign.deepseekV4Flash.workbenchBadge': string;
  'campaign.deepseekV4Flash.workbenchBadgeAria': string;

  'experienceSurvey.tag': string;
  'experienceSurvey.close': string;
  'experienceSurvey.recommendation': string;
  'experienceSurvey.recommendationLow': string;
  'experienceSurvey.recommendationHigh': string;
  'experienceSurvey.improvement': string;
  'experienceSurvey.improvement.wrongOutput': string;
  'experienceSurvey.improvement.falseDone': string;
  'experienceSurvey.improvement.hardToUse': string;
  'experienceSurvey.improvement.upgradePrompts': string;
  'experienceSurvey.improvement.stuck': string;
  'experienceSurvey.improvement.slow': string;
  'experienceSurvey.improvement.looks': string;
  'experienceSurvey.improvement.regression': string;
  'experienceSurvey.improvement.other': string;
  'experienceSurvey.otherPlaceholder': string;
  'experienceSurvey.submit': string;
  'experienceSurvey.skip': string;
  'experienceSurvey.thanksTitle': string;
  'experienceSurvey.thanksBody': string;

  /* 执行记录(chat 重构):动词、状态词、计数。数值与措辞来自 wangchenglong 交付稿 */
  'chat.record.verb.read': string;
  'chat.record.verb.write': string;
  'chat.record.verb.edit': string;
  'chat.record.verb.search': string;
  'chat.record.verb.exec': string;
  'chat.record.failed': string;
  'chat.record.hits': string;
  /**
   * S12「上游响应慢，已等 N 秒」。**2026-08-27 起没有调用点** —— 产品把这句文案从壳头
   * 撤回了(裁决原文在 `components/chat/ExecutionShell.tsx` 的 `head` 注释里),
   * 但明确要求「后续可能会用到,只不过用别的展现形式」。
   *
   * 键连同 19 份译文一起留着:一个没人调的键不花什么成本,而重新组织 19 种语言的翻译
   * 要花的是真金白银。`scripts/i18n-check.ts` 只查「每个 locale 有没有 Dict 里的键」,
   * 不查反向的「键有没有人用」,所以留着不会让任何检查变红(已核对)。
   */
  'chat.record.slowUpstream': string;
  'chat.record.running': string;
  'chat.record.thinking': string;
  /** 思考行右边那个槽:「3.3k tokens」。`{count}` 已由 `formatThinkingTokens` 写好 */
  'chat.record.thinkingTokens': string;
  'chat.record.thoughts': string;
  'chat.record.done': string;
  'chat.record.canceled': string;
  'chat.record.pending': string;
  /**
   * 一条步骤**没跑完就结束了** —— 不知道它成没成(`closeRunningSegments`)。
   * 两种来源:轮次被用户停掉,或者轮次跑完了但 agent 收尾时没再发一次清单。
   * 所以不能写成「已取消」(succeeded 那一档没人取消过它),也不能写成
   * 「未开始」(它起过步)—— OPEND-2626。
   */
  'chat.record.unfinished': string;
  'chat.record.failedTurn': string;
  'chat.record.plan': string;
  /* Plan 卡收起态那枚药丸(设计稿第 71 格):N = 当前正在做第几步,M = 总步数 */
  'chat.record.planStep': string;
  'chat.record.imageBatch': string;
  'chat.record.imageCount': string;
  'chat.record.retry': string;
  'chat.record.viewImage': string;
  /**
   * 缩略图条上那枚 26×34 的可见提示。稿子 `729fa43ce7` 的
   * `docs/design/chat-panel/src/body-components.html:1041`:`data-tip="查看大图"` ——
   * 和带序号的 `chat.record.viewImage`(读屏用的 `aria-label`)是两句话:
   * 稿子 `src/components.css:2533-2534` 写死了理由「26×34 已经小到看不出内容了,
   * tip 是它唯一能自报家门的方式」。
   */
  'chat.record.viewLarge': string;
  'chat.record.imagePending': string;
  /* OPEND-2625:音频 / 视频不是「插图」,三类各说各的话 */
  'chat.record.audioBatch': string;
  'chat.record.videoBatch': string;
  'chat.record.audioCount': string;
  'chat.record.videoCount': string;
  'chat.record.audioPending': string;
  'chat.record.videoPending': string;
  'chat.record.viewAudio': string;
  'chat.record.viewVideo': string;
  /*
   * 「设计系统工作区 · 自动创建」状态卡的两句文案。逐字取自稿子
   * `729fa43ce7:docs/design/chat-panel/src/body-components.html:50-51`:
   *   Creating design system workspace
   *   Open Design is using the setup sources to generate this project.
   * 和菜单项那句 `designFiles.createDesignSystemFromProject`(「基于此项目创建
   * 设计系统」)是**两回事**:那一句说的是用户点了什么,这两句说的是系统正在做什么。
   * 菜单项和首轮会话标题仍在用旧那枚,别顺手合并。
   */
  'chat.designSystemStatus.title': string;
  'chat.designSystemStatus.description': string;
  'labs.title': string;
  'labs.itemAbout': string;
  'labs.navHint': string;
  'labs.pageDesc': string;
  'labs.harnessName': string;
  'labs.harnessHint': string;
  'labs.harnessTooltip': string;
  'labs.harnessScope': string;
  'labs.envOverrideNotice': string;
  'labs.loadFailedNotice': string;
  'labs.optOutPrompt': string;
  'labs.optOutWorseOutput': string;
  'labs.optOutTooSlow': string;
  'labs.optOutNotWhatIWanted': string;
  'labs.optOutOther': string;
  'labs.optOutSkip': string;
  'labs.optOutOtherPlaceholder': string;
  'labs.optOutSubmit': string;
}
